import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import {
  Search,
  Link2,
  Ship,
  Users,
  Building2,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Mail,
  Plus,
  Upload,
  Paperclip,
  Link2 as LinkIcon,
  RefreshCw,
  Loader2,
  MapPin,
  Calendar,
  Phone,
  ExternalLink,
  ChevronRight,
  Package,
  Anchor,
  Truck,
  FileBox,
  ListTodo,
  FolderOpen,
  Receipt,
  DollarSign,
  LayoutDashboard,
  Save,
  CheckSquare,
  Square,
  Inbox,
  FileSearch,
  ClipboardCheck,
  MessageSquare,
  Flame,
  Send,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  Train,
  X,
  Sparkles,
  Edit3,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { API_URL, API_BASE_URL } from "../config/api";
import { EMAIL_TEMPLATES, TEMPLATE_CATEGORIES, fillTemplate } from "../constants/emailTemplates";
import { MILESTONE_TEMPLATES, getMilestoneLabel } from "../constants/milestoneTemplates";

// Shows the EXACT composed PA reply (From display name + address, To, Cc,
// Subject, and the full HTML: AI header → reply → signature → quoted thread).
function PaReplyPreview({ draftId, authHeaders }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (data) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/pa/queue/${draftId}/preview`, authHeaders);
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load reply preview");
    } finally { setLoading(false); }
  };
  return (
    <div className="pt-1" data-testid="current-draft-preview">
      <button type="button" onClick={toggle} className="text-xs text-indigo-600 hover:underline" data-testid="current-draft-preview-toggle">
        {open ? "Hide" : "Preview"} exact reply (as it will be sent)
      </button>
      {open && (
        <div className="mt-1 border rounded-md overflow-hidden" data-testid="current-draft-preview-panel">
          {loading ? (
            <div className="p-2 text-xs text-slate-400">Loading…</div>
          ) : data ? (
            <>
              <div className="bg-slate-50 px-2 py-1.5 text-[11px] border-b space-y-0.5">
                <p><span className="text-slate-400">From:</span> <b>{data.from_name}</b> &lt;{data.from_email}&gt;</p>
                <p><span className="text-slate-400">To:</span> {data.to_email}</p>
                {data.cc?.length > 0 && <p><span className="text-slate-400">Cc:</span> {data.cc.join(", ")}</p>}
                <p><span className="text-slate-400">Subject:</span> {data.subject}</p>
              </div>
              <div className="p-2 max-h-64 overflow-y-auto bg-white text-xs" dangerouslySetInnerHTML={{ __html: data.html }} />
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

// Declare Office as a global variable (loaded dynamically)
/* global Office */

// ── Typed-date parser ───────────────────────────────────────────────────
// Ops staff TYPE milestone dates (they don't click the calendar). A native
// <input type="date"> only commits a value when every segment is complete,
// so a typed/partial entry silently stays empty and the backend records
// `now()`. We therefore use a plain text box and parse the typed value here.
// Accepts: dd/mm/yyyy, dd.mm.yyyy, dd-mm-yyyy (2- or 4-digit year) and ISO
// yyyy-mm-dd. Returns { iso } ("" when blank) or { invalid: true }.
function parseTypedMilestoneDate(raw) {
  const s = (raw || "").trim();
  if (!s) return { iso: "" };
  const pad = (n) => String(n).padStart(2, "0");
  // ISO yyyy-mm-dd (what the old date picker produced)
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) {
    const y = +m[1], mo = +m[2], d = +m[3];
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) return { iso: `${y}-${pad(mo)}-${pad(d)}` };
    return { invalid: true };
  }
  // dd/mm/yyyy and friends (also dd.mm.yy)
  m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
  if (m) {
    const d = +m[1], mo = +m[2];
    let y = m[3];
    if (y.length === 2) y = "20" + y;
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) return { iso: `${+y}-${pad(mo)}-${pad(d)}` };
  }
  return { invalid: true };
}



// ── PA trust indicator (mirrors the dashboard TrustBadge) so operators can
// spot safe drafts fast, computed from fields available on a queue draft. ──
function paTrustLevel(draft) {
  const info = (draft && draft.email_info) || {};
  const flags = (draft && draft.safety_flags) || info.safety_flags || [];
  if (flags.length) return "review";
  const risk = draft && draft.risk_level;
  const conf = (info.confidence != null ? info.confidence : (draft && draft.confidence)) || 0;
  if (risk === "high") return "review";
  if (risk === "low" || conf >= 0.9) return "high";
  if (conf >= 0.7 || risk === "medium") return "medium";
  return "low";
}
const PA_TRUST_META = {
  high: { label: "High trust", cls: "bg-green-100 text-green-700 border-green-300" },
  medium: { label: "Medium", cls: "bg-amber-100 text-amber-700 border-amber-300" },
  low: { label: "Low", cls: "bg-slate-100 text-slate-600 border-slate-300" },
  review: { label: "Needs review", cls: "bg-rose-100 text-rose-700 border-rose-300" },
};
function PaTrustBadge({ draft, testId }) {
  const m = PA_TRUST_META[paTrustLevel(draft)];
  return (
    <span data-testid={testId} className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium shrink-0 ${m.cls}`}>
      {m.label}
    </span>
  );
}


function TOEditCard({ order, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    driver_name: order.driver_name || "",
    driver_phone: order.driver_phone || "",
    truck_number: order.truck_number || "",
    trailer_number: order.trailer_number || "",
    loading_place: order.loading_place || "",
    unloading_place: order.unloading_place || "",
    date_loading: (order.date_loading || "").replace(" ", "T").slice(0, 16),
    date_unloading: (order.date_unloading || "").replace(" ", "T").slice(0, 16),
    status: order.status || "draft",
    remarks: order.remarks || "",
    customs_clearance: order.customs_clearance || "",
    price: order.price || "",
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/outlook/transport-orders/${order.id || order.order_number}`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Transport order updated");
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Failed to update order");
    }
    setSaving(false);
  };

  const tc = order.trucking_company || {};
  return (
    <Card className="border-l-4 border-l-blue-500">
      <CardContent className="p-3">
        <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div>
            <p className="font-semibold text-sm">{order.order_number || order.id?.slice(0, 8)}</p>
            <p className="text-xs text-gray-500">{order.loading_place || "?"} → {order.unloading_place || "?"}</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs">{order.status}</Badge>
              {order.price && <span className="text-xs font-medium text-green-600">€{order.price}</span>}
              {tc.name && <span className="text-xs text-gray-400">{tc.name}</span>}
            </div>
          </div>
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>

        {expanded && (
          <div className="mt-3 pt-3 border-t space-y-2">
            {!editing ? (
              <>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-gray-400">PF:</span> {order.pf_number || "-"}</div>
                  <div><span className="text-gray-400">Customer:</span> {order.customer_name || "-"}</div>
                  <div><span className="text-gray-400">Driver:</span> {order.driver_name || "-"}</div>
                  <div><span className="text-gray-400">Phone:</span> {order.driver_phone || "-"}</div>
                  <div><span className="text-gray-400">Truck:</span> {order.truck_number || "-"}</div>
                  <div><span className="text-gray-400">Trailer:</span> {order.trailer_number || "-"}</div>
                  <div><span className="text-gray-400">Loading:</span> {order.date_loading?.slice(0, 16) || "-"}</div>
                  <div><span className="text-gray-400">Unloading:</span> {order.date_unloading?.slice(0, 16) || "-"}</div>
                  <div><span className="text-gray-400">Customs:</span> {order.customs_clearance || "-"}</div>
                  <div><span className="text-gray-400">Remarks:</span> {order.remarks || "-"}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="w-full mt-2">
                  <Edit3 className="h-3 w-3 mr-1" /> Edit Order
                </Button>
              </>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">Driver Name</label>
                    <Input className="h-7 text-xs" value={form.driver_name} onChange={e => setForm({...form, driver_name: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Driver Phone</label>
                    <Input className="h-7 text-xs" value={form.driver_phone} onChange={e => setForm({...form, driver_phone: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Truck #</label>
                    <Input className="h-7 text-xs" value={form.truck_number} onChange={e => setForm({...form, truck_number: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Trailer #</label>
                    <Input className="h-7 text-xs" value={form.trailer_number} onChange={e => setForm({...form, trailer_number: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Loading Date</label>
                    <Input type="datetime-local" className="h-7 text-xs" value={form.date_loading} onChange={e => setForm({...form, date_loading: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Unloading Date</label>
                    <Input type="datetime-local" className="h-7 text-xs" value={form.date_unloading} onChange={e => setForm({...form, date_unloading: e.target.value})} />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Status</label>
                  <select className="w-full h-7 text-xs border rounded px-2" value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                    <option value="draft">Draft</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="picked_up">Picked Up</option>
                    <option value="in_transit">In Transit</option>
                    <option value="delivered">Delivered</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Remarks</label>
                  <Input className="h-7 text-xs" value={form.remarks} onChange={e => setForm({...form, remarks: e.target.value})} />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="flex-1">Cancel</Button>
                  <Button size="sm" onClick={handleSave} disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
                    {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Save className="h-3 w-3 mr-1" /> Save</>}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


export default function OutlookAddinPage() {
  // Office.js loading state
  const [officeLoaded, setOfficeLoaded] = useState(false);
  const [isOutlook, setIsOutlook] = useState(false);
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(localStorage.getItem("outlook_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  
  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [twoFactorEmail, setTwoFactorEmail] = useState("");

  // Email context from Outlook
  const [currentEmail, setCurrentEmail] = useState(null);
  const [emailBody, setEmailBody] = useState("");

  // Match results
  const [matchResults, setMatchResults] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);

  // ── Email templates (Outlook reply integration) — 2026-05-15 ─────────
  const [tplSelectedId, setTplSelectedId] = useState(null);
  const [tplPfPicked, setTplPfPicked] = useState(null);   // matched/picked PF
  const [tplSubject, setTplSubject] = useState("");
  const [tplBody, setTplBody] = useState("");
  const [tplSearch, setTplSearch] = useState("");
  const [tplBusy, setTplBusy] = useState(false);

  // Navigation
  const [activeTab, setActiveTab] = useState("match");
  const [activeCategory, setActiveCategory] = useState("operations");

  // Error state for debugging
  const [renderError, setRenderError] = useState(null);

  // ── LogiGrowth Handoff Brief → AI reply (2026-06) ───────────────────
  const [hbSelectedPoints, setHbSelectedPoints] = useState([]); // array of talking-point indices
  const [hbCustomAngle, setHbCustomAngle] = useState("");
  const [hbLanguage, setHbLanguage] = useState("English");
  const [hbReply, setHbReply] = useState("");        // editable AI reply text
  const [hbBusy, setHbBusy] = useState(false);
  const [hbLogging, setHbLogging] = useState(false);
  const [hbLogged, setHbLogged] = useState(false);
  const [hbAutoSwitchedFor, setHbAutoSwitchedFor] = useState(null);

  // Module categories
  const categories = {
    operations: { label: "Operations", modules: ["match", "pf", "tracking", "orders", "docs", "handoff", "templates", "whatsapp"] },
    sales: { label: "Sales", modules: ["offers", "crm", "tasks", "handoff", "templates"] },
    pa: { label: "PA", modules: ["pa_notif", "pa_inbox", "pa_queue", "pa_erp"] },
  };

  const moduleLabels = {
    match: "Match",
    pf: "PF",
    tracking: "Track",
    orders: "Orders",
    docs: "Docs",
    handoff: "Handoff",
    whatsapp: "WhatsApp",
    offers: "Offers",
    crm: "CRM",
    tasks: "Tasks",
    templates: "Templates",
    pa_notif: "Alerts",
    pa_inbox: "Inbox",
    pa_queue: "Queue",
    pa_erp: "ERP",
  };

  // Search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // Tracking
  const [trackingQuery, setTrackingQuery] = useState("");
  const [trackingType, setTrackingType] = useState("container"); // container, bl, booking, wagon, train
  const [trackingTransport, setTrackingTransport] = useState("fcl"); // fcl, lcl, rail
  const [trackingCarrier, setTrackingCarrier] = useState("auto");
  const [trackingResult, setTrackingResult] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingHistory, setTrackingHistory] = useState([]);

  // Tasks
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [newTask, setNewTask] = useState({ title: "", description: "", due_date: "", priority: "normal" });

  // Position Folders
  const [positionFolders, setPositionFolders] = useState([]);
  const [pfsLoading, setPfsLoading] = useState(false);
  const [selectedPf, setSelectedPf] = useState(null);
  const [detectedReferences, setDetectedReferences] = useState(null); // Auto-detected PF/BL/Container from email
  const [autoMatchedPf, setAutoMatchedPf] = useState(null); // Auto-loaded PF based on detection

  // Offers
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);

  // Transport Orders
  const [transportOrders, setTransportOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Finance
  const [financeStatus, setFinanceStatus] = useState(null);
  const [financeLoading, setFinanceLoading] = useState(false);

  // Dashboard stats
  const [dashboardStats, setDashboardStats] = useState(null);

  // Contact creation
  const [showCreateContact, setShowCreateContact] = useState(false);
  const [newContact, setNewContact] = useState({
    name: "",
    email: "",
    company_name: "",
    company_id: "",
    phone: "",
    title: "",
  });
  const [contactCompanySearch, setContactCompanySearch] = useState("");
  const [contactCompanyResults, setContactCompanyResults] = useState([]);
  const [contactCompanyLoading, setContactCompanyLoading] = useState(false);
  const [showContactCompanyDropdown, setShowContactCompanyDropdown] = useState(false);

  // Activity creation
  const [showCreateActivity, setShowCreateActivity] = useState(false);
  const [newActivity, setNewActivity] = useState({
    activity_type: "email_received",
    notes: "",
    due_date: "",
    company_id: "",
    company_name: "",
  });
  const [activityCompanySearch, setActivityCompanySearch] = useState("");
  const [activityCompanyResults, setActivityCompanyResults] = useState([]);
  const [activityCompanyLoading, setActivityCompanyLoading] = useState(false);
  const [showActivityCompanyDropdown, setShowActivityCompanyDropdown] = useState(false);

  // Linking
  const [linkingTo, setLinkingTo] = useState(null);

  // PF Actions - Milestones & Inspections
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [showOrderInspection, setShowOrderInspection] = useState(false);

  // PF Status Comments (2026-05-14)
  const [showAddComment, setShowAddComment] = useState(false);
  const [savingComment, setSavingComment] = useState(false);
  const [newComment, setNewComment] = useState({ text: "", container_number: "", notify_customer: true, audience: "customer" });
  const [newMilestone, setNewMilestone] = useState({
    milestone_type: "customs_cleared",
    description: "",
    date: "",
    notes: "",
    container_number: "",
    send_notification: true
  });
  const [newInspection, setNewInspection] = useState({
    inspection_type: "customs",
    container_number: "",
    port: "KOPER",
    notes: "",
    urgency: "normal"
  });
  
  // PF Document Upload
  const [showPfDocUpload, setShowPfDocUpload] = useState(false);
  const [pfDocUploading, setPfDocUploading] = useState(false);
  const [pfDocDragging, setPfDocDragging] = useState(false);
  const pfDocDragCounter = useRef(0);
  const [pfDocType, setPfDocType] = useState("invoice");

  // DVH (vhodna dispozicija) — quick input from the Outlook plugin so
  // staff can close the DVH follow-up loop without leaving Outlook.
  const [showDvhDialog, setShowDvhDialog] = useState(false);
  const [dvhNumberInput, setDvhNumberInput] = useState("");
  const [dvhSaving, setDvhSaving] = useState(false);
  const [dvhDoneToggle, setDvhDoneToggle] = useState(false);

  // DIZ (izhodna dispozicija) — export-side mirror of the DVH quick
  // input. Closes the DIZ follow-up loop for IN/F-at-Koper export PFs.
  const [showDizDialog, setShowDizDialog] = useState(false);
  const [dizNumberInput, setDizNumberInput] = useState("");
  const [dizSaving, setDizSaving] = useState(false);
  const [dizDoneToggle, setDizDoneToggle] = useState(false);

  // Create New PF
  const [showCreatePf, setShowCreatePf] = useState(false);
  const [creatingPf, setCreatingPf] = useState(false);
  const [newPf, setNewPf] = useState({
    shipper: "",
    consignee: "",
    origin_port: "",
    destination_port: "",
    container_type: "40HC",
    incoterm: "FOB",
    commodity: "",
    notes: "",
    customer_id: "",
    customer_name: "",
  });
  
  // PF Customer search
  const [pfCustomerSearch, setPfCustomerSearch] = useState("");
  const [pfCustomerResults, setPfCustomerResults] = useState([]);
  const [pfCustomerLoading, setPfCustomerLoading] = useState(false);
  const [showPfCustomerDropdown, setShowPfCustomerDropdown] = useState(false);

  // Create New Trucking Order
  const [showCreateTruckingOrder, setShowCreateTruckingOrder] = useState(false);
  const [creatingTruckingOrder, setCreatingTruckingOrder] = useState(false);
  const [newTruckingOrder, setNewTruckingOrder] = useState({
    pickup_location: "",
    delivery_location: "",
    pickup_date: "",
    container_number: "",
    cargo_description: "",
    weight: "",
    special_instructions: "",
  });

  // Suggestion Box
  const [suggestionText, setSuggestionText] = useState("");
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);

  // PF Milestones View
  const [expandedPfMilestones, setExpandedPfMilestones] = useState({});
  const [pfMilestonesLoading, setPfMilestonesLoading] = useState({});
  const [pfMilestonesData, setPfMilestonesData] = useState({});

  // WhatsApp Tickets
  const [whatsappTicket, setWhatsappTicket] = useState(null);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappReply, setWhatsappReply] = useState("");
  const [sendingWhatsappReply, setSendingWhatsappReply] = useState(false);

  // PA (Personal Assistant)
  const [paEmails, setPaEmails] = useState([]);
  const [paEmailsLoading, setPaEmailsLoading] = useState(false);
  const [paQueue, setPaQueue] = useState([]);
  const [paQueueLoading, setPaQueueLoading] = useState(false);
  const [paStats, setPaStats] = useState(null);
  const [paSelectedEmail, setPaSelectedEmail] = useState(null);
  const [paErpQuery, setPaErpQuery] = useState("");
  const [paErpResult, setPaErpResult] = useState(null);
  const [paErpLoading, setPaErpLoading] = useState(false);
  const [paEmailFilter, setPaEmailFilter] = useState("all");
  const [paEditDraft, setPaEditDraft] = useState(null);
  const [paEditBody, setPaEditBody] = useState("");
  const [paReplyTo, setPaReplyTo] = useState("");
  const [paReplySubject, setPaReplySubject] = useState("");
  const [paReplyBody, setPaReplyBody] = useState("");
  const [paReplyCc, setPaReplyCc] = useState("");
  const [paReplySending, setPaReplySending] = useState(false);
  const [paNote, setPaNote] = useState("");
  const [paReplyMode, setPaReplyMode] = useState(false);
  const [paEditingDraftBody, setPaEditingDraftBody] = useState("");
  const [paIsEditingDraft, setPaIsEditingDraft] = useState(false);
  const [paDraftActionLoading, setPaDraftActionLoading] = useState(false);

  // PA Queue — bulk cleanup state
  const [paQueueSelected, setPaQueueSelected] = useState(new Set());
  const [paRewriteText, setPaRewriteText] = useState({});
  const [paRewriteLoading, setPaRewriteLoading] = useState(null);
  const [paRejectAllConfirming, setPaRejectAllConfirming] = useState(false);
  const [paQueueStats, setPaQueueStats] = useState(null);
  const [paBulkLoading, setPaBulkLoading] = useState(false);
  // 2-click confirmation flags. Outlook task panes silently block native
  // `window.confirm()` modals, so we use an in-button "tap again" pattern.
  const [paBulkRejectConfirming, setPaBulkRejectConfirming] = useState(false);
  const [paAutoCleanupConfirming, setPaAutoCleanupConfirming] = useState(false);
  // Mine vs All filter on the Approval Queue tab. Default to "mine" so
  // each user sees only drafts for emails addressed to their own mailbox.
  const [paQueueFilter, setPaQueueFilter] = useState("mine");
  // Pinned "current email" draft at top of Queue tab
  const [paCurrentDraft, setPaCurrentDraft] = useState(null);
  const [paCurrentDraftLoading, setPaCurrentDraftLoading] = useState(false);

  // PA 3-Tier Notifications
  const [paNotifications, setPaNotifications] = useState({ tier1: [], tier2: [], tier3: [], counts: {} });
  const [paNotifExpanded, setPaNotifExpanded] = useState(true);
  const paNotifPollRef = useRef(null);
  const prevNotifCountRef = useRef(0);

  // PA Internal Domains
  const [paInternalDomains, setPaInternalDomains] = useState([]);

  // Sidebar PA Card (auto-detects PA ticket for current Outlook email)
  const [sidebarTicket, setSidebarTicket] = useState(null);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [sidebarActionLoading, setSidebarActionLoading] = useState(false);

  const getAuthHeaders = useCallback(() => {
    return {
      headers: { Authorization: `Bearer ${authToken}` },
    };
  }, [authToken]);

  // Load Office.js dynamically
  useEffect(() => {
    // Check if Office.js is already loaded
    if (typeof Office !== "undefined") {
      setOfficeLoaded(true);
      setIsOutlook(!!Office.context?.mailbox);
      return;
    }

    // Dynamically load Office.js
    const script = document.createElement("script");
    script.src = "https://appsforoffice.microsoft.com/lib/1.1/hosted/office.js";
    script.async = true;
    script.onload = () => {
      setOfficeLoaded(true);
      // Check if running in Outlook context
      if (typeof Office !== "undefined") {
        Office.onReady((info) => {
          setIsOutlook(info.host === Office.HostType.Outlook);
        });
      }
    };
    script.onerror = () => {
      // Office.js failed to load - running outside Outlook, which is fine
      setOfficeLoaded(true);
      setIsOutlook(false);
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup not needed as Office.js should persist
    };
  }, []);

  // Initialize email context when Office is ready
  useEffect(() => {
    if (!officeLoaded || !isOutlook) return;

    try {
      Office.onReady(() => {
        try {
          // Auto-fill user's email from Office profile
          if (Office.context.mailbox.userProfile) {
            const userEmail = Office.context.mailbox.userProfile.emailAddress;
            if (userEmail && !email) {
              setEmail(userEmail);
            }
          }
          
          const item = Office.context.mailbox.item;
          if (item) {
            const emailData = {
              message_id: item.itemId || `temp_${Date.now()}`,
              conversation_id: item.conversationId,
              subject: item.subject || "",
              sender_email: item.from?.emailAddress || "",
              sender_name: item.from?.displayName || "",
              recipients: item.to?.map((r) => r.emailAddress) || [],
              received_at: item.dateTimeCreated?.toISOString(),
              body_preview: "",
            };
            
            // Get body content
            item.body.getAsync(Office.CoercionType.Text, (result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                const bodyText = result.value.substring(0, 2000);
                setEmailBody(bodyText);
                emailData.body_preview = bodyText.substring(0, 500);
              }
              // Set email data AFTER body is retrieved
              setCurrentEmail(emailData);
              console.log("Email context loaded:", emailData.subject);
            });
          } else {
            console.log("No email item found in Office context");
          }

          // Pinned task panes MUST handle ItemChanged, otherwise the add-in keeps
          // operating on the first-loaded message when staff switch emails — a
          // likely cause of "worked once, then no reply opens". Re-read the
          // freshly selected message so the reply target stays current.
          try {
            Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, () => {
              try {
                const it = Office.context.mailbox.item;
                if (!it) return;
                setCurrentEmail({
                  message_id: it.itemId || `temp_${Date.now()}`,
                  conversation_id: it.conversationId,
                  subject: it.subject || "",
                  sender_email: it.from?.emailAddress || "",
                  sender_name: it.from?.displayName || "",
                  recipients: it.to?.map((r) => r.emailAddress) || [],
                  received_at: it.dateTimeCreated?.toISOString(),
                  body_preview: "",
                });
                if (it.body && typeof it.body.getAsync === "function") {
                  it.body.getAsync(Office.CoercionType.Text, (r) => {
                    if (r.status === Office.AsyncResultStatus.Succeeded) setEmailBody((r.value || "").substring(0, 2000));
                  });
                }
              } catch (e) { /* ignore */ }
            });
          } catch (e) { /* host may not support ItemChanged */ }
        } catch (e) {
          console.error("Error reading email:", e);
        }
      });
    } catch (e) {
      console.error("Office.onReady error:", e);
    }
  }, [officeLoaded, isOutlook]);

  // Check auth on load
  useEffect(() => {
    if (authToken) {
      verifyAuth();
    }
  }, [authToken]);

  // Auto-match when email context changes and user is authenticated
  useEffect(() => {
    if (isAuthenticated && currentEmail && currentEmail.subject) {
      // Extract references from email subject
      extractReferencesFromEmail(currentEmail);
      // Auto-match on email change
      matchEmail();
      // Also fetch position folders that might match
      fetchPositionFolders();
      // Check if PA has a ticket for this conversation
      checkSidebarTicket();
    } else if (isAuthenticated) {
      // Still fetch PFs even without email context
      fetchPositionFolders();
    }
  }, [isAuthenticated, currentEmail?.message_id]);
  
  // Extract PF number, BL number, Container number from email subject/body
  const extractReferencesFromEmail = (emailData) => {
    if (!emailData) return;
    
    const text = `${emailData.subject || ''} ${emailData.body_preview || ''}`;
    const refs = {
      pf_numbers: [],
      bl_numbers: [],
      container_numbers: [],
      booking_numbers: [],
    };
    
    // PF Number pattern: PF-XXXXXX-XX or PF-XXXXXX
    const pfPattern = /PF-\d{6}-\d{2}|PF-\d{6}/gi;
    const pfMatches = text.match(pfPattern);
    if (pfMatches) {
      refs.pf_numbers = [...new Set(pfMatches.map(m => m.toUpperCase()))];
    }
    
    // Container number pattern: 4 letters + 7 digits (e.g., MAEU3437434, MRKU6274450)
    const containerPattern = /[A-Z]{4}\d{7}/gi;
    const containerMatches = text.match(containerPattern);
    if (containerMatches) {
      refs.container_numbers = [...new Set(containerMatches.map(m => m.toUpperCase()))];
    }
    
    // BL number patterns (various formats)
    const blPattern = /\b(MAEU|MSCU|HLCU|CMDU|COSU|OOLU|YMLU|EGLV|HDMU|ONEY)\d{9,12}\b/gi;
    const blMatches = text.match(blPattern);
    if (blMatches) {
      refs.bl_numbers = [...new Set(blMatches.map(m => m.toUpperCase()))];
    }
    
    // Booking number patterns (example: JD260191)
    const bookingPattern = /\b[A-Z]{2}\d{6}\b/gi;
    const bookingMatches = text.match(bookingPattern);
    if (bookingMatches) {
      // Filter out container numbers
      const filtered = bookingMatches.filter(m => !refs.container_numbers.includes(m.toUpperCase()));
      refs.booking_numbers = [...new Set(filtered.map(m => m.toUpperCase()))];
    }
    
    // Only set if we found something
    if (refs.pf_numbers.length || refs.bl_numbers.length || refs.container_numbers.length || refs.booking_numbers.length) {
      setDetectedReferences(refs);
      console.log("Detected references from email:", refs);
      
      // Auto-search for the first PF number found
      if (refs.pf_numbers.length > 0) {
        autoSearchPf(refs.pf_numbers[0]);
      } else if (refs.container_numbers.length > 0) {
        // Search by container number
        autoSearchByReference(refs.container_numbers[0], 'container');
      } else if (refs.bl_numbers.length > 0) {
        // Search by BL number
        autoSearchByReference(refs.bl_numbers[0], 'bl');
      }
    } else {
      setDetectedReferences(null);
      setAutoMatchedPf(null);
    }
  };
  
  // Auto-search for a specific PF number
  const autoSearchPf = async (pfNumber) => {
    try {
      const response = await axios.get(`${API_URL}/outlook/position-folders`, {
        ...getAuthHeaders(),
        params: { search: pfNumber }
      });
      const pfs = response.data.position_folders || [];
      
      // Find exact match
      const exactMatch = pfs.find(pf => 
        pf.pf_number === pfNumber || 
        pf.id === pfNumber
      );
      
      if (exactMatch) {
        setAutoMatchedPf(exactMatch);
        setPositionFolders([exactMatch, ...pfs.filter(p => p.id !== exactMatch.id)]);
        console.log("Auto-matched PF:", exactMatch.pf_number);
      } else if (pfs.length > 0) {
        setAutoMatchedPf(pfs[0]);
        setPositionFolders(pfs);
      }
    } catch (error) {
      console.error("Error auto-searching PF:", error);
    }
  };
  
  // Auto-search by container or BL number
  const autoSearchByReference = async (reference, type) => {
    try {
      const response = await axios.get(`${API_URL}/outlook/position-folders`, {
        ...getAuthHeaders(),
        params: { search: reference }
      });
      const pfs = response.data.position_folders || [];
      
      if (pfs.length > 0) {
        setAutoMatchedPf(pfs[0]);
        setPositionFolders(pfs);
        console.log(`Auto-matched PF by ${type}:`, pfs[0].pf_number);
      }
    } catch (error) {
      console.error(`Error auto-searching by ${type}:`, error);
    }
  };

  const verifyAuth = async () => {
    try {
      await axios.get(`${API_URL}/auth/me`, getAuthHeaders());
      setIsAuthenticated(true);
    } catch {
      setIsAuthenticated(false);
      localStorage.removeItem("outlook_token");
      setAuthToken(null);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setTwoFactorEmail(response.data.email || email);
        toast.info("Verification code sent to your email");
        return;
      }
      
      // No 2FA - direct login
      const token = response.data.access_token;
      localStorage.setItem("outlook_token", token);
      setAuthToken(token);
      setIsAuthenticated(true);
      toast.success("Logged in successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle 2FA verification
  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/verify-2fa`, {
        email: twoFactorEmail,
        code: twoFactorCode,
      });
      
      const token = response.data.access_token;
      localStorage.setItem("outlook_token", token);
      setAuthToken(token);
      setIsAuthenticated(true);
      setRequires2FA(false);
      setTwoFactorCode("");
      toast.success("Logged in successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
    } finally {
      setLoginLoading(false);
    }
  };

  // Resend 2FA code
  const handleResend2FA = async () => {
    try {
      await axios.post(`${API_URL}/auth/resend-2fa`, { email, password });
      toast.success("New verification code sent");
    } catch (error) {
      toast.error("Failed to resend code");
    }
  };

  // ── Template helpers (Outlook reply integration) ────────────────────
  // Auto-pick the matched PF when match results arrive
  useEffect(() => {
    if (!tplPfPicked && matchResults?.position_folders?.length) {
      setTplPfPicked(matchResults.position_folders[0]);
    }
  }, [matchResults, tplPfPicked]);

  const selectTemplate = (tplId) => {
    const t = EMAIL_TEMPLATES.find((x) => x.id === tplId);
    if (!t) return;
    setTplSelectedId(tplId);
    const senderName = email?.split("@")[0]?.replace(".", " ") || "";
    const { subject, body } = fillTemplate(t, tplPfPicked || {}, senderName);
    setTplSubject(subject);
    setTplBody(body);
  };

  // Re-render placeholders when the operator picks a different PF
  useEffect(() => {
    if (tplSelectedId) selectTemplate(tplSelectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tplPfPicked]);

  const _textToHtml = (txt) =>
    (txt || "")
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/\n/g, "<br>");

  // ── Parse a LogiGrowth HANDOFF BRIEF email into its structured sections.
  const parseHandoffBrief = (subject, body) => {
    const subj = subject || "";
    const text = body || "";
    const isHandoff = /handoff brief/i.test(subj)
      || (/talking points/i.test(text) && /your goal/i.test(text));
    if (!isHandoff) return { isHandoff: false };

    // Contact + company from subject: "🔥 HANDOFF BRIEF: uros @ Company"
    let contactName = "", company = "";
    const sm = (subj.split(/handoff brief:?/i)[1] || "").trim();
    if (sm.includes("@")) {
      const idx = sm.indexOf("@");
      contactName = sm.slice(0, idx).trim();
      company = sm.slice(idx + 1).trim();
    } else {
      contactName = sm;
    }

    const sliceBetween = (startRe, endRes) => {
      const s = text.search(startRe);
      if (s < 0) return "";
      const after = text.slice(s).replace(startRe, "");
      let end = after.length;
      for (const er of endRes) {
        const m = after.search(er);
        if (m >= 0 && m < end) end = m;
      }
      return after.slice(0, end).trim();
    };

    const goal = sliceBetween(/your goal:?/i, [/what they wrote/i, /erp history/i, /talking points/i]);
    const whatTheyWrote = sliceBetween(/what they wrote/i, [/erp history/i, /talking points/i, /↩/, /reply to /i]);
    const erpHistory = sliceBetween(/erp history/i, [/talking points/i, /↩/, /reply to /i]);
    const tpRaw = sliceBetween(/talking points/i, [/↩/, /reply to /i, /sent by europacific/i]);

    let talkingPoints = [];
    if (tpRaw) {
      const lines = tpRaw
        .split(/\n+/)
        .map((l) => l.replace(/\u00a0/g, " ").replace(/\t/g, " ").trim())
        .filter(Boolean);
      // Outlook's Text coercion sometimes puts the list number on its own line
      // (separate from the point text), so track a running buffer instead of
      // assuming "number + text" on one line.
      let cur = null;
      for (const l of lines) {
        const numOnly = l.match(/^(\d+)[.):]?$/);          // "1" or "1." alone
        const numText = l.match(/^(\d+)[.):]?\s+(.+)$/);    // "1 text" / "1. text"
        if (numOnly) {
          if (cur !== null) talkingPoints.push(cur);
          cur = "";
        } else if (numText) {
          if (cur !== null) talkingPoints.push(cur);
          cur = numText[2];
        } else {
          cur = cur === null ? l : cur + " " + l;
        }
      }
      if (cur !== null) talkingPoints.push(cur);
      talkingPoints = talkingPoints.map((p) => p.replace(/\s+/g, " ").trim()).filter(Boolean);
      // Fallback: if no numbering was detected, treat each non-numeric line as a point.
      if (talkingPoints.length === 0) {
        talkingPoints = lines.filter((l) => !/^\d+[.):]?$/.test(l));
      }
    }

    // Reply-to contact email (LogiGrowth threads replies straight to them)
    let replyEmail = "";
    const rt = text.match(/reply to[^<]*<mailto:([^>]+)>/i);
    if (rt) replyEmail = rt[1].trim();
    else {
      const mm = text.match(/mailto:([^\s>]+)/i) || text.match(/[\w.+-]+@[\w.-]+\.\w+/);
      if (mm) replyEmail = (mm[1] || mm[0]).trim();
    }

    return { isHandoff: true, contactName, company, goal, whatTheyWrote, erpHistory, talkingPoints, replyEmail };
  };

  const generateHandoffReply = async () => {
    if (!handoffData?.isHandoff) return;
    const selected = hbSelectedPoints
      .map((i) => handoffData.talkingPoints[i])
      .filter(Boolean);
    if (selected.length === 0 && !hbCustomAngle.trim()) {
      toast.error("Pick at least one talking point (or add a custom angle)");
      return;
    }
    setHbBusy(true);
    try {
      const senderName = window?.Office?.context?.mailbox?.userProfile?.displayName || "";
      const resp = await axios.post(
        `${API_URL}/outlook/handoff/generate-reply`,
        {
          goal: handoffData.goal,
          what_they_wrote: handoffData.whatTheyWrote,
          erp_history: handoffData.erpHistory,
          selected_points: selected,
          custom_angle: hbCustomAngle,
          language: hbLanguage,
          contact_name: handoffData.contactName,
          company: handoffData.company,
          sender_name: senderName,
        },
        getAuthHeaders()
      );
      setHbReply(resp.data?.reply_text || "");
      toast.success("Reply drafted ✓");
    } catch (e) {
      toast.error("AI draft failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setHbBusy(false);
    }
  };

  const insertHandoffReply = async () => {
    const replyText = (hbReply || "").trim();
    if (!replyText) {
      toast.error("Generate a reply first");
      return;
    }
    // Build HTML body: AI reply + a quoted block of the original "what they wrote".
    let html = _textToHtml(replyText);
    if (handoffData?.whatTheyWrote) {
      html +=
        `<br><br><div style="border-left:3px solid #ccc;padding-left:10px;color:#555;">` +
        `<p style="margin:0 0 6px;color:#888;">${handoffData.contactName || "The contact"} wrote:</p>` +
        _textToHtml(handoffData.whatTheyWrote) +
        `</div>`;
    }
    await openOutlookReplyWithTemplate({ bodyHtml: html, bodyText: replyText, subject: null });
  };

  const logHandoffToCrm = async () => {
    if (!handoffData?.isHandoff) return;
    setHbLogging(true);
    try {
      const senderName = window?.Office?.context?.mailbox?.userProfile?.displayName || "";
      const resp = await axios.post(
        `${API_URL}/outlook/handoff/log-to-crm`,
        {
          contact_name: handoffData.contactName,
          company: handoffData.company,
          reply_email: handoffData.replyEmail,
          reply_text: hbReply,
          email_subject: currentEmail?.subject,
          email_message_id: currentEmail?.message_id,
          create_followup: true,
          followup_days: 3,
          sender_name: senderName,
        },
        getAuthHeaders()
      );
      setHbLogged(true);
      const parts = [];
      if (resp.data?.contact_created) parts.push("contact created");
      if (resp.data?.task_created) parts.push("follow-up task in 3 days");
      toast.success("Logged to CRM" + (parts.length ? ` (${parts.join(", ")})` : "") + " ✓");
    } catch (e) {
      toast.error("Log to CRM failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setHbLogging(false);
    }
  };

  // Parse the currently-open email once per render (cheap regex).
  // NOTE: the full body lives in `emailBody` state (currentEmail only carries a
  // short preview), so we feed that to the parser.
  const handoffData = parseHandoffBrief(currentEmail?.subject, emailBody);

  // Auto-jump to the Handoff tab when a LogiGrowth brief is opened.
  useEffect(() => {
    if (
      handoffData?.isHandoff &&
      currentEmail?.message_id &&
      hbAutoSwitchedFor !== currentEmail.message_id
    ) {
      setActiveCategory("operations");
      setActiveTab("handoff");
      setHbAutoSwitchedFor(currentEmail.message_id);
      setHbSelectedPoints([]);
      setHbReply("");
      setHbCustomAngle("");
      setHbLogged(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEmail?.message_id]);

  // 2026-05-27 v3 — Persistent diagnostic state for the template buttons.
  // Toasts disappear and the user couldn't tell what was happening in
  // production. Now every Open Reply / Insert / Copy click writes a
  // human-readable line into `tplLastResult` which is shown right under
  // the buttons until the next click. This solved the "nothing happens"
  // perception bug — the user always sees a clear status.
  const [tplLastResult, setTplLastResult] = useState(null);
  const _setResult = (kind, ok, msg) =>
    setTplLastResult({
      kind,
      ok,
      msg,
      at: new Date().toLocaleTimeString(),
    });

  // Live snapshot of the Office.js context so the user (and we) can see
  // EXACTLY what state Outlook is in when they click a button.
  const [officeDiag, setOfficeDiag] = useState({
    office: false, mailbox: false, item: false,
    itemType: null, mode: "?", asyncReplyAll: false,
  });
  useEffect(() => {
    const probe = () => {
      const Office = window?.Office;
      const mailbox = Office?.context?.mailbox;
      const item = mailbox?.item;
      const composeMode = !!(item?.body && typeof item.body.setAsync === "function");
      setOfficeDiag({
        office: !!Office,
        mailbox: !!mailbox,
        item: !!item,
        itemType: item?.itemType || (item ? "unknown" : null),
        mode: !item ? "no-item" : composeMode ? "compose" : "read",
        asyncReplyAll: !!(item?.displayReplyAllFormAsync),
      });
    };
    probe();
    const t = setInterval(probe, 2000); // re-check while pane is open
    return () => clearInterval(t);
  }, []);

  // ── Attachable customs & company documents (Operations panel "Docs" tab) ──
  const [docItems, setDocItems] = useState([]);
  const [docLoading, setDocLoading] = useState(false);
  const [docSearch, setDocSearch] = useState("");
  // Multi-select: { [docId]: "attach" | "link" }
  const [docSel, setDocSel] = useState({});
  const [docDiag, setDocDiag] = useState("");

  const setDocMode = (docId, modeWanted) => {
    setDocSel((prev) => {
      const next = { ...prev };
      if (next[docId] === modeWanted) delete next[docId]; // toggle off
      else next[docId] = modeWanted;
      return next;
    });
  };

  const loadAttachableDocs = useCallback(async () => {
    setDocLoading(true);
    try {
      const items = [];
      // 1) Public fillable customs forms (Authorisation 42, POA, etc.)
      try {
        const f = await axios.get(`${API_URL}/customs-fillable/forms`);
        const forms = f.data?.forms || f.data || [];
        forms.forEach((fm) => {
          const slug = fm.slug || fm.id;
          if (!slug) return;
          const title = fm.title || fm.name || slug;
          items.push({
            id: `form:${slug}`,
            name: title,
            url: `${API_URL}/customs-fillable/${slug}/blank.pdf`,
            filename: `${title}.pdf`,
            kind: "Customs form",
          });
        });
      } catch (e) { /* forms optional */ }
      // 2) Uploaded CRM / company documents (registration, KYC, etc.)
      try {
        const r = await axios.get(`${API_URL}/crm/uploaded-documents`, getAuthHeaders());
        (r.data?.documents || []).forEach((d) => {
          items.push({
            id: `doc:${d.id}`,
            name: d.name || d.original_filename,
            url: `${API_URL}/crm/uploaded-documents/${d.id}/download`,
            filename: d.original_filename || d.name || "document",
            kind: d.linked_company_name || d.category || "Document",
          });
        });
      } catch (e) { /* docs optional */ }
      setDocItems(items);
    } finally {
      setDocLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (activeTab === "docs") loadAttachableDocs();
  }, [activeTab, loadAttachableDocs]);

  const _composeItem = () => {
    const item = window?.Office?.context?.mailbox?.item;
    const isCompose = item && item.body && typeof item.body.setAsync === "function";
    return isCompose ? item : null;
  };

  // Add ALL selected documents to ONE reply. Each doc is either attached as a
  // PDF (mode "attach") or referenced as a link (mode "link"). Office fetches
  // the public URLs server-side, so they must be reachable without auth.
  //
  // Reliability note: Microsoft's `displayReplyAllForm` URL-attachment path is
  // a known, unresolved bug on OWA / "new Outlook" (createAttachmentFromUri 500)
  // and behaves inconsistently across builds. So we ALWAYS put the document
  // links into the reply body (works on every Outlook → the reply always opens
  // and every doc is reachable), AND additionally pass the real PDF attachments
  // for clients that support them. A watchdog + links-only fallback guarantees
  // the action never dead-ends, and a diagnostic line reports what happened.
  const addSelectedToReply = async () => {
    const Office = window?.Office;
    const chosen = docItems.filter((d) => docSel[d.id]);
    if (chosen.length === 0) {
      toast.error("Select at least one document first.");
      return;
    }
    const fileType = Office?.MailboxEnums?.AttachmentType?.File || "file";
    const attachDocs = chosen.filter((d) => docSel[d.id] === "attach");
    const attachments = attachDocs.map((d) => ({ type: fileType, name: d.filename, url: d.url }));
    const bodyHtml =
      "<br><p><strong>Documents:</strong></p><ul>" +
      chosen
        .map((d) => `<li><a href="${d.url}">${d.name}</a>${docSel[d.id] === "attach" ? " — PDF (also attached where supported)" : ""}</li>`)
        .join("") +
      "</ul>";

    const writeDiag = (m) => setDocDiag(`[${new Date().toLocaleTimeString()}] ${m}`);

    // 1) Add-in already in a compose window → add everything to the open draft.
    const composeItem = _composeItem();
    if (composeItem && typeof composeItem.addFileAttachmentAsync === "function") {
      attachments.forEach((a) => {
        try { composeItem.addFileAttachmentAsync(a.url, a.name, { isInline: false }, () => {}); } catch (e) { /* ignore */ }
      });
      if (composeItem.body && typeof composeItem.body.setSelectedDataAsync === "function") {
        try { composeItem.body.setSelectedDataAsync(bodyHtml, { coercionType: Office.CoercionType.Html }, () => {}); } catch (e) { /* ignore */ }
      }
      toast.success(`Added ${chosen.length} document(s) to the open reply.`);
      writeDiag(`compose: added ${attachments.length} file(s) + links`);
      setDocSel({});
      return;
    }

    const item = Office?.context?.mailbox?.item;
    if (!item) {
      toast.error("Open the email in Outlook (reading pane) and try again.");
      writeDiag("no mailbox item");
      return;
    }

    const platform = Office?.context?.platform;
    let set19 = false;
    try { set19 = Office.context.requirements.isSetSupported("Mailbox", "1.9"); } catch (e) { /* old host */ }

    // ── PRIMARY (reliable): build the reply DRAFT server-side via Microsoft
    // Graph and embed the PDFs as real attachments — no dependence on Outlook
    // downloading our URLs (the flaky createAttachmentFromUri path). Then open
    // the created draft for the user to edit & send. Falls back to the Office.js
    // reply paths below if Graph is unavailable (e.g. preview env).
    const mailbox = Office?.context?.mailbox?.userProfile?.emailAddress;
    const linkDocs = chosen.filter((d) => docSel[d.id] === "link");
    const linksPrefix = linkDocs.length
      ? "<p><strong>Documents:</strong></p><ul>" + linkDocs.map((d) => `<li><a href="${d.url}">${d.name}</a></li>`).join("") + "</ul><br>"
      : "";
    if (mailbox && item.itemId && attachDocs.length) {
      try {
        const restId = Office.context.mailbox.convertToRestId(item.itemId, Office.MailboxEnums.RestVersion.v2_0);
        const resp = await axios.post(`${API_URL}/outlook/graph-draft-reply`, {
          mailbox,
          message_id: restId,
          attachments: attachDocs.map((d) => ({ name: d.filename, url: d.url })),
          reply_all: true,
          body_html_prefix: linksPrefix,
        });
        const data = resp.data || {};
        if (data.ok && data.draft_id) {
          const attachedN = (data.attached || []).length;
          const failedN = (data.failed || []).length;
          try {
            const ewsId = Office.context.mailbox.convertToEwsId(data.draft_id, Office.MailboxEnums.RestVersion.v2_0);
            if (typeof Office.context.mailbox.displayMessageForm === "function") Office.context.mailbox.displayMessageForm(ewsId);
            else if (data.web_link) window.open(data.web_link, "_blank");
          } catch (e) {
            if (data.web_link) window.open(data.web_link, "_blank");
          }
          toast.success(`Draft reply created with ${attachedN} attachment(s)${failedN ? `, ${failedN} failed` : ""} — opening it now.`);
          writeDiag(`graph draft ok: attached=${attachedN} failed=${failedN} (platform=${platform})`);
          setDocSel({});
          return;
        }
      } catch (e) {
        writeDiag(`graph draft failed: ${e?.response?.status || e?.message || e} → Office.js fallback`);
        // fall through to the Office.js reply paths below
      }
    }

    let settled = false;

    // RELIABLE OPEN — string overload `displayReplyAllForm("<html>")`. This is
    // the original Requirement Set 1.1 API and opens reliably on classic
    // Outlook desktop, where the newer 1.9 OBJECT form (`{htmlBody,attachments}`)
    // hangs / silently no-ops (confirmed via the in-app diagnostic). The links
    // for every selected doc are in the body, so all docs are reachable.
    const openReplyWithLinks = (why) => {
      if (settled) return; settled = true;
      try {
        if (typeof item.displayReplyAllForm === "function") item.displayReplyAllForm(bodyHtml);
        else if (typeof item.displayReplyForm === "function") item.displayReplyForm(bodyHtml);
        else { toast.error("Reply isn't available here."); writeDiag("no reply method on item"); return; }
        toast.success(`Opened a reply with ${chosen.length} document link(s) in the body — edit and send.`);
        writeDiag(`opened via string-overload (${why}; platform=${platform}, set1.9=${set19})`);
        setDocSel({});
      } catch (e) {
        toast.error(`Could not open reply: ${e?.message || e}`);
        writeDiag(`string-overload threw: ${e?.message || e}`);
      }
    };

    // BEST-EFFORT real PDF attachments via the 1.9 async object form. On classic
    // Outlook this OPENS the reply (with attachments) but the callback usually
    // never fires, so we mark success immediately and do NOT run a watchdog —
    // an earlier watchdog opened a duplicate reply. If the callback does fire
    // with a failure, we just note it (the doc links are already in the body,
    // and Outlook shows a per-attachment "Retry" link).
    if (attachments.length && set19 && typeof item.displayReplyAllFormAsync === "function") {
      try {
        item.displayReplyAllFormAsync({ htmlBody: bodyHtml, attachments }, (res) => {
          if (res && res.status === Office.AsyncResultStatus.Failed) {
            writeDiag(`async cb failed: ${res.error?.message || "?"} (links are in body)`);
            toast("Some attachments couldn't be downloaded by Outlook — the document links are in the reply body (or click Retry on the attachment).");
          } else if (res) {
            writeDiag(`async cb OK with attachments`);
          }
        });
        settled = true;
        toast.success(`Opening a reply with ${chosen.length} document(s)… edit and send.`);
        writeDiag(`async invoked with attachments (platform=${platform}, set1.9=${set19})`);
        setDocSel({});
        return;
      } catch (e) {
        writeDiag(`async threw: ${e?.message || e} → links reply`);
      }
    }

    // Links-only mode, no 1.9, or async threw → reliable string-overload open.
    openReplyWithLinks(attachments.length ? "no async support" : "links-only");
  };


  const openOutlookReplyWithTemplate = async (opts = {}) => {
    // `opts` may be a React event (when used directly as an onClick handler)
    // or an override object: { bodyHtml, bodyText, subject }. The Handoff tab
    // passes ready-made HTML (AI reply + quoted original) via opts.bodyHtml.
    const _ovBodyHtml = (opts && typeof opts === "object" && "bodyHtml" in opts) ? opts.bodyHtml : null;
    const _ovBodyText = (opts && typeof opts === "object" && "bodyText" in opts) ? opts.bodyText : null;
    const _ovSubject = (opts && typeof opts === "object" && "subject" in opts) ? opts.subject : null;
    const _replyBodyText = _ovBodyText != null ? _ovBodyText : tplBody;
    const _replySubject = _ovSubject != null ? _ovSubject : tplSubject;
    if (!_replyBodyText && !_ovBodyHtml) {
      toast.error("Pick a template first");
      _setResult("Open Reply", false, "No template selected.");
      return;
    }
    setTplBusy(true);

    // Pre-copy as a safety net — clipboard always works.
    try {
      await navigator.clipboard.writeText(`Subject: ${_replySubject || ""}\n\n${_replyBodyText || ""}`);
    } catch { /* clipboard may be unavailable */ }

    try {
      const Office = window?.Office;
      const mailbox = window?.Office?.context?.mailbox;
      const item = mailbox?.item;
      if (!Office || !mailbox) {
        toast.error("Outlook not detected — template is in clipboard. Paste into your reply.");
        _setResult(
          "Open Reply", false,
          "Outlook Office.js is NOT available in this pane (probably opened outside Outlook). " +
          "Template was copied — paste with Ctrl-V into a reply."
        );
        return;
      }
      const htmlBody = _ovBodyHtml != null ? _ovBodyHtml : _textToHtml(_replyBodyText);

      // ── NO SOURCE EMAIL → open a fresh New Message with the template.
      // This is the case when the user opened the pinned plugin from the
      // ribbon without an email selected (or no reading-pane email).
      // Falling back to displayNewMessageForm here means "Open Reply"
      // ALWAYS does something visible — exactly what the user expects.
      if (!item) {
        if (typeof mailbox.displayNewMessageForm === "function") {
          try {
            mailbox.displayNewMessageForm({
              subject: _replySubject || "",
              htmlBody,
            });
            _setResult(
              "Open Reply (no email selected → new compose)", true,
              "No email was selected, so a new compose window has been requested. " +
              "Look in your taskbar — it may be behind Outlook."
            );
            return;
          } catch (eNew) {
            _setResult(
              "Open Reply", false,
              `Could not open compose: ${eNew?.message || eNew}. Template is in clipboard.`
            );
            return;
          }
        }
        _setResult(
          "Open Reply", false,
          "No email is selected and your Outlook does not expose displayNewMessageForm. " +
          "Select an email and try again, or paste from clipboard (template was copied)."
        );
        return;
      }

      // ── COMPOSE-mode detector (2026-05-27 v2 — Office.js canonical).
      // The ONLY reliable compose-mode discriminator is `item.body.setAsync`:
      // it exists only when the mailbox item is being authored. Both
      // `item.body.setSelectedDataAsync` AND `item.subject.getAsync` have
      // shown up on read-mode items in some Outlook Desktop builds, so we
      // do NOT rely on them as primary signals.
      const isComposeMode =
        item.body && typeof item.body.setAsync === "function";

      if (isComposeMode) {
        item.body.setSelectedDataAsync(
          htmlBody,
          { coercionType: Office.CoercionType.Html },
          (res) => {
            if (res.status === Office.AsyncResultStatus.Succeeded) {
              toast.success("Template inserted into the open reply ✓");
              _setResult(
                "Open Reply (compose mode)", true,
                "Pane is already in compose mode — template inserted at cursor."
              );
            } else {
              toast.error(`Insert failed (${res.error?.message || "?"}) — paste from clipboard.`);
              _setResult(
                "Open Reply", false,
                `setSelectedDataAsync failed: ${res.error?.message || "?"}.`
              );
            }
          },
        );
        return;
      }

      // ── READ mode: open a proper Reply-All window (this is what the
      // "Open Reply" button promises — thread + recipients preserved).
      // We deliberately try displayReplyAllForm FIRST because on Outlook
      // Desktop (Win) `displayNewMessageForm` opens the compose window
      // BEHIND the main window — users think nothing happened. Reply
      // forms are always brought to the foreground by Outlook itself.
      //
      // 2026-05-27 — Outlook Classic (Win) silently fails to open the
      // reply window when `htmlBody` exceeds ~32 KB OR contains certain
      // unicode quirks. Office.js throws no error in that case, so the
      // old toast was lying ("Reply window opened ✓"). Defend by:
      //   1. Always staging the body to the clipboard first, so even if
      //      the reply window doesn't materialise the user can paste.
      //   2. Warning up-front if the body is too large for a clean open.
      //   3. Down-grading the success toast wording so it points to the
      //      taskbar / clipboard if nothing appeared.
      const bodySize = (htmlBody || "").length;
      const SIZE_LIMIT = 28000; // safe margin under Outlook's ~32 KB cap
      // Stage to clipboard (best-effort — Office iframes may block).
      try {
        if (navigator?.clipboard?.writeText) {
          // strip tags for plain-text fallback so the user can paste in
          // either rich or plain mode.
          const plain = (htmlBody || "").replace(/<[^>]+>/g, "");
          await navigator.clipboard.writeText(plain);
        }
      } catch (_clipErr) {
        // ignore — clipboard staging is just a safety net
      }

      if (bodySize > SIZE_LIMIT) {
        toast.warning(
          `Template is ${(bodySize / 1024).toFixed(1)} KB — Outlook may fail to open the reply silently. ` +
          `Template has been copied to your clipboard. Click Reply in Outlook then paste.`,
          { duration: 8000 },
        );
      }

      // 2026-05-27 v2 — Switch to the *async* variants of
      // displayReplyAllForm / displayReplyForm so we get an explicit
      // success/failure callback. The sync versions silently no-op on
      // Outlook Desktop's pinned task pane when the underlying call
      // fails (HTML too large, security context wrong, etc.), which is
      // exactly the bug Rok reported on 2026-05-27.
      const _replyToast = (kind) => {
        toast.success(
          `${kind} window opened ✓ Look for it in your taskbar / behind Outlook.`,
          { duration: 5000 },
        );
        _setResult(
          `Open Reply (${kind})`, true,
          `Outlook reported the ${kind} window was opened. If you don't see it, check your taskbar — it often opens behind the main Outlook window.`
        );
      };
      const _replyError = (label, res) => {
        const msg = res?.error?.message || res?.error?.name || "unknown";
        console.warn(`${label} failed:`, res);
        toast.error(
          `${label} failed (${msg}). Template is in your clipboard — open Reply manually and paste with Ctrl-V.`,
          { duration: 9000 },
        );
        _setResult(
          "Open Reply", false,
          `${label} failed: ${msg}. Template is in clipboard — open Reply in Outlook and paste with Ctrl-V.`
        );
      };

      if (typeof item.displayReplyAllFormAsync === "function") {
        item.displayReplyAllFormAsync({ htmlBody }, (res) => {
          if (res.status === Office.AsyncResultStatus.Succeeded) {
            _replyToast("Reply All");
          } else {
            _replyError("displayReplyAllFormAsync", res);
          }
        });
        return;
      }
      if (typeof item.displayReplyAllForm === "function") {
        try {
          item.displayReplyAllForm({ htmlBody });
          _replyToast("Reply All");
          return;
        } catch (e1) {
          console.warn("displayReplyAllForm threw, trying displayReplyForm:", e1);
        }
      }
      if (typeof item.displayReplyFormAsync === "function") {
        item.displayReplyFormAsync({ htmlBody }, (res) => {
          if (res.status === Office.AsyncResultStatus.Succeeded) {
            _replyToast("Reply");
          } else {
            _replyError("displayReplyFormAsync", res);
          }
        });
        return;
      }
      if (typeof item.displayReplyForm === "function") {
        try {
          item.displayReplyForm({ htmlBody });
          _replyToast("Reply");
          return;
        } catch (e2) {
          console.warn("displayReplyForm threw, falling back to new message:", e2);
        }
      }

      // ── Final fallback: pop a fresh compose window pre-addressed to
      // the sender. Used only when reply forms are unavailable (rare —
      // some very old hosts or appointment items).
      const senderAddr = item.from?.emailAddress
                       || item.sender?.emailAddress
                       || (mailbox.userProfile && item.from?.address)
                       || "";
      const baseSubject = (item.subject || "").trim();
      const reSubject = _replySubject?.trim()
        || (baseSubject.toLowerCase().startsWith("re:")
              ? baseSubject
              : (baseSubject ? `RE: ${baseSubject}` : "Reply"));

      const newFormParams = {
        toRecipients: senderAddr ? [senderAddr] : [],
        subject: reSubject,
        htmlBody: htmlBody,
      };

      if (typeof mailbox.displayNewMessageForm === "function") {
        try {
          mailbox.displayNewMessageForm(newFormParams);
          toast.success("New email window opened — check your taskbar if it's hidden behind Outlook");
          return;
        } catch (e3) {
          console.warn("displayNewMessageForm threw, falling back:", e3);
        }
      }
      if (typeof mailbox.displayNewMessageFormAsync === "function") {
        await new Promise((resolve) => {
          mailbox.displayNewMessageFormAsync(newFormParams, (res) => {
            if (res.status === Office.AsyncResultStatus.Succeeded) {
              toast.success("New email window opened — check your taskbar if it's hidden behind Outlook");
            } else {
              toast.error(`Couldn't open new email (${res.error?.message || "?"}). Template is in clipboard.`);
            }
            resolve();
          });
        });
        return;
      }

      toast.error("This Outlook version doesn't expose a compose form — template is in clipboard.");
    } catch (e) {
      console.error("openOutlookReplyWithTemplate failed:", e);
      toast.error(`Couldn't open reply (${e.message}). Template is in clipboard — Ctrl-V to paste.`);
    } finally {
      setTplBusy(false);
    }
  };

  const insertTemplateIntoDraft = async () => {
    // PRIMARY: when the Outlook item is in COMPOSE mode (user already
    // hit Reply → the pinned pane is bound to the draft), drop the
    // template at the cursor.
    // FALLBACK: if there is NO open draft (most common case — plugin is
    // pinned in read mode), do what the user actually wants — open a
    // fresh Reply with the template, same as "Open Reply".
    if (!tplBody) {
      toast.error("Pick a template first");
      return;
    }
    setTplBusy(true);

    // Pre-copy so clipboard always has the body as a safety net.
    try { await navigator.clipboard.writeText(tplBody); } catch {}

    try {
      const Office = window?.Office;
      const item = window?.Office?.context?.mailbox?.item;
      if (!item || !Office) {
        toast.error("Outlook not detected — body is in clipboard.");
        return;
      }
      const htmlBody = _textToHtml(tplBody);

      // ── COMPOSE-mode detector (2026-05-27 v2). The canonical signal is
      // `item.body.setAsync` — only present when authoring a message.
      const isComposeMode =
        item.body && typeof item.body.setAsync === "function";

      if (isComposeMode) {
        item.body.setSelectedDataAsync(
          htmlBody,
          { coercionType: Office.CoercionType.Html },
          (res) => {
            if (res.status === Office.AsyncResultStatus.Succeeded) {
              toast.success("Template inserted at cursor ✓");
              _setResult("Insert in draft", true, "Template inserted at the current cursor position in your draft.");
            } else if (typeof item.body.setAsync === "function") {
              // Fallback: replace whole body
              item.body.setAsync(
                htmlBody,
                { coercionType: Office.CoercionType.Html },
                (r2) => {
                  if (r2.status === Office.AsyncResultStatus.Succeeded) {
                    toast.success("Template inserted into draft ✓");
                    _setResult("Insert in draft", true, "Template replaced the draft body.");
                  } else {
                    toast.error(`Insert failed (${r2.error?.message || "?"}) — paste from clipboard.`);
                    _setResult("Insert in draft", false, `setAsync failed: ${r2.error?.message || "?"}. Body is in clipboard.`);
                  }
                },
              );
            } else {
              toast.error(`Insert failed (${res.error?.message || "?"}) — paste from clipboard.`);
              _setResult("Insert in draft", false, `setSelectedDataAsync failed: ${res.error?.message || "?"}.`);
            }
          },
        );
        return;
      }

      // ── No draft open → fall back to opening a Reply All (same as
      // the "Open Reply" button). We try ReplyAll/Reply FIRST because
      // on Outlook Desktop displayNewMessageForm opens the compose
      // window BEHIND the main window — users think nothing happened.
      // Reply forms are always brought to the foreground by Outlook.
      //
      // 2026-05-27 — see openOutlookReplyWithTemplate for the silent-
      // failure caveat. Stage the body to clipboard + warn on size > 28 KB.
      const mailbox = window?.Office?.context?.mailbox;
      const bodySize = (htmlBody || "").length;
      const SIZE_LIMIT = 28000;
      try {
        if (navigator?.clipboard?.writeText) {
          const plain = (htmlBody || "").replace(/<[^>]+>/g, "");
          await navigator.clipboard.writeText(plain);
        }
      } catch (_e) { /* clipboard staging is best-effort */ }
      if (bodySize > SIZE_LIMIT) {
        toast.warning(
          `Draft body is ${(bodySize / 1024).toFixed(1)} KB — Outlook may fail to open the reply silently. ` +
          `Template has been copied to clipboard. Click Reply in Outlook then paste.`,
          { duration: 8000 },
        );
      }

      if (typeof item.displayReplyAllFormAsync === "function") {
        item.displayReplyAllFormAsync({ htmlBody }, (res) => {
          if (res.status === Office.AsyncResultStatus.Succeeded) {
            toast.success("Reply All window opened ✓ Look in your taskbar.", { duration: 5000 });
          } else {
            console.warn("displayReplyAllFormAsync failed:", res);
            toast.error(
              `Reply window didn't open (${res?.error?.message || "?"}). Template is in clipboard — paste with Ctrl-V.`,
              { duration: 9000 },
            );
          }
        });
        return;
      }
      if (typeof item.displayReplyAllForm === "function") {
        try {
          item.displayReplyAllForm({ htmlBody });
          toast.success(
            "Reply All requested ✓ If the window didn't open, check Outlook taskbar — or paste from clipboard.",
            { duration: 6000 },
          );
          return;
        } catch (e0) { console.warn("displayReplyAllForm threw:", e0); }
      }
      if (typeof item.displayReplyFormAsync === "function") {
        item.displayReplyFormAsync({ htmlBody }, (res) => {
          if (res.status === Office.AsyncResultStatus.Succeeded) {
            toast.success("Reply window opened ✓ Look in your taskbar.", { duration: 5000 });
          } else {
            console.warn("displayReplyFormAsync failed:", res);
            toast.error(
              `Reply window didn't open (${res?.error?.message || "?"}). Template is in clipboard — paste with Ctrl-V.`,
              { duration: 9000 },
            );
          }
        });
        return;
      }
      if (typeof item.displayReplyForm === "function") {
        try {
          item.displayReplyForm({ htmlBody });
          toast.success(
            "Reply requested ✓ If the window didn't open, check Outlook taskbar — or paste from clipboard.",
            { duration: 6000 },
          );
          return;
        } catch (e1) { console.warn("displayReplyForm threw:", e1); }
      }

      // ── Final fallback: brand-new compose window (used only when
      // Reply forms are unavailable — rare hosts/appointment items).
      const senderAddr = item.from?.emailAddress || item.sender?.emailAddress || "";
      const baseSubject = (item.subject || "").trim();
      const reSubject = tplSubject?.trim()
        || (baseSubject.toLowerCase().startsWith("re:")
              ? baseSubject
              : (baseSubject ? `RE: ${baseSubject}` : "Reply"));
      const newFormParams = {
        toRecipients: senderAddr ? [senderAddr] : [],
        subject: reSubject,
        htmlBody,
      };
      if (mailbox && typeof mailbox.displayNewMessageForm === "function") {
        try {
          mailbox.displayNewMessageForm(newFormParams);
          toast.success("No draft was open — opened a new email (check your taskbar if it's hidden)");
          return;
        } catch (e2) { console.warn("displayNewMessageForm threw:", e2); }
      }
      if (mailbox && typeof mailbox.displayNewMessageFormAsync === "function") {
        await new Promise((resolve) => {
          mailbox.displayNewMessageFormAsync(newFormParams, (res) => {
            if (res.status === Office.AsyncResultStatus.Succeeded) {
              toast.success("No draft was open — opened a new email (check your taskbar if it's hidden)");
            } else {
              toast.error(`Couldn't open compose (${res.error?.message || "?"}) — body is in clipboard.`);
            }
            resolve();
          });
        });
        return;
      }
      toast.error("Outlook didn't expose Reply form — body is in clipboard.");
    } catch (e) {
      console.error("insertTemplateIntoDraft failed:", e);
      toast.error(`Insert failed (${e.message}) — body is in clipboard.`);
    } finally {
      setTplBusy(false);
    }
  };

  const copyTemplateToClipboard = async () => {
    if (!tplBody) {
      _setResult("Copy", false, "No template selected.");
      return;
    }
    try {
      await navigator.clipboard.writeText(`Subject: ${tplSubject}\n\n${tplBody}`);
      toast.success("Template copied to clipboard");
      _setResult("Copy", true, "Subject + body copied. Paste with Ctrl-V in your reply.");
    } catch (e) {
      toast.error("Copy failed");
      _setResult("Copy", false, `Clipboard API blocked: ${e?.message || e}`);
    }
  };

  const matchEmail = async () => {
    if (!currentEmail && !emailBody) {
      toast.error("No email context available");
      return;
    }

    setMatchLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/outlook/match-email`,
        {
          email: currentEmail || {
            message_id: `manual_${Date.now()}`,
            subject: "Manual search",
            sender_email: searchQuery || "",
            sender_name: "",
          },
          search_body: emailBody,
        },
        getAuthHeaders()
      );
      setMatchResults(response.data);
      
      // Highlight found references
      const refs = response.data.references_found;
      const totalRefs = (refs.container_numbers?.length || 0) + 
                       (refs.bl_numbers?.length || 0) + 
                       (refs.booking_refs?.length || 0);
      if (totalRefs > 0) {
        toast.success(`Found ${totalRefs} reference(s) in email`);
      }
    } catch (error) {
      toast.error("Failed to match email");
      console.error(error);
    } finally {
      setMatchLoading(false);
    }
  };

  const linkToRecord = async (linkType, linkId, recordName) => {
    if (!currentEmail) {
      toast.error("No email context available");
      return;
    }

    try {
      await axios.post(
        `${API_URL}/outlook/link-email`,
        {
          email: currentEmail,
          link_type: linkType,
          link_id: linkId,
        },
        getAuthHeaders()
      );
      toast.success(`Email linked to ${recordName}`);
      
      // If in Outlook, add category to email
      if (isOutlook && Office.context.mailbox.item) {
        try {
          // Note: Setting categories requires additional permissions
          // Office.context.mailbox.item.categories.addAsync([`Linked: ${linkType}`]);
        } catch (e) {
          console.log("Could not set category:", e);
        }
      }
    } catch (error) {
      toast.error("Failed to link email");
      console.error(error);
    }
  };

  const searchAll = async () => {
    if (!searchQuery.trim()) return;
    
    setSearchLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/outlook/search`,
        { query: searchQuery, search_type: "all" },
        getAuthHeaders()
      );
      setSearchResults(response.data);
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setSearchLoading(false);
    }
  };

  // Track shipment (container, BL, or booking)
  const trackShipment = async () => {
    if (!trackingQuery.trim()) {
      toast.error("Please enter a tracking reference");
      return;
    }
    
    setTrackingLoading(true);
    setTrackingResult(null);
    
    try {
      const response = await axios.post(
        `${API_URL}/tracking/search`,
        {
          query: trackingQuery.trim().toUpperCase(),
          type: trackingType,
          transport_mode: trackingTransport,
          carrier: trackingCarrier === "auto" ? null : trackingCarrier,
        },
        getAuthHeaders()
      );
      
      setTrackingResult(response.data);
      
      // Add to history
      setTrackingHistory(prev => {
        const newHistory = [{ query: trackingQuery, type: trackingType, transport: trackingTransport, timestamp: new Date().toISOString() }, ...prev.filter(h => h.query !== trackingQuery)].slice(0, 5);
        return newHistory;
      });
      
      if (!response.data.found) {
        toast.info("No tracking data found for this reference");
      }
    } catch (error) {
      console.error("Tracking error:", error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' 
        ? errorDetail?.message || "Tracking lookup failed"
        : errorDetail || "Tracking lookup failed";
      toast.error(errorMessage);
    } finally {
      setTrackingLoading(false);
    }
  };

  const createContact = async () => {
    if (!newContact.email || !newContact.name) {
      toast.error("Name and email are required");
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/outlook/create-contact`,
        {
          ...newContact,
          source_email_id: currentEmail?.message_id,
        },
        getAuthHeaders()
      );
      
      if (response.data.success) {
        toast.success("Contact created");
        setShowCreateContact(false);
        setNewContact({ name: "", email: "", company_name: "", company_id: "", phone: "", title: "" });
        setContactCompanySearch("");
        // Refresh matches
        if (currentEmail) matchEmail();
      } else {
        toast.error(response.data.error || "Failed to create contact");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create contact");
    }
  };

  const createActivity = async (linkedTo) => {
    try {
      await axios.post(
        `${API_URL}/outlook/create-activity`,
        {
          email: currentEmail,
          activity_type: newActivity.activity_type,
          notes: newActivity.notes,
          due_date: newActivity.due_date || null,
          linked_to_type: linkedTo?.type || (newActivity.company_id ? "company" : null),
          linked_to_id: linkedTo?.id || newActivity.company_id || null,
          company_name: newActivity.company_name,
        },
        getAuthHeaders()
      );
      toast.success("Activity created");
      setShowCreateActivity(false);
      setNewActivity({ activity_type: "email_received", notes: "", due_date: "", company_id: "", company_name: "" });
      setActivityCompanySearch("");
    } catch (error) {
      toast.error("Failed to create activity");
    }
  };

  // Search Pantheon customers for Contact form
  const searchContactCompanies = async (query) => {
    if (!query || query.length < 2) {
      setContactCompanyResults([]);
      return;
    }
    setContactCompanyLoading(true);
    try {
      const response = await axios.get(`${API_URL}/crm/search-companies`, {
        params: { query: query, limit: 8 },
        ...getAuthHeaders()
      });
      setContactCompanyResults(response.data.companies || []);
      setShowContactCompanyDropdown(true);
    } catch (error) {
      setContactCompanyResults([]);
    } finally {
      setContactCompanyLoading(false);
    }
  };

  // Search Pantheon customers for Activity form
  const searchActivityCompanies = async (query) => {
    if (!query || query.length < 2) {
      setActivityCompanyResults([]);
      return;
    }
    setActivityCompanyLoading(true);
    try {
      const response = await axios.get(`${API_URL}/crm/search-companies`, {
        params: { query: query, limit: 8 },
        ...getAuthHeaders()
      });
      setActivityCompanyResults(response.data.companies || []);
      setShowActivityCompanyDropdown(true);
    } catch (error) {
      setActivityCompanyResults([]);
    } finally {
      setActivityCompanyLoading(false);
    }
  };

  // Request new Pantheon customer
  const requestNewPantheonCustomer = async (name, context) => {
    try {
      await axios.post(`${API_URL}/email/send`, {
        to: "acc@europacific.com",
        subject: `[NEW CUSTOMER REQUEST] ${name}`,
        body: `
New Pantheon customer creation request.

Requested Customer Name: ${name}
Requested By: ${user?.email || "Unknown"}
Context: ${context}
Email Subject: ${currentEmail?.subject || "N/A"}
Email From: ${currentEmail?.sender_email || "N/A"}

Please create this customer in Pantheon and notify the requester.

---
This is an automated message from myEuropacific Outlook Add-in.
        `.trim(),
        from_name: "myEuropacific CRM",
      }, getAuthHeaders());
      toast.success("Request sent to acc@europacific.com");
    } catch (error) {
      toast.error("Failed to send request");
    }
  };

  // ========== NEW FEATURES ==========

  // Fetch Tasks
  const fetchTasks = async () => {
    setTasksLoading(true);
    try {
      const response = await axios.get(`${API_URL}/outlook/tasks`, getAuthHeaders());
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setTasksLoading(false);
    }
  };

  // Complete Task
  const completeTask = async (taskId) => {
    try {
      await axios.post(`${API_URL}/outlook/tasks/${taskId}/complete`, {}, getAuthHeaders());
      toast.success("Task completed!");
      fetchTasks();
    } catch (error) {
      toast.error("Failed to complete task");
    }
  };

  // Create Task
  const createTask = async () => {
    if (!newTask.title) {
      toast.error("Task title is required");
      return;
    }
    try {
      await axios.post(`${API_URL}/outlook/tasks/create`, {
        ...newTask,
        email_message_id: currentEmail?.message_id,
      }, getAuthHeaders());
      toast.success("Task created!");
      setShowCreateTask(false);
      setNewTask({ title: "", description: "", due_date: "", priority: "normal" });
      fetchTasks();
    } catch (error) {
      toast.error("Failed to create task");
    }
  };

  // Fetch Position Folders (with search support)
  const fetchPositionFolders = async () => {
    setPfsLoading(true);
    try {
      const params = {};
      // Use search query if provided
      if (searchQuery && searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      // Also filter by customer email if available
      if (currentEmail?.sender_email && !searchQuery) {
        params.customer_email = currentEmail.sender_email;
      }
      const response = await axios.get(`${API_URL}/outlook/position-folders`, { ...getAuthHeaders(), params });
      setPositionFolders(response.data.position_folders || []);
    } catch (error) {
      console.error("Error fetching PFs:", error);
      toast.error("Failed to search position folders");
    } finally {
      setPfsLoading(false);
    }
  };

  // Link Email to PF
  const linkEmailToPf = async (pfId) => {
    if (!currentEmail) {
      toast.error("No email context available");
      return;
    }
    try {
      await axios.post(`${API_URL}/outlook/position-folders/${pfId}/link-email`, currentEmail, getAuthHeaders());
      toast.success("Email linked to Position Folder!");
    } catch (error) {
      toast.error("Failed to link email");
    }
  };

  // Add Milestone to PF
  const [pfNotificationEmails, setPfNotificationEmails] = useState({ has_emails: false, emails: [] });
  const [editableNotificationEmails, setEditableNotificationEmails] = useState([]);
  const [newNotificationEmail, setNewNotificationEmail] = useState("");
  
  // Check notification emails when opening milestone dialog
  const checkNotificationEmails = async (pf) => {
    try {
      const pfId = pf.pf_number || pf.id;
      const result = await axios.get(
        `${API_URL}/outlook/position-folders/${pfId}/notification-emails`,
        getAuthHeaders()
      );
      setPfNotificationEmails(result.data);
      // Pre-populate editable emails from existing emails + customer_email
      const allEmails = [...(result.data.emails || [])];
      if (result.data.customer_email && !allEmails.includes(result.data.customer_email)) {
        allEmails.unshift(result.data.customer_email);
      }
      setEditableNotificationEmails(allEmails);
    } catch (error) {
      console.error("Error checking notification emails:", error);
      setPfNotificationEmails({ has_emails: false, emails: [] });
      setEditableNotificationEmails([]);
    }
  };
  
  const addNotificationEmail = () => {
    const email = newNotificationEmail.trim();
    if (email && email.includes("@") && !editableNotificationEmails.includes(email)) {
      setEditableNotificationEmails([...editableNotificationEmails, email]);
      setNewNotificationEmail("");
    }
  };
  
  const removeNotificationEmail = (emailToRemove) => {
    setEditableNotificationEmails(editableNotificationEmails.filter(e => e !== emailToRemove));
  };
  
  const addMilestone = async (forceOverride = false) => {
    // Defensive: if a caller passes the SyntheticEvent (e.g. `onClick={addMilestone}`
    // instead of `onClick={() => addMilestone()}`), `forceOverride` would be a DOM
    // event object → axios JSON.stringify hits a circular reference (HTMLButtonElement
    // / React fiber). Coerce to strict boolean.
    forceOverride = forceOverride === true;
    if (!selectedPf) {
      toast.error("No PF selected");
      return;
    }
    // Parse the TYPED date (staff type dd/mm/yyyy rather than picking it). If
    // they typed something we can't understand, stop and tell them — never
    // silently fall through to the backend's `now()` fallback (the exact bug
    // that recorded the current time instead of the entered date).
    const parsedDate = parseTypedMilestoneDate(newMilestone.date);
    if (parsedDate.invalid) {
      toast.error("Please type the date as dd/mm/yyyy (e.g. 23/06/2026), or leave it blank to record it as now.");
      return;
    }
    try {
      const pfId = selectedPf.pf_number || selectedPf.id;
      
      // Include notification_emails - the edited list
      const payload = {
        ...newMilestone,
        date: parsedDate.iso,
        container_number: newMilestone.container_number || (selectedPf.containers?.[0] || selectedPf.container_number || ""),
        force_override: forceOverride,
      };
      
      // Always send the current list of notification emails (they will be saved to PF)
      if (newMilestone.send_notification && editableNotificationEmails.length > 0) {
        payload.notification_emails = editableNotificationEmails;
      }
      
      const result = await axios.post(
        `${API_URL}/outlook/position-folders/${pfId}/milestones`,
        payload,
        getAuthHeaders()
      );
      
      if (result.data.notification_sent) {
        toast.success("Milestone added & customer notified via email!");
      } else if (result.data.notification_emails_missing) {
        toast.warning("Milestone added, but no notification email configured");
      } else {
        toast.success("Milestone added successfully");
      }
      
      setShowAddMilestone(false);
      setNewMilestone({ milestone_type: "customs_cleared", description: "", date: "", notes: "", container_number: "", send_notification: true });
      setNewNotificationEmail("");
      setEditableNotificationEmails([]);
      setPfNotificationEmails({ has_emails: false, emails: [] });
      
      // Refresh milestones if expanded
      if (expandedPfMilestones[pfId]) {
        togglePfMilestones(selectedPf.id, pfId);
      }
    } catch (error) {
      // SeaRates source-of-truth conflict (409) — surface the exact
      // reason and let staff choose to override after manual verification.
      const detail = error?.response?.data?.detail;
      const isConflict = error?.response?.status === 409 &&
        (typeof detail === "object") && detail?.code === "searates_conflict";
      if (isConflict) {
        const ok = window.confirm(
          `⚠️ SeaRates conflict\n\n${detail.message}\n\n` +
          `Press OK to override (you are confirming the box has actually been delivered).\n` +
          `Press Cancel to abort.`
        );
        if (ok) {
          return addMilestone(true);
        }
        return;
      }
      toast.error("Failed to add milestone");
      console.error(error);
      // Surface the actual backend reason so the user knows WHY it failed
      // instead of having to dig through the browser console.
      const detail2 = error?.response?.data?.detail;
      if (detail2) {
        const msg = typeof detail2 === "string"
          ? detail2
          : (detail2.message || JSON.stringify(detail2).substring(0, 250));
        toast.error(`Reason: ${msg}`, { duration: 12000 });
      } else if (error?.response?.status) {
        toast.error(`HTTP ${error.response.status} — see console`, { duration: 10000 });
      } else if (error?.message) {
        toast.error(error.message, { duration: 10000 });
      }
    }
  };


  // Add Status Comment to PF (2026-05-14)
  const addComment = async () => {
    if (!selectedPf) {
      toast.error("No PF selected");
      return;
    }
    const text = (newComment.text || "").trim();
    if (text.length < 3) {
      toast.error("Comment must be at least 3 characters");
      return;
    }
    setSavingComment(true);
    try {
      const pfId = selectedPf.id || selectedPf.pf_number;
      const r = await axios.post(
        `${API_URL}/position-folders/${pfId}/comments`,
        {
          text,
          container_number: newComment.container_number || null,
          source: "outlook",
          notify_customer: !!newComment.notify_customer,
          visible_to_customer: newComment.audience !== "master",
          audience: newComment.audience || "customer",
        },
        getAuthHeaders()
      );
      const alert = r.data?.alert || {};
      const audLabel = newComment.audience === "master" ? "co-loader" : "customer";
      if (newComment.notify_customer && alert.sent) {
        toast.success(`Comment saved — ${audLabel} alert sent to ${alert.to || "notify list"}`);
      } else if (newComment.notify_customer && alert.error === "no_recipients") {
        toast.warning(`Comment saved — no ${audLabel} notification emails on PF (alert skipped)`);
      } else {
        toast.success("Comment saved on PF");
      }
      setShowAddComment(false);
      setNewComment({ text: "", container_number: "", notify_customer: true, audience: "customer" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add comment");
    } finally {
      setSavingComment(false);
    }
  };


  // Order Port Inspection
  const orderInspection = async () => {
    if (!selectedPf) {
      toast.error("No PF selected");
      return;
    }
    if (!newInspection.container_number) {
      toast.error("Container number is required");
      return;
    }
    try {
      const pfId = selectedPf.pf_number || selectedPf.id;
      const result = await axios.post(
        `${API_URL}/outlook/position-folders/${pfId}/inspections`,
        newInspection,
        getAuthHeaders()
      );
      toast.success(result.data.message || "Inspection ordered successfully");
      setShowOrderInspection(false);
      setNewInspection({ inspection_type: "customs", container_number: "", port: "KOPER", notes: "", urgency: "normal" });
    } catch (error) {
      toast.error("Failed to order inspection");
      console.error(error);
    }
  };

  // Upload document to PF
  const uploadPfDocument = async (files) => {
    if (!selectedPf || !files || files.length === 0) {
      toast.error("Please select a PF first");
      return;
    }

    setPfDocUploading(true);
    try {
      const pfId = selectedPf.id || selectedPf.pf_number;
      console.log("Uploading to PF:", pfId, "Files:", files.length);
      
      // Upload files one by one (backend accepts single file)
      for (const file of files) {
        console.log("Uploading file:", file.name, "Size:", file.size, "Type:", file.type);
        
        const formData = new FormData();
        formData.append("file", file);
        formData.append("tag", pfDocType);
        formData.append("description", `Uploaded from Outlook: ${file.name}`);

        const response = await axios.post(`${API_URL}/position-folders/${pfId}/documents`, formData, {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            "Content-Type": "multipart/form-data"
          }
        });
        console.log("Upload response:", response.data);
      }

      toast.success(`${files.length} document(s) uploaded to ${selectedPf.pf_number}`);
      setShowPfDocUpload(false);
    } catch (error) {
      console.error("Upload error:", error);
      console.error("Error response:", error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to upload document";
      toast.error(errorMsg);
    } finally {
      setPfDocUploading(false);
      setPfDocDragging(false);
    }
  };

  // PF Doc drag handlers
  const handlePfDocDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    pfDocDragCounter.current++;
    if (pfDocDragCounter.current === 1) {
      setPfDocDragging(true);
    }
  };

  // Save DVH state on the selected PF — auto-closes the follow-up
  // loop on the next backend tick when either a number is entered OR
  // the Pending/Done toggle is flipped to Done.
  const saveDvhNumber = async () => {
    if (!selectedPf) return;
    const trimmed = (dvhNumberInput || "").trim();
    // Allow either a number OR the toggle alone
    if (!trimmed && !dvhDoneToggle) {
      toast.error("Enter a DVH number or toggle to Done");
      return;
    }
    setDvhSaving(true);
    try {
      const pfId = selectedPf.id || selectedPf.pf_number;
      const payload = { dvh_done: dvhDoneToggle };
      if (trimmed) payload.dvh_number = trimmed;
      await axios.put(
        `${API_URL}/position-folders/${pfId}`,
        payload,
        getAuthHeaders()
      );
      const action = trimmed
        ? `DVH ${trimmed} saved`
        : (dvhDoneToggle ? "DVH marked as Done" : "DVH marked as Pending");
      toast.success(`${action} for ${selectedPf.pf_number}`);
      setShowDvhDialog(false);
      setDvhNumberInput("");
    } catch (err) {
      console.error("Save DVH error:", err);
      toast.error(err.response?.data?.detail || "Failed to save DVH");
    } finally {
      setDvhSaving(false);
    }
  };

  // Save DIZ state on the selected PF — export-side mirror of saveDvhNumber.
  const saveDizNumber = async () => {
    if (!selectedPf) return;
    const trimmed = (dizNumberInput || "").trim();
    if (!trimmed && !dizDoneToggle) {
      toast.error("Enter a DIZ number or toggle to Done");
      return;
    }
    setDizSaving(true);
    try {
      const pfId = selectedPf.id || selectedPf.pf_number;
      const payload = { diz_done: dizDoneToggle };
      if (trimmed) payload.diz_number = trimmed;
      await axios.put(
        `${API_URL}/position-folders/${pfId}`,
        payload,
        getAuthHeaders()
      );
      const action = trimmed
        ? `DIZ ${trimmed} saved`
        : (dizDoneToggle ? "DIZ marked as Done" : "DIZ marked as Pending");
      toast.success(`${action} for ${selectedPf.pf_number}`);
      setShowDizDialog(false);
      setDizNumberInput("");
    } catch (err) {
      console.error("Save DIZ error:", err);
      toast.error(err.response?.data?.detail || "Failed to save DIZ");
    } finally {
      setDizSaving(false);
    }
  };

  const handlePfDocDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    pfDocDragCounter.current--;
    if (pfDocDragCounter.current === 0) {
      setPfDocDragging(false);
    }
  };

  const handlePfDocDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handlePfDocDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    pfDocDragCounter.current = 0;
    setPfDocDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      uploadPfDocument(files);
    }
  };

  // Create new Position Folder
  const createNewPf = async () => {
    if (!newPf.shipper || !newPf.consignee) {
      toast.error("Shipper and Consignee are required");
      return;
    }

    setCreatingPf(true);
    try {
      const response = await axios.post(`${API_URL}/position-folders`, {
        ...newPf,
        pantheon_customer_id: newPf.customer_id || null,
        pantheon_customer_name: newPf.customer_name || null,
        source: "outlook_addin",
        source_email_id: currentEmail?.message_id,
        source_email_subject: currentEmail?.subject,
      }, getAuthHeaders());

      toast.success(`PF ${response.data.pf_number} created successfully!`);
      setShowCreatePf(false);
      setNewPf({
        shipper: "",
        consignee: "",
        origin_port: "",
        destination_port: "",
        container_type: "40HC",
        incoterm: "FOB",
        commodity: "",
        notes: "",
        customer_id: "",
        customer_name: "",
      });
      setPfCustomerSearch("");
      setPfCustomerResults([]);
      // Refresh PFs
      fetchPositionFolders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create PF");
    } finally {
      setCreatingPf(false);
    }
  };

  // Search Pantheon customers for PF
  const searchPfCustomers = async (query) => {
    if (!query || query.length < 2) {
      setPfCustomerResults([]);
      return;
    }
    setPfCustomerLoading(true);
    try {
      const response = await axios.get(`${API_URL}/crm/search-companies`, {
        params: { query: query, limit: 10 },
        ...getAuthHeaders()
      });
      setPfCustomerResults(response.data.companies || []);
      setShowPfCustomerDropdown(true);
    } catch (error) {
      setPfCustomerResults([]);
    } finally {
      setPfCustomerLoading(false);
    }
  };

  // Create new Trucking Order
  const createNewTruckingOrder = async () => {
    if (!newTruckingOrder.pickup_location || !newTruckingOrder.delivery_location) {
      toast.error("Pickup and Delivery locations are required");
      return;
    }

    setCreatingTruckingOrder(true);
    try {
      const response = await axios.post(`${API_URL}/transport-orders`, {
        ...newTruckingOrder,
        order_type: "trucking",
        source: "outlook_addin",
        source_email_id: currentEmail?.message_id,
        source_email_subject: currentEmail?.subject,
        customer_email: currentEmail?.sender_email,
      }, getAuthHeaders());

      toast.success(`Trucking order ${response.data.reference || response.data.id} created!`);
      setShowCreateTruckingOrder(false);
      setNewTruckingOrder({
        pickup_location: "",
        delivery_location: "",
        pickup_date: "",
        container_number: "",
        cargo_description: "",
        weight: "",
        special_instructions: "",
      });
      // Refresh orders
      fetchTransportOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create trucking order");
    } finally {
      setCreatingTruckingOrder(false);
    }
  };

  // Submit suggestion
  const submitSuggestion = async () => {
    if (!suggestionText.trim()) {
      toast.error("Please enter your suggestion");
      return;
    }

    setSubmittingSuggestion(true);
    try {
      await axios.post(`${API_URL}/outlook/suggestions`, {
        suggestion: suggestionText,
        plugin: "operations",
        user_email: user?.email,
        user_name: user?.full_name,
        email_context: currentEmail?.subject,
      }, getAuthHeaders());

      toast.success("Thank you for your suggestion!");
      setSuggestionText("");
    } catch (error) {
      toast.error("Failed to submit suggestion");
    } finally {
      setSubmittingSuggestion(false);
    }
  };

  // Fetch milestones for a PF
  const fetchPfMilestones = async (pfId, pfNumber) => {
    const key = pfId || pfNumber;
    setPfMilestonesLoading(prev => ({ ...prev, [key]: true }));
    try {
      const response = await axios.get(`${API_URL}/position-folders/${pfId}/milestones`, getAuthHeaders());
      setPfMilestonesData(prev => ({ ...prev, [key]: response.data.milestones || [] }));
    } catch (error) {
      console.error("Failed to fetch milestones:", error);
      setPfMilestonesData(prev => ({ ...prev, [key]: [] }));
    } finally {
      setPfMilestonesLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Toggle milestones view for a PF
  const togglePfMilestones = (pfId, pfNumber) => {
    const key = pfId || pfNumber;
    const isExpanded = expandedPfMilestones[key];
    
    setExpandedPfMilestones(prev => ({ ...prev, [key]: !isExpanded }));
    
    if (!isExpanded && !pfMilestonesData[key]) {
      fetchPfMilestones(pfId, pfNumber);
    }
  };

  // Fetch Offers
  const fetchOffers = async () => {
    setOffersLoading(true);
    try {
      const params = {};
      if (currentEmail?.sender_email) {
        params.customer_email = currentEmail.sender_email;
      }
      const response = await axios.get(`${API_URL}/outlook/offers`, { ...getAuthHeaders(), params });
      setOffers(response.data.offers || []);
    } catch (error) {
      console.error("Error fetching offers:", error);
    } finally {
      setOffersLoading(false);
    }
  };

  // Fetch Transport Orders
  const fetchTransportOrders = async () => {
    setOrdersLoading(true);
    try {
      const params = {};
      if (currentEmail?.sender_email) {
        params.customer_email = currentEmail.sender_email;
      }
      // Also search by PF number extracted from email
      if (detectedReferences?.pf_numbers?.length > 0) {
        params.pf_number = detectedReferences.pf_numbers[0];
      }
      const response = await axios.get(`${API_URL}/outlook/transport-orders`, { ...getAuthHeaders(), params });
      setTransportOrders(response.data.transport_orders || []);
    } catch (error) {
      console.error("Error fetching transport orders:", error);
    } finally {
      setOrdersLoading(false);
    }
  };

  // Fetch Finance Status
  const fetchFinanceStatus = async () => {
    if (!currentEmail?.sender_email) return;
    setFinanceLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/outlook/finance/customer-status?customer_email=${encodeURIComponent(currentEmail.sender_email)}`,
        getAuthHeaders()
      );
      setFinanceStatus(response.data);
    } catch (error) {
      console.error("Error fetching finance:", error);
    } finally {
      setFinanceLoading(false);
    }
  };

  // Save Email to Repository
  const saveToRepository = async (category) => {
    if (!currentEmail) {
      toast.error("No email context available");
      return;
    }
    try {
      await axios.post(`${API_URL}/outlook/email-repository/save`, {
        ...currentEmail,
        category,
      }, getAuthHeaders());
      toast.success("Email saved to repository!");
    } catch (error) {
      toast.error("Failed to save email");
    }
  };

  // Fetch Dashboard Stats
  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/outlook/dashboard-stats`, getAuthHeaders());
      setDashboardStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  // ========== PA FUNCTIONS ==========

  // Fetch PA dashboard stats
  const fetchPaStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/pa/dashboard`, getAuthHeaders());
      setPaStats(res.data.stats);
    } catch (err) { console.error("PA stats error:", err); }
  };

  // Fetch PA emails
  const fetchPaEmails = async () => {
    setPaEmailsLoading(true);
    try {
      const params = paEmailFilter !== "all" ? `?status=${paEmailFilter}&limit=30` : "?limit=30";
      const res = await axios.get(`${API_URL}/pa/emails${params}`, getAuthHeaders());
      setPaEmails(res.data.emails || []);
    } catch (err) { console.error("PA emails error:", err); }
    finally { setPaEmailsLoading(false); }
  };

  // Fetch PA email detail
  const fetchPaEmailDetail = async (emailId) => {
    try {
      const res = await axios.get(`${API_URL}/pa/emails/${emailId}`, getAuthHeaders());
      setPaSelectedEmail(res.data);
    } catch (err) { toast.error("Failed to load email detail"); }
  };

  // Fetch PA approval queue
  const fetchPaQueue = async () => {
    setPaQueueLoading(true);
    try {
      const mineParam = paQueueFilter === "mine" ? "?mine=true" : "";
      const res = await axios.get(`${API_URL}/pa/queue${mineParam}`, getAuthHeaders());
      setPaQueue(res.data.drafts || []);
    } catch (err) { console.error("PA queue error:", err); }
    finally { setPaQueueLoading(false); }
  };

  // PA queue actions (approve/edit/reject)
  const handlePaQueueAction = async (draftId, action, editedBody) => {
    try {
      await axios.post(`${API_URL}/pa/queue/${draftId}`, {
        action, edited_body: editedBody, acted_by: "outlook_user",
      }, getAuthHeaders());
      toast.success(`Draft ${action === "approve" ? "approved" : action === "reject" ? "rejected" : "updated"}`);
      fetchPaQueue();
      fetchPaStats();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  // PA queue — cleanup stats (aging buckets)
  const fetchPaQueueStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/pa/queue-cleanup-stats`, getAuthHeaders());
      setPaQueueStats(res.data);
    } catch (err) { console.error("queue stats error:", err); }
  };

  // PA queue — bulk reject (manual: explicit IDs or by age)
  const handleBulkReject = async ({ draftIds, olderThanDays, reason, allPending }) => {
    setPaBulkLoading(true);
    try {
      const res = await axios.post(`${API_URL}/pa/queue-bulk-reject`, {
        draft_ids: draftIds || null,
        older_than_days: olderThanDays ?? null,
        keep_last_hours: olderThanDays != null ? 24 : 0,
        all_pending: allPending || false,
        rejected_by: email || "outlook_user",
        reason: reason || "bulk_cleanup",
      }, getAuthHeaders());
      const n = res.data.rejected_count || 0;
      if (n === 0) toast.info("Nothing to reject");
      else toast.success(`${n} draft${n === 1 ? "" : "s"} rejected`);
      setPaQueueSelected(new Set());
      fetchPaQueue();
      fetchPaStats();
      fetchPaQueueStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk reject failed");
    } finally { setPaBulkLoading(false); }
  };

  const handlePaRewrite = async (draftId) => {
    const instruction = (paRewriteText[draftId] || "").trim();
    if (!instruction) { toast.info("Type a rewrite suggestion first"); return; }
    setPaRewriteLoading(draftId);
    try {
      await axios.post(`${API_URL}/pa/queue/${draftId}`, {
        action: "rewrite", rewrite_instruction: instruction, acted_by: email || "outlook_user",
      }, getAuthHeaders());
      toast.success("Draft rewritten by AI");
      setPaRewriteText(prev => ({ ...prev, [draftId]: "" }));
      fetchPaQueue();
      if (paCurrentDraft?.draft?.draft_id === draftId) ensureDraftForCurrentEmail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rewrite failed");
    } finally { setPaRewriteLoading(null); }
  };

  // PA queue — daily auto-cleanup (server-side). Note: confirmation is
  // handled by the 2-click button pattern in the queue UI (Outlook task
  // panes block native window.confirm). The caller is responsible for
  // gating the call behind a confirm step.
  const handleAutoCleanup = async (days = 7) => {
    await handleBulkReject({ olderThanDays: days, reason: `auto_cleanup_${days}d` });
  };

  // Ensure a draft exists for the email currently displayed in Outlook
  const ensureDraftForCurrentEmail = async () => {
    if (!currentEmail) { setPaCurrentDraft(null); return; }
    setPaCurrentDraftLoading(true);
    try {
      const res = await axios.post(`${API_URL}/pa/emails/ensure-draft-for-current`, {
        conversation_id: currentEmail.conversation_id,
        message_id: currentEmail.message_id,
        subject: currentEmail.subject,
        from_email: currentEmail.sender_email,
        sender_name: currentEmail.sender_name,
        body_preview: (currentEmail.body_preview || currentEmail.body || "").slice(0, 1000),
        received_at: currentEmail.received_at,
      }, getAuthHeaders());
      setPaCurrentDraft(res.data);
    } catch (err) {
      console.error("ensure-draft error:", err);
      setPaCurrentDraft(null);
    } finally { setPaCurrentDraftLoading(false); }
  };

  // Approve/Reject the pinned "current email" draft from the Queue tab
  const handleCurrentDraftAction = async (action, editedBody) => {
    if (!paCurrentDraft?.draft?.draft_id) return;
    try {
      await axios.post(`${API_URL}/pa/queue/${paCurrentDraft.draft.draft_id}`, {
        action, edited_body: editedBody, acted_by: email || "outlook_user",
      }, getAuthHeaders());
      toast.success(`Current email ${action === "approve" ? "approved & sent" : action === "reject" ? "rejected" : "updated"}`);
      if (action === "approve" || action === "reject") {
        // Close the current-email draft card once it's been sent/rejected.
        setPaCurrentDraft(null);
      } else {
        ensureDraftForCurrentEmail();
      }
      fetchPaQueue();
      fetchPaStats();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
  };

  // PA ERP search
  const searchPaErp = async () => {
    if (!paErpQuery.trim()) return;
    setPaErpLoading(true);
    setPaErpResult(null);
    try {
      const res = await axios.get(`${API_URL}/pa/erp/find-shipment?reference=${encodeURIComponent(paErpQuery.trim())}`, getAuthHeaders());
      setPaErpResult(res.data);
      if (res.data.count === 0) toast.info("No results found");
    } catch (err) { toast.error("ERP search failed"); }
    finally { setPaErpLoading(false); }
  };

  // PA ERP get status
  const getPaErpStatus = async (pfNumber) => {
    try {
      const res = await axios.get(`${API_URL}/pa/erp/status/${pfNumber}`, getAuthHeaders());
      setPaErpResult({ reference: pfNumber, status_detail: res.data, results: [], count: 0 });
    } catch (err) { toast.error("Status lookup failed"); }
  };

  // PA take action on email
  const handlePaEmailAction = async (emailId, action) => {
    try {
      await axios.put(`${API_URL}/pa/emails/${emailId}/action`, {
        action, acted_by: "outlook_user",
      }, getAuthHeaders());
      toast.success(`Action: ${action}`);
      fetchPaEmails();
      fetchPaStats();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
  };

  // PA send reply to email
  const handlePaReply = async (emailId) => {
    if (!paReplyBody.trim()) { toast.error("Reply body required"); return; }
    setPaReplySending(true);
    try {
      const res = await axios.post(`${API_URL}/pa/emails/${emailId}/reply`, {
        to_email: paReplyTo,
        subject: paReplySubject,
        body: paReplyBody,
        cc: paReplyCc ? `${paReplyCc}, ${email}` : email || null,
        replied_by: email || "outlook_user",
      }, getAuthHeaders());
      if (res.data.success) {
        toast.success("Reply sent!");
        setPaReplyBody(""); setPaReplyCc(""); setPaReplyMode(false);
        // Close overlay and return to inbox list
        setPaSelectedEmail(null);
        fetchPaEmails();
        fetchPaStats();
      } else {
        toast.error(`Send failed: ${res.data.error || "Unknown error"}`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Reply failed"); }
    setPaReplySending(false);
  };

  // PA add internal note
  const handlePaAddNote = async (emailId) => {
    if (!paNote.trim()) return;
    try {
      await axios.post(`${API_URL}/pa/emails/${emailId}/note`, {
        text: paNote,
        added_by: "outlook_user",
      }, getAuthHeaders());
      toast.success("Note added");
      setPaNote("");
      const updated = await axios.get(`${API_URL}/pa/emails/${emailId}`, getAuthHeaders());
      setPaSelectedEmail(updated.data);
    } catch (err) { toast.error("Note failed"); }
  };

  // Open PA email detail with reply fields pre-filled
  const openPaEmailForReply = async (emailId) => {
    try {
      const res = await axios.get(`${API_URL}/pa/emails/${emailId}`, getAuthHeaders());
      const em = res.data;
      setPaSelectedEmail(em);
      setPaReplyTo(em.from_email || "");
      const refTag = em.reference ? ` [${em.reference}]` : "";
      setPaReplySubject(`Re: ${em.subject || ""}${refTag}`);
      setPaReplyBody("");
      setPaReplyCc("");
      setPaNote("");
      setPaReplyMode(false);
      setPaIsEditingDraft(false);
      setPaEditingDraftBody("");
    } catch (err) { toast.error("Error loading email"); }
  };

  // PA draft actions: approve, edit, reject
  const handlePaDraftAction = async (draftId, action, editedBody) => {
    setPaDraftActionLoading(true);
    try {
      const payload = { action, acted_by: email || "outlook_user" };
      if (action === "edit" && editedBody) payload.edited_body = editedBody;
      if (action === "reject") payload.notes = "Declined from Outlook plugin";
      const res = await axios.post(`${API_URL}/pa/queue/${draftId}`, payload, getAuthHeaders());
      if (res.data.success) {
        toast.success(action === "approve" ? "Draft approved!" : action === "reject" ? "Draft declined" : "Draft updated");
        setPaIsEditingDraft(false);
        // Close overlay and return to drafted inbox list
        setPaSelectedEmail(null);
        setPaReplyMode(false);
        fetchPaEmails();
        fetchPaStats();
        fetchPaQueue();
      }
    } catch (err) { toast.error(err.response?.data?.detail || `${action} failed`); }
    setPaDraftActionLoading(false);
  };

  // ─── PA 3-Tier Notification Polling ───
  const fetchPaNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/pa/notifications`, getAuthHeaders());
      const newData = res.data;
      const newTotal = newData.counts?.total || 0;
      const prevTotal = prevNotifCountRef.current;

      // Show toast for NEW urgent items
      if (newData.tier1.length > 0 && newTotal > prevTotal) {
        const newest = newData.tier1[0];
        toast.error(newest.title, { description: newest.detail, duration: 6000 });
      }
      // Show toast for NEW action items
      else if (newData.tier2.length > 0 && newTotal > prevTotal) {
        const newest = newData.tier2[0];
        toast.warning(newest.title, { description: newest.detail, duration: 4000 });
      }

      prevNotifCountRef.current = newTotal;
      setPaNotifications(newData);
    } catch (err) { console.error("Notification poll error:", err); }
  }, [authToken]);

  // Start/stop notification polling when on PA tab
  useEffect(() => {
    if (!isAuthenticated || activeCategory !== "pa") {
      if (paNotifPollRef.current) { clearInterval(paNotifPollRef.current); paNotifPollRef.current = null; }
      return;
    }
    // Fetch immediately, then poll every 30s
    fetchPaNotifications();
    paNotifPollRef.current = setInterval(fetchPaNotifications, 30000);
    return () => { if (paNotifPollRef.current) clearInterval(paNotifPollRef.current); };
  }, [isAuthenticated, activeCategory, fetchPaNotifications]);

  // Auto-refresh PA queue/inbox every 5 minutes
  const paDataPollRef = useRef(null);
  useEffect(() => {
    if (!isAuthenticated || activeCategory !== "pa") {
      if (paDataPollRef.current) { clearInterval(paDataPollRef.current); paDataPollRef.current = null; }
      return;
    }
    const refreshPaData = () => {
      if (activeTab === "pa_inbox") { fetchPaEmails(); fetchPaStats(); }
      if (activeTab === "pa_queue") { fetchPaQueue(); fetchPaStats(); fetchPaQueueStats(); }
    };
    paDataPollRef.current = setInterval(refreshPaData, 300000); // 5 minutes
    return () => { if (paDataPollRef.current) clearInterval(paDataPollRef.current); };
  }, [isAuthenticated, activeCategory, activeTab]);

  // Fetch internal domains for display
  const fetchPaInternalDomains = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/pa/internal-domains`, getAuthHeaders());
      setPaInternalDomains(res.data.domains || []);
    } catch (err) { console.error("Internal domains fetch error:", err); }
  }, [authToken]);

  useEffect(() => {
    if (isAuthenticated && activeCategory === "pa") fetchPaInternalDomains();
  }, [isAuthenticated, activeCategory, fetchPaInternalDomains]);

  // ─── Action Query Params (deep links) ───
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const actionParam = params.get("action");
    const ticketParam = params.get("ticket");
    const tabParam = params.get("tab");

    if (tabParam === "pa_inbox" || tabParam === "pa_queue" || tabParam === "pa_erp") {
      setActiveCategory("pa");
      setActiveTab(tabParam);
    }
    if (actionParam === "approve-draft" && ticketParam) {
      setActiveCategory("pa");
      setActiveTab("pa_queue");
      // Auto-open the ticket
      setTimeout(() => openPaEmailForReply(ticketParam), 500);
    }
    if (actionParam === "review-escalation" && ticketParam) {
      setActiveCategory("pa");
      setActiveTab("pa_inbox");
      setPaEmailFilter("escalated");
      setTimeout(() => openPaEmailForReply(ticketParam), 500);
    }
  }, [isAuthenticated]);

  // ─── Sidebar PA Ticket Detection ───
  const checkSidebarTicket = async () => {
    if (!currentEmail?.conversation_id) { setSidebarTicket(null); return; }
    setSidebarLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/pa/emails/by-conversation/${encodeURIComponent(currentEmail.conversation_id)}`,
        getAuthHeaders()
      );
      if (res.data.found) {
        setSidebarTicket(res.data);
      } else {
        setSidebarTicket(null);
      }
    } catch (err) {
      console.error("Sidebar ticket check failed:", err);
      setSidebarTicket(null);
    }
    setSidebarLoading(false);
  };

  const handleSidebarApprove = async () => {
    if (!sidebarTicket?.ticket) return;
    setSidebarActionLoading(true);
    try {
      const res = await axios.put(
        `${API_URL}/pa/emails/${sidebarTicket.ticket}/approve`,
        { source: "outlook_sidebar", approved_by: email },
        getAuthHeaders()
      );
      if (res.data.already_handled) {
        toast.info(res.data.conflict?.message || "Already handled");
      } else {
        toast.success("Booking approved from Outlook");
      }
      // Refresh sidebar card
      checkSidebarTicket();
    } catch (err) { toast.error(err.response?.data?.detail || "Approval failed"); }
    setSidebarActionLoading(false);
  };

  const handleSidebarReject = async (reason) => {
    if (!sidebarTicket?.ticket) return;
    setSidebarActionLoading(true);
    try {
      const res = await axios.put(
        `${API_URL}/pa/emails/${sidebarTicket.ticket}/reject`,
        { source: "outlook_sidebar", reason: reason || "Rejected from Outlook sidebar", rejected_by: email },
        getAuthHeaders()
      );
      if (res.data.already_handled) {
        if (res.data.success === false) {
          toast.error(res.data.conflict?.message || "Cannot reject — already approved");
        } else {
          toast.info(res.data.conflict?.message || "Already handled");
        }
      } else {
        toast.success("Booking rejected from Outlook");
      }
      checkSidebarTicket();
    } catch (err) { toast.error(err.response?.data?.detail || "Rejection failed"); }
    setSidebarActionLoading(false);
  };

  // Load data when tab changes
  useEffect(() => {
    if (!isAuthenticated) return;
    
    if (activeTab === "tasks") fetchTasks();
    if (activeTab === "pf") fetchPositionFolders();
    if (activeTab === "offers") fetchOffers();
    if (activeTab === "orders") fetchTransportOrders();
    if (activeTab === "finance") fetchFinanceStatus();
    if (activeTab === "whatsapp") detectWhatsAppTicket();
    if (activeTab === "pa_inbox") { fetchPaEmails(); fetchPaStats(); }
    if (activeTab === "pa_queue") { fetchPaQueue(); fetchPaStats(); fetchPaQueueStats(); ensureDraftForCurrentEmail(); }
    if (activeTab === "pa_erp") { fetchPaStats(); }
    if (activeTab === "pa_notif") { fetchPaNotifications(); fetchPaStats(); }
  }, [activeTab, isAuthenticated]);

  // Refresh pinned "current email" draft whenever the Outlook reading-pane email changes
  useEffect(() => {
    if (!isAuthenticated) return;
    if (activeTab !== "pa_queue") return;
    if (!currentEmail) { setPaCurrentDraft(null); return; }
    ensureDraftForCurrentEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEmail?.message_id, currentEmail?.conversation_id, activeTab, isAuthenticated]);

  // Re-fetch the queue when the user toggles Mine ↔ All on the Approval Queue tab.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (activeTab !== "pa_queue") return;
    fetchPaQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paQueueFilter]);

  // Detect WhatsApp ticket from current email
  const detectWhatsAppTicket = async () => {
    if (!currentEmail) {
      setWhatsappTicket(null);
      return;
    }
    
    // Check if this is a WhatsApp ticket email
    const subject = currentEmail.subject || "";
    const ticketMatch = subject.match(/WhatsApp Ticket #(WA-\d{14}-\d{4})/i);
    
    if (!ticketMatch) {
      setWhatsappTicket(null);
      return;
    }
    
    const ticketId = ticketMatch[1];
    setWhatsappLoading(true);
    
    try {
      const response = await axios.get(
        `${API_URL}/whatsapp/tickets/${ticketId}`,
        getAuthHeaders()
      );
      setWhatsappTicket(response.data);
      setWhatsappReply("");
    } catch (error) {
      console.error("Error fetching WhatsApp ticket:", error);
      setWhatsappTicket(null);
      toast.error("Could not load WhatsApp ticket");
    } finally {
      setWhatsappLoading(false);
    }
  };

  // Send WhatsApp reply
  const sendWhatsAppReply = async () => {
    if (!whatsappTicket || !whatsappReply.trim()) return;
    
    setSendingWhatsappReply(true);
    try {
      await axios.post(
        `${API_URL}/whatsapp/tickets/${whatsappTicket.ticket_id}/reply`,
        { message: whatsappReply },
        getAuthHeaders()
      );
      toast.success("WhatsApp reply sent!");
      setWhatsappReply("");
      // Refresh ticket data
      detectWhatsAppTicket();
    } catch (error) {
      console.error("Error sending WhatsApp reply:", error);
      toast.error(error.response?.data?.detail || "Failed to send reply");
    } finally {
      setSendingWhatsappReply(false);
    }
  };

  // Update WhatsApp ticket status
  const updateWhatsAppStatus = async (newStatus) => {
    if (!whatsappTicket) return;
    
    try {
      await axios.patch(
        `${API_URL}/whatsapp/tickets/${whatsappTicket.ticket_id}/status`,
        { status: newStatus },
        getAuthHeaders()
      );
      toast.success(`Ticket marked as ${newStatus}`);
      setWhatsappTicket({ ...whatsappTicket, status: newStatus });
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  // Load dashboard stats on auth
  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardStats();
      fetchPaStats(); // Load PA stats early for badge count
    }
  }, [isAuthenticated]);

  // PA email filter change
  useEffect(() => {
    if (isAuthenticated && activeTab === "pa_inbox") fetchPaEmails();
  }, [paEmailFilter]);

  // Login form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-4">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Ship className="h-8 w-8 text-blue-600" />
              <h1 className="text-xl font-bold text-gray-900">myEuropacific</h1>
            </div>
            <p className="text-sm text-gray-600">Outlook Add-in</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {requires2FA ? "Verification Code" : "Sign In"}
              </CardTitle>
              <CardDescription>
                {requires2FA 
                  ? `Enter the code sent to ${twoFactorEmail}`
                  : "Use your myEuropacific credentials"
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {requires2FA ? (
                // 2FA Verification Form
                <form onSubmit={handleVerify2FA} className="space-y-4">
                  <div>
                    <Label>Verification Code</Label>
                    <Input
                      type="text"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value)}
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                      required
                      autoFocus
                      data-testid="outlook-2fa-code"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loginLoading} data-testid="outlook-verify-2fa-btn">
                    {loginLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying...</>
                    ) : (
                      "Verify & Sign In"
                    )}
                  </Button>
                  <div className="flex justify-between text-sm">
                    <button 
                      type="button"
                      onClick={handleResend2FA}
                      className="text-blue-600 hover:underline"
                    >
                      Resend code
                    </button>
                    <button 
                      type="button"
                      onClick={() => {
                        setRequires2FA(false);
                        setTwoFactorCode("");
                      }}
                      className="text-gray-500 hover:underline"
                    >
                      Back to login
                    </button>
                  </div>
                </form>
              ) : (
                // Regular Login Form
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@europacific.com"
                      required
                      className="text-sm"
                      style={{ fontSize: '14px' }}
                      data-testid="outlook-login-email"
                    />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="outlook-login-password"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loginLoading} data-testid="outlook-login-btn">
                    {loginLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in...</>
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          <p className="text-xs text-center text-gray-500 mt-4">
            This add-in connects to your myEuropacific account to provide CRM, tracking, and document management features directly in Outlook.
          </p>
        </div>
      </div>
    );
  }

  // Show error if render fails
  if (renderError) {
    return (
      <div className="min-h-screen bg-red-50 p-4">
        <div className="bg-white p-4 rounded shadow">
          <h2 className="text-red-600 font-bold mb-2">Error Loading Add-in</h2>
          <p className="text-sm text-gray-600 mb-4">{renderError}</p>
          <Button onClick={() => {
            setRenderError(null);
            window.location.reload();
          }}>
            Reload
          </Button>
        </div>
      </div>
    );
  }

  // Main add-in interface
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-blue-600 text-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Ship className="h-5 w-5" />
            <span className="font-semibold">myEuropacific</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-blue-700"
            onClick={() => {
              localStorage.removeItem("outlook_token");
              setAuthToken(null);
              setIsAuthenticated(false);
            }}
          >
            Sign Out
          </Button>
        </div>
        
        {/* Current Email Context */}
        {currentEmail && (
          <div className="mt-2 text-xs bg-blue-700 rounded px-2 py-1">
            <div className="flex items-center gap-1 truncate">
              <Mail className="h-3 w-3" />
              <span className="font-medium">{currentEmail.sender_name || currentEmail.sender_email}</span>
            </div>
            <div className="truncate opacity-80">{currentEmail.subject}</div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Dashboard Stats Bar — wraps when narrow */}
        {dashboardStats && (
          <div className="flex flex-wrap gap-x-3 gap-y-1 px-3 py-2 bg-slate-50 text-[11px] border-b">
            <span className="whitespace-nowrap">📋 {dashboardStats.pending_tasks} tasks</span>
            <span className="whitespace-nowrap">📁 {dashboardStats.open_pfs} PFs</span>
            <span className="whitespace-nowrap">💼 {dashboardStats.pending_offers} offers</span>
            <span className="whitespace-nowrap">🚚 {dashboardStats.active_shipments} active</span>
          </div>
        )}
        
        {/* Category Tabs — responsive, never scrolls horizontally */}
        <div className="flex flex-wrap border-b bg-white w-full">
          {Object.entries(categories).map(([catId, cat]) => (
            <button
              key={catId}
              onClick={() => {
                setActiveCategory(catId);
                // Auto-select first module in this category
                const firstModule = cat.modules[0];
                setActiveTab(firstModule);
              }}
              className={`flex-grow basis-[calc(33.333%-0px)] min-w-[80px] py-2 px-1 text-[11px] sm:text-xs font-medium transition-colors border-b-2 relative truncate ${
                activeCategory === catId
                  ? "text-blue-600 border-blue-500 bg-blue-50"
                  : "text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50"
              }`}
              data-testid={`outlook-cat-${catId}`}
            >
              <span className="truncate">{cat.label}</span>
              {catId === "pa" && paStats && ((paStats.drafts_waiting || 0) + (paStats.escalated || 0)) > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center text-[9px] font-bold text-white bg-red-500 rounded-full leading-none" data-testid="pa-badge-count">
                  {(paStats.drafts_waiting || 0) + (paStats.escalated || 0)}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Module Pills — wrap-to-fit, never scrolls horizontally */}
        <div className="bg-gray-50 border-b px-1.5 py-1.5 w-full overflow-x-hidden">
          <TabsList className="flex flex-wrap gap-1 bg-transparent h-auto p-0 w-full">
            {categories[activeCategory]?.modules.map((modId) => (
              <TabsTrigger
                key={modId}
                value={modId}
                className={`flex-grow basis-[calc(33.333%-0.25rem)] min-w-[56px] max-w-full text-[10px] sm:text-[11px] px-1 py-1 rounded-md transition-colors relative truncate ${
                  activeTab === modId
                    ? "bg-white text-blue-600 shadow-sm border border-blue-200"
                    : "text-gray-600 hover:bg-white hover:shadow-sm border border-transparent"
                }`}
              >
                {moduleLabels[modId]}
                {modId === "pa_notif" && paNotifications.counts?.urgent > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 px-0.5 flex items-center justify-center text-[8px] font-bold text-white bg-red-500 rounded-full leading-none animate-pulse">
                    {paNotifications.counts.urgent}
                  </span>
                )}
                {modId === "pa_queue" && paStats && (paStats.drafts_waiting || 0) > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 px-0.5 flex items-center justify-center text-[8px] font-bold text-white bg-amber-500 rounded-full leading-none">
                    {paStats.drafts_waiting}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* PA Sidebar Card — shows when current email has a PA ticket */}
        {sidebarTicket && (
          <div className="mx-3 mt-2 mb-1" data-testid="pa-sidebar-card">
            <Card className={`border-l-4 ${
              sidebarTicket.approval?.status === "approved" ? "border-l-green-500 bg-green-50/50" :
              sidebarTicket.approval?.status === "rejected" ? "border-l-red-500 bg-red-50/50" :
              "border-l-amber-500 bg-amber-50/50"
            }`}>
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Ship className="h-3.5 w-3.5 text-slate-600" />
                    <span className="text-xs font-semibold text-slate-700">{sidebarTicket.ticket}</span>
                  </div>
                  <Badge className={`text-[10px] ${
                    sidebarTicket.category === "booking" ? "bg-emerald-100 text-emerald-700" :
                    sidebarTicket.category === "complaint" ? "bg-red-100 text-red-700" :
                    "bg-blue-100 text-blue-700"
                  }`}>
                    {(sidebarTicket.category || "email").replace("_", " ")}
                  </Badge>
                </div>

                {sidebarTicket.subject && (
                  <p className="text-xs text-slate-600 truncate">{sidebarTicket.subject}</p>
                )}

                {/* Booking Summary */}
                {sidebarTicket.booking_data?.parsed && (
                  <div className="text-[10px] text-slate-500 space-y-0.5 bg-white/60 rounded p-1.5">
                    {sidebarTicket.booking_data.parsed.route && <div>Route: <b>{sidebarTicket.booking_data.parsed.route}</b></div>}
                    {sidebarTicket.booking_data.parsed.customer && <div>Customer: <b>{sidebarTicket.booking_data.parsed.customer}</b></div>}
                    {sidebarTicket.booking_data.parsed.containers && <div>Containers: <b>{sidebarTicket.booking_data.parsed.containers}</b></div>}
                    {sidebarTicket.booking_data.parsed.eta && <div>ETA: <b>{sidebarTicket.booking_data.parsed.eta}</b></div>}
                  </div>
                )}

                {/* Already handled — conflict display */}
                {sidebarTicket.approval?.status === "approved" && (
                  <div className="flex items-center gap-1.5 text-xs text-green-700 bg-green-100 rounded px-2 py-1" data-testid="sidebar-approved-badge">
                    <CheckCircle className="h-3.5 w-3.5" />
                    <span>Approved by <b>{sidebarTicket.approval.approved_by}</b> via {sidebarTicket.approval.source || "system"}
                      {sidebarTicket.approval.approved_at && ` at ${sidebarTicket.approval.approved_at.slice(11, 16)}`}
                    </span>
                  </div>
                )}
                {sidebarTicket.approval?.status === "rejected" && (
                  <div className="flex items-center gap-1.5 text-xs text-red-700 bg-red-100 rounded px-2 py-1" data-testid="sidebar-rejected-badge">
                    <X className="h-3.5 w-3.5" />
                    <span>Rejected by <b>{sidebarTicket.approval.rejected_by}</b> via {sidebarTicket.approval.source || "system"}</span>
                  </div>
                )}

                {/* Action buttons — only if not yet approved/rejected */}
                {!sidebarTicket.approval?.status && (
                  <div className="flex gap-1.5 pt-1">
                    <Button size="sm" className="flex-1 h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                      onClick={handleSidebarApprove} disabled={sidebarActionLoading} data-testid="sidebar-approve-btn">
                      {sidebarActionLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                      Approve
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handleSidebarReject()} disabled={sidebarActionLoading} data-testid="sidebar-reject-btn">
                      {sidebarActionLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3 mr-1" />}
                      Reject
                    </Button>
                  </div>
                )}

                {/* Draft preview if available */}
                {sidebarTicket.draft && !sidebarTicket.approval?.status && (
                  <div className="text-[10px] text-slate-400 border-t pt-1 mt-1">
                    Draft reply: <span className="italic">{(sidebarTicket.draft.body || "").substring(0, 80)}...</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
        {sidebarLoading && (
          <div className="mx-3 mt-2 flex items-center gap-2 text-xs text-slate-400">
            <Loader2 className="h-3 w-3 animate-spin" /> Checking PA...
          </div>
        )}

        {/* Match Tab */}
        <TabsContent value="match" className="p-3 space-y-3">
          <div className="flex gap-2">
            <Button
              onClick={matchEmail}
              disabled={matchLoading}
              className="flex-1"
              data-testid="match-email-btn"
            >
              {matchLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <><RefreshCw className="h-4 w-4 mr-1" /> Match Email</>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (currentEmail) {
                  setNewContact({
                    name: currentEmail.sender_name || "",
                    email: currentEmail.sender_email,
                    company_name: "",
                    phone: "",
                    title: "",
                  });
                  setShowCreateContact(true);
                }
              }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {/* References Found */}
          {matchResults?.references_found && (
            <div className="text-xs">
              {matchResults.references_found.container_numbers?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1">
                  <span className="text-gray-500">Containers:</span>
                  {matchResults.references_found.container_numbers.map((c) => (
                    <Badge key={c} variant="secondary" className="text-xs">
                      {c}
                    </Badge>
                  ))}
                </div>
              )}
              {matchResults.references_found.bl_numbers?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1">
                  <span className="text-gray-500">B/L:</span>
                  {matchResults.references_found.bl_numbers.map((b) => (
                    <Badge key={b} variant="secondary" className="text-xs">
                      {b}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          <ScrollArea className="h-[calc(100vh-280px)]">
            {/* Matched Contacts */}
            {matchResults?.contacts?.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <Users className="h-4 w-4" /> Contacts
                </h3>
                {matchResults.contacts.map((contact, i) => (
                  <Card key={i} className="mb-2">
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm">{contact.name}</p>
                          <p className="text-xs text-gray-500">{contact.email}</p>
                          {contact.company_name && (
                            <p className="text-xs text-gray-500">{contact.company_name}</p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Badge variant="outline" className="text-xs">
                            {contact.match_type}
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => linkToRecord("contact", contact.id, contact.name)}
                          >
                            <Link2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Matched Shipments */}
            {matchResults?.shipments?.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <Package className="h-4 w-4" /> Shipments
                </h3>
                {matchResults.shipments.map((shipment, i) => (
                  <Card key={i} className="mb-2">
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm font-mono">
                            {shipment.container_number}
                          </p>
                          <p className="text-xs text-gray-500">
                            {shipment.status} - {shipment.terminal || shipment.location}
                          </p>
                          {shipment.vessel && (
                            <p className="text-xs text-gray-500">
                              <Anchor className="h-3 w-3 inline mr-1" />
                              {shipment.vessel}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Badge variant="outline" className="text-xs">
                            {shipment.match_type}
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => linkToRecord("shipment", shipment.id, shipment.container_number)}
                          >
                            <Link2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Matched Position Folders */}
            {matchResults?.position_folders?.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <FileBox className="h-4 w-4" /> Position Folders
                </h3>
                {matchResults.position_folders.map((pf, i) => (
                  <Card key={i} className="mb-2">
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm">{pf.reference || pf.bl_number}</p>
                          <p className="text-xs text-gray-500">
                            {pf.shipper} → {pf.consignee}
                          </p>
                          {pf.container_number && (
                            <p className="text-xs font-mono text-gray-500">
                              {pf.container_number}
                            </p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => linkToRecord("position_folder", pf.id, pf.reference)}
                        >
                          <Link2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Matched Companies */}
            {matchResults?.companies?.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <Building2 className="h-4 w-4" /> Companies
                </h3>
                {matchResults.companies.map((company, i) => (
                  <Card key={i} className="mb-2">
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm">{company.name}</p>
                          <p className="text-xs text-gray-500">{company.domain}</p>
                          {company.country && (
                            <p className="text-xs text-gray-500">{company.country}</p>
                          )}
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {company.match_type}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Matched Offers */}
            {matchResults?.offers?.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <FileText className="h-4 w-4" /> Recent Offers
                </h3>
                {matchResults.offers.map((offer, i) => (
                  <Card key={i} className="mb-2">
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm">{offer.reference || offer.id?.slice(0, 8)}</p>
                          <p className="text-xs text-gray-500">
                            {offer.origin} → {offer.destination}
                          </p>
                          <p className="text-xs text-gray-500">
                            {offer.status} - {new Date(offer.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => linkToRecord("offer", offer.id, offer.reference)}
                        >
                          <Link2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {matchResults && 
             !matchResults.contacts?.length && 
             !matchResults.shipments?.length && 
             !matchResults.position_folders?.length && 
             !matchResults.companies?.length && 
             !matchResults.offers?.length && (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No matches found</p>
                <p className="text-xs">Try creating a new contact or searching manually</p>
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Tracking Tab */}
        <TabsContent value="tracking" className="p-3 space-y-3">
          {/* Transport Mode Selection */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => { setTrackingTransport("fcl"); setTrackingType("container"); }}
              className={`flex-1 text-xs py-1.5 px-2 rounded flex items-center justify-center gap-1 ${trackingTransport === "fcl" ? "bg-white shadow-sm font-medium text-blue-600" : "text-gray-600"}`}
            >
              <Package className="h-3 w-3" /> FCL
            </button>
            <button
              onClick={() => { setTrackingTransport("lcl"); setTrackingType("bl"); }}
              className={`flex-1 text-xs py-1.5 px-2 rounded flex items-center justify-center gap-1 ${trackingTransport === "lcl" ? "bg-white shadow-sm font-medium text-green-600" : "text-gray-600"}`}
            >
              <Package className="h-3 w-3" /> LCL
            </button>
            <button
              onClick={() => { setTrackingTransport("rail"); setTrackingType("wagon"); setTrackingCarrier("METRANS"); }}
              className={`flex-1 text-xs py-1.5 px-2 rounded flex items-center justify-center gap-1 ${trackingTransport === "rail" ? "bg-white shadow-sm font-medium text-orange-600" : "text-gray-600"}`}
            >
              <Train className="h-3 w-3" /> Rail
            </button>
          </div>

          {/* Tracking Type Tabs - changes based on transport mode */}
          <div className="flex gap-1 bg-gray-50 p-1 rounded-lg">
            {trackingTransport === "rail" ? (
              <>
                <button
                  onClick={() => setTrackingType("wagon")}
                  className={`flex-1 text-xs py-1.5 px-2 rounded ${trackingType === "wagon" ? "bg-white shadow-sm font-medium" : "text-gray-600"}`}
                >
                  Wagon Number
                </button>
                <button
                  onClick={() => setTrackingType("container")}
                  className={`flex-1 text-xs py-1.5 px-2 rounded ${trackingType === "container" ? "bg-white shadow-sm font-medium" : "text-gray-600"}`}
                >
                  Container
                </button>
                <button
                  onClick={() => setTrackingType("train")}
                  className={`flex-1 text-xs py-1.5 px-2 rounded ${trackingType === "train" ? "bg-white shadow-sm font-medium" : "text-gray-600"}`}
                >
                  Train ID
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setTrackingType("container")}
                  className={`flex-1 text-xs py-1.5 px-2 rounded ${trackingType === "container" ? "bg-white shadow-sm font-medium" : "text-gray-600"}`}
                >
                  Container
                </button>
                <button
                  onClick={() => setTrackingType("bl")}
                  className={`flex-1 text-xs py-1.5 px-2 rounded ${trackingType === "bl" ? "bg-white shadow-sm font-medium" : "text-gray-600"}`}
                >
                  B/L Number
                </button>
                <button
                  onClick={() => setTrackingType("booking")}
                  className={`flex-1 text-xs py-1.5 px-2 rounded ${trackingType === "booking" ? "bg-white shadow-sm font-medium" : "text-gray-600"}`}
                >
                  Booking
                </button>
              </>
            )}
          </div>

          {/* Carrier Selection */}
          <div>
            <Label className="text-xs">{trackingTransport === "rail" ? "Rail Operator" : "Carrier"}</Label>
            <select
              className="w-full border rounded px-3 py-2 text-sm mt-1"
              value={trackingCarrier}
              onChange={(e) => setTrackingCarrier(e.target.value)}
            >
              {trackingTransport === "rail" ? (
                <>
                  <option value="METRANS">Metrans</option>
                  <option value="DBCARGO">DB Cargo</option>
                  <option value="PKPCARGO">PKP Cargo</option>
                  <option value="RAILCARGO">Rail Cargo Austria</option>
                  <option value="CNCARGO">CNCARGO (China Rail)</option>
                  <option value="auto">Other / Auto-detect</option>
                </>
              ) : (
                <>
                  <option value="auto">Auto-detect Carrier</option>
                  <optgroup label="Major Sea Carriers">
                    <option value="MAEU">Maersk</option>
                    <option value="MSCU">MSC</option>
                    <option value="CMDU">CMA CGM</option>
                    <option value="COSU">COSCO</option>
                    <option value="EGLV">Evergreen</option>
                    <option value="HLCU">Hapag-Lloyd</option>
                    <option value="ONEY">ONE</option>
                    <option value="YMLU">Yang Ming</option>
                    <option value="ZIMU">ZIM</option>
                  </optgroup>
                  <optgroup label="Other Sea Carriers">
                    <option value="OOLU">OOCL</option>
                    <option value="HDMU">HMM</option>
                    <option value="WHLC">Wan Hai</option>
                    <option value="SEAU">Sealand</option>
                  </optgroup>
                  {trackingTransport === "lcl" && (
                    <optgroup label="LCL Consolidators">
                      <option value="ECUW">ECU Worldwide</option>
                      <option value="VANGUARD">Vanguard Logistics</option>
                      <option value="CFLCL">CF LCL</option>
                    </optgroup>
                  )}
                </>
              )}
            </select>
          </div>

          {/* Search Input */}
          <div className="flex gap-2">
            <Input
              placeholder={
                trackingTransport === "rail" ? (
                  trackingType === "wagon" ? "e.g., 37 TEN 84 4668 002-5" :
                  trackingType === "train" ? "e.g., 40123" : "e.g., MRKU1234567"
                ) : (
                  trackingType === "container" ? "e.g., MRKU1234567" :
                  trackingType === "bl" ? "e.g., MAEU123456789" : "e.g., 123456789"
                )
              }
              value={trackingQuery}
              onChange={(e) => setTrackingQuery(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === "Enter" && trackShipment()}
              className="font-mono"
              data-testid="tracking-input"
            />
            <Button onClick={trackShipment} disabled={trackingLoading} className="bg-blue-600 hover:bg-blue-700">
              {trackingLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>

          {/* Quick access to detected references from email */}
          {matchResults?.references_found?.container_numbers?.length > 0 && (
            <div className="bg-blue-50 p-2 rounded border border-blue-200">
              <p className="text-xs text-blue-600 font-medium mb-1">Detected in email:</p>
              <div className="flex flex-wrap gap-1">
                {matchResults.references_found.container_numbers.map((c) => (
                  <Button
                    key={c}
                    variant="outline"
                    size="sm"
                    className="text-xs h-6 font-mono bg-white"
                    onClick={() => {
                      setTrackingQuery(c);
                      setTrackingType("container");
                      setTimeout(trackShipment, 100);
                    }}
                  >
                    <Package className="h-3 w-3 mr-1" /> {c}
                  </Button>
                ))}
                {matchResults.references_found.bl_numbers?.map((bl) => (
                  <Button
                    key={bl}
                    variant="outline"
                    size="sm"
                    className="text-xs h-6 font-mono bg-white"
                    onClick={() => {
                      setTrackingQuery(bl);
                      setTrackingType("bl");
                      setTimeout(trackShipment, 100);
                    }}
                  >
                    <FileText className="h-3 w-3 mr-1" /> {bl}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Recent Searches */}
          {trackingHistory.length > 0 && !trackingResult && (
            <div className="border-t pt-2">
              <p className="text-xs text-gray-500 mb-1">Recent searches:</p>
              <div className="flex flex-wrap gap-1">
                {trackingHistory.map((h, i) => (
                  <Button
                    key={i}
                    variant="ghost"
                    size="sm"
                    className="text-xs h-6"
                    onClick={() => {
                      setTrackingQuery(h.query);
                      setTrackingType(h.type);
                    }}
                  >
                    {h.query}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Tracking Results */}
          {trackingResult && (
            <ScrollArea className="h-[calc(100vh-380px)]">
              {/* Rail/LCL Tracking Result (new format with top-level status) */}
              {trackingResult.transport_mode && (
                <div className="space-y-3">
                  {/* Main Status Card */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        {trackingResult.transport_mode === "rail" ? (
                          <Train className="h-4 w-4 text-orange-600" />
                        ) : (
                          <Package className="h-4 w-4 text-blue-600" />
                        )}
                        {trackingResult.container_number || trackingResult.reference}
                      </CardTitle>
                      {(trackingResult.carrier || trackingResult.order_number) && (
                        <CardDescription className="text-xs">
                          {trackingResult.carrier} • {trackingResult.transport_mode?.toUpperCase()}
                          {trackingResult.order_number && ` • Order: ${trackingResult.order_number}`}
                        </CardDescription>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {/* Container State */}
                      {trackingResult.container_state && (
                        <div className="flex items-center justify-between bg-green-50 -mx-3 px-3 py-2 mb-2">
                          <span className="text-xs text-green-700 font-medium">Container State</span>
                          <span className="text-xs font-bold text-green-700">{trackingResult.container_state}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Status</span>
                        <Badge variant={trackingResult.found ? "default" : "secondary"}>
                          {trackingResult.status}
                        </Badge>
                      </div>
                      {trackingResult.location && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Location</span>
                          <span className="text-sm font-medium">{trackingResult.location}</span>
                        </div>
                      )}
                      {trackingResult.container_type && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Container Type</span>
                          <span className="text-sm font-medium">{trackingResult.container_type}</span>
                        </div>
                      )}
                      {trackingResult.eta && (
                        <div className="flex items-center justify-between bg-blue-50 -mx-3 px-3 py-1">
                          <span className="text-sm text-blue-700 font-medium">ETA</span>
                          <span className="text-sm font-bold text-blue-700">{trackingResult.eta}</span>
                        </div>
                      )}
                      
                      {/* Rail-specific details */}
                      {trackingResult.rail_details && (
                        <div className="mt-2 pt-2 border-t">
                          <p className="text-xs text-gray-500 mb-1">Rail Details</p>
                          {trackingResult.rail_details.wagon_number && (
                            <div className="text-xs">Wagon: {trackingResult.rail_details.wagon_number}</div>
                          )}
                          {trackingResult.rail_details.train_id && (
                            <div className="text-xs">Train: {trackingResult.rail_details.train_id}</div>
                          )}
                          <div className="text-xs text-gray-500">Operator: {trackingResult.rail_details.operator}</div>
                        </div>
                      )}
                      
                      {/* Notes/Warnings */}
                      {trackingResult.note && (
                        <div className="mt-2 p-2 bg-yellow-50 rounded text-xs text-yellow-800">
                          {trackingResult.note}
                        </div>
                      )}
                      {trackingResult.otp_required && (
                        <div className="mt-2 p-2 bg-orange-50 rounded text-xs text-orange-800">
                          OTP verification required. Check roket@europacific.com inbox for the OTP email.
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Transport Schedule */}
                  {trackingResult.transport_schedule && trackingResult.transport_schedule.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-xs flex items-center gap-2">
                          <MapPin className="h-3 w-3" /> Transport Schedule
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-50 border-b">
                              <tr>
                                <th className="px-2 py-1 text-left">From</th>
                                <th className="px-2 py-1 text-left">To</th>
                                <th className="px-2 py-1 text-left">Departure</th>
                                <th className="px-2 py-1 text-left">Arrival</th>
                                <th className="px-2 py-1 text-left">Train</th>
                                <th className="px-2 py-1 text-left">State</th>
                              </tr>
                            </thead>
                            <tbody>
                              {trackingResult.transport_schedule.map((leg, idx) => (
                                <tr key={idx} className="border-b last:border-0">
                                  <td className="px-2 py-1.5 font-medium">{leg.from}</td>
                                  <td className="px-2 py-1.5 font-medium">{leg.to}</td>
                                  <td className="px-2 py-1.5">{leg.departure}</td>
                                  <td className="px-2 py-1.5">{leg.arrival}</td>
                                  <td className="px-2 py-1.5 text-gray-500 font-mono text-[10px]">{leg.train}</td>
                                  <td className="px-2 py-1.5">
                                    <Badge variant={leg.state === 'Arrived' ? 'default' : 'secondary'} className="text-[10px]">
                                      {leg.state}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Container History */}
                  {trackingResult.container_history && trackingResult.container_history.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-xs flex items-center gap-2">
                          <Clock className="h-3 w-3" /> Container History
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-0">
                        <div className="divide-y">
                          {trackingResult.container_history.map((event, idx) => (
                            <div key={idx} className="px-3 py-2 hover:bg-gray-50">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium">{event.date}</span>
                                <Badge variant={event.loaded === 'Empty' ? 'secondary' : 'default'} className="text-[10px]">
                                  {event.loaded}
                                </Badge>
                              </div>
                              <p className="text-xs text-gray-600 mt-0.5">{event.state}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Events (for LCL or fallback) */}
                  {(!trackingResult.container_history || trackingResult.container_history.length === 0) && 
                   trackingResult.events && trackingResult.events.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-xs flex items-center gap-2">
                          <Clock className="h-3 w-3" /> Tracking Events
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-0">
                        <div className="divide-y">
                          {trackingResult.events.map((event, idx) => (
                            <div key={idx} className="px-3 py-2 hover:bg-gray-50">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium">{event.date}</span>
                                {event.location && (
                                  <span className="text-xs text-gray-500">{event.location}</span>
                                )}
                              </div>
                              <p className="text-xs text-gray-600 mt-0.5">{event.status || event.description}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
              
              {/* Current Status (original format for sea tracking) */}
              {trackingResult.current_status && (
                <Card className="mb-3">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Package className="h-4 w-4 text-blue-600" />
                      {trackingResult.container_number || trackingResult.reference}
                    </CardTitle>
                    {trackingResult.carrier && (
                      <CardDescription className="text-xs">{trackingResult.carrier}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Status</span>
                      <Badge variant={
                        trackingResult.current_status.status?.includes("DELIVERED") ? "default" :
                        trackingResult.current_status.status?.includes("PORT") ? "secondary" : "outline"
                      }>
                        {trackingResult.current_status.status}
                      </Badge>
                    </div>
                    {trackingResult.current_status.location && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Location</span>
                        <span className="text-sm font-medium">{trackingResult.current_status.location}</span>
                      </div>
                    )}
                    {trackingResult.current_status.vessel && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Vessel</span>
                        <span className="text-sm">{trackingResult.current_status.vessel}</span>
                      </div>
                    )}
                    {trackingResult.current_status.voyage && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Voyage</span>
                        <span className="text-sm">{trackingResult.current_status.voyage}</span>
                      </div>
                    )}
                    {(trackingResult.current_status.eta || trackingResult.eta) && (
                      <div className="flex items-center justify-between bg-blue-50 -mx-3 px-3 py-1">
                        <span className="text-sm text-blue-700 font-medium">ETA</span>
                        <span className="text-sm font-bold text-blue-700">
                          {trackingResult.current_status.eta || trackingResult.eta}
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Route Info */}
              {(trackingResult.origin || trackingResult.destination) && (
                <Card className="mb-3">
                  <CardContent className="py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 text-center">
                        <p className="text-xs text-gray-500">Origin</p>
                        <p className="font-medium text-sm">{trackingResult.origin || "—"}</p>
                      </div>
                      <Ship className="h-5 w-5 text-gray-400" />
                      <div className="flex-1 text-center">
                        <p className="text-xs text-gray-500">Destination</p>
                        <p className="font-medium text-sm">{trackingResult.destination || "—"}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Milestones/Events */}
              {trackingResult.milestones?.length > 0 && (
                <Card className="mb-3">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Tracking Events</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {trackingResult.milestones.map((m, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm border-l-2 border-gray-200 pl-3 py-1">
                          <div className={`h-2 w-2 rounded-full mt-1.5 -ml-4 ${
                            m.completed || i === 0 ? "bg-green-500" : "bg-gray-300"
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-xs">{m.title || m.type || m.event}</p>
                            <p className="text-xs text-gray-500">{m.location}</p>
                            <p className="text-xs text-gray-400">{m.date || m.timestamp}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Related Position Folders */}
              {trackingResult.position_folders?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Related Folders</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {trackingResult.position_folders.map((pf, i) => (
                      <div key={i} className="py-2 border-b last:border-0">
                        <p className="font-medium text-sm">{pf.reference || pf.bl_number}</p>
                        <p className="text-xs text-gray-500">{pf.shipper} → {pf.consignee}</p>
                        <div className="flex gap-2 mt-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-6"
                            onClick={() => linkToRecord("position_folder", pf.id, pf.reference)}
                          >
                            <Link2 className="h-3 w-3 mr-1" /> Link Email
                          </Button>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* No Results */}
              {!trackingResult.found && !trackingResult.current_status && (
                <div className="text-center py-8 text-gray-500">
                  <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No tracking data found</p>
                  <p className="text-xs">Check the reference number and carrier</p>
                </div>
              )}
            </ScrollArea>
          )}

          {/* Empty State */}
          {!trackingResult && !trackingLoading && (
            <div className="text-center py-8 text-gray-400">
              <Ship className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Enter a tracking reference above</p>
              <p className="text-xs">Container number, B/L, or booking number</p>
            </div>
          )}
        </TabsContent>

        {/* CRM Tab */}
        <TabsContent value="crm" className="p-3 space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder="Search contacts, companies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && searchAll()}
              data-testid="crm-search-input"
            />
            <Button onClick={searchAll} disabled={searchLoading}>
              {searchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => setShowCreateContact(true)}
            >
              <Plus className="h-4 w-4 mr-1" /> Contact
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => setShowCreateActivity(true)}
            >
              <Clock className="h-4 w-4 mr-1" /> Activity
            </Button>
          </div>

          <ScrollArea className="h-[calc(100vh-260px)]">
            {searchResults && (
              <>
                {searchResults.contacts?.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold mb-2">Contacts</h3>
                    {searchResults.contacts.map((c, i) => (
                      <Card key={i} className="mb-2">
                        <CardContent className="p-3">
                          <p className="font-medium text-sm">{c.name}</p>
                          <p className="text-xs text-gray-500">{c.email}</p>
                          {c.phone && <p className="text-xs text-gray-500">{c.phone}</p>}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {searchResults.companies?.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold mb-2">Companies</h3>
                    {searchResults.companies.map((c, i) => (
                      <Card key={i} className="mb-2">
                        <CardContent className="p-3">
                          <p className="font-medium text-sm">{c.name}</p>
                          <p className="text-xs text-gray-500">{c.domain}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {searchResults.position_folders?.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold mb-2">Position Folders</h3>
                    {searchResults.position_folders.map((pf, i) => (
                      <Card key={i} className="mb-2">
                        <CardContent className="p-3">
                          <p className="font-medium text-sm">{pf.reference || pf.bl_number}</p>
                          <p className="text-xs text-gray-500">{pf.shipper} → {pf.consignee}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Docs Tab */}
        <TabsContent value="docs" className="p-3 space-y-3">
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Save this email to repository:</p>
            <div className="grid grid-cols-2 gap-2">
              {["Important", "Booking", "Claims", "Other"].map((cat) => (
                <Button 
                  key={cat}
                  variant="outline" 
                  size="sm"
                  onClick={() => saveToRepository(cat.toLowerCase())}
                  className="text-xs"
                >
                  <Save className="h-3 w-3 mr-1" /> {cat}
                </Button>
              ))}
            </div>
          </div>
          <Separator />
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-700">Attach customs &amp; company docs to reply</p>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs"
                onClick={loadAttachableDocs} disabled={docLoading} data-testid="docs-refresh-btn">
                {docLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Refresh"}
              </Button>
            </div>
            <p className="text-[11px] text-gray-500">
              Pick documents, choose <strong>PDF</strong> (attach file) or <strong>Link</strong> (in email body) for each, then <strong>Add to reply</strong> — one editable reply opens with everything, ready to send.
            </p>
            <Input
              placeholder="Search documents..."
              value={docSearch}
              onChange={(e) => setDocSearch(e.target.value)}
              className="h-8 text-xs"
              data-testid="docs-search-input"
            />
            {docLoading && docItems.length === 0 ? (
              <div className="text-center py-4 text-gray-400 text-xs">
                <Loader2 className="h-5 w-5 mx-auto mb-2 animate-spin" /> Loading documents…
              </div>
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {docItems
                  .filter((d) => {
                    const q = docSearch.trim().toLowerCase();
                    return !q || (d.name || "").toLowerCase().includes(q) || (d.kind || "").toLowerCase().includes(q);
                  })
                  .map((doc) => {
                    const mode = docSel[doc.id]; // undefined | "attach" | "link"
                    const selected = !!mode;
                    return (
                      <div key={doc.id}
                        className={`border rounded-md p-2 ${selected ? "bg-blue-50 border-blue-300" : "bg-white"}`}
                        data-testid={`doc-row-${doc.id}`}>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => setDocMode(doc.id, mode || "attach")}
                            className="h-4 w-4 shrink-0 accent-blue-600"
                            data-testid={`doc-select-${doc.id}`}
                          />
                          <FileText className="h-4 w-4 text-gray-400 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-gray-800 truncate" title={doc.name}>{doc.name}</p>
                            <p className="text-[10px] text-gray-400 truncate">{doc.kind}</p>
                          </div>
                          <div className="flex shrink-0 rounded-md border border-gray-200 overflow-hidden">
                            <button
                              type="button"
                              onClick={() => setDocMode(doc.id, "attach")}
                              className={`px-2 py-1 text-[11px] flex items-center gap-1 ${mode === "attach" ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                              data-testid={`doc-mode-pdf-${doc.id}`}>
                              <Paperclip className="h-3 w-3" /> PDF
                            </button>
                            <button
                              type="button"
                              onClick={() => setDocMode(doc.id, "link")}
                              className={`px-2 py-1 text-[11px] flex items-center gap-1 border-l border-gray-200 ${mode === "link" ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                              data-testid={`doc-mode-link-${doc.id}`}>
                              <LinkIcon className="h-3 w-3" /> Link
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                {!docLoading && docItems.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-3">No documents available.</p>
                )}
              </div>
            )}
            {Object.keys(docSel).length > 0 && (
              <div className="sticky bottom-0 bg-white pt-2 border-t flex items-center gap-2">
                <Button
                  size="sm"
                  className="h-9 flex-1 text-xs bg-blue-600 hover:bg-blue-700"
                  onClick={addSelectedToReply}
                  data-testid="docs-add-to-reply-btn">
                  <Paperclip className="h-3.5 w-3.5 mr-1" />
                  Add {Object.keys(docSel).length} to reply
                </Button>
                <Button
                  size="sm" variant="outline" className="h-9 text-xs"
                  onClick={() => setDocSel({})}
                  data-testid="docs-clear-selection-btn">
                  Clear
                </Button>
              </div>
            )}
            {docDiag && (
              <p className="text-[10px] text-gray-400 break-words" data-testid="docs-attach-diag">{docDiag}</p>
            )}
          </div>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="p-3 space-y-3">
          <div className="flex gap-2">
            <Button onClick={fetchTasks} variant="outline" size="sm" disabled={tasksLoading}>
              {tasksLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
            <Button onClick={() => setShowCreateTask(true)} size="sm" className="flex-1">
              <Plus className="h-4 w-4 mr-1" /> New Task
            </Button>
          </div>
          
          <ScrollArea className="h-[calc(100vh-280px)]">
            {tasks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ListTodo className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No pending tasks</p>
              </div>
            ) : (
              <div className="space-y-2">
                {tasks.map((task) => (
                  <Card key={task.id} className="cursor-pointer hover:bg-slate-50">
                    <CardContent className="p-3">
                      <div className="flex items-start gap-2">
                        <button onClick={() => completeTask(task.id)} className="mt-0.5">
                          {task.status === "completed" ? (
                            <CheckSquare className="h-4 w-4 text-green-500" />
                          ) : (
                            <Square className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium text-sm ${task.status === "completed" ? "line-through text-gray-400" : ""}`}>
                            {task.title}
                          </p>
                          {task.description && (
                            <p className="text-xs text-gray-500 truncate">{task.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            {task.due_date && (
                              <span className="text-xs text-gray-400">
                                <Calendar className="h-3 w-3 inline mr-1" />
                                {new Date(task.due_date).toLocaleDateString()}
                              </span>
                            )}
                            {task.priority && task.priority !== "normal" && (
                              <Badge variant={task.priority === "urgent" ? "destructive" : "secondary"} className="text-xs">
                                {task.priority}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Position Folders Tab */}
        <TabsContent value="pf" className="p-3 space-y-3">
          {/* Auto-Detected References Banner */}
          {detectedReferences && (detectedReferences.pf_numbers.length > 0 || detectedReferences.container_numbers.length > 0 || detectedReferences.bl_numbers.length > 0) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
              <p className="text-xs font-medium text-blue-800 mb-1.5 flex items-center gap-1">
                <Sparkles className="h-3 w-3" />
                Detected from email:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {detectedReferences.pf_numbers.map((ref, idx) => (
                  <button
                    key={`pf-${idx}`}
                    onClick={() => { setSearchQuery(ref); autoSearchPf(ref); }}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700"
                  >
                    <FileBox className="h-3 w-3" />
                    {ref}
                  </button>
                ))}
                {detectedReferences.container_numbers.map((ref, idx) => (
                  <button
                    key={`cnt-${idx}`}
                    onClick={() => { setSearchQuery(ref); autoSearchByReference(ref, 'container'); }}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-green-600 text-white text-xs rounded-md hover:bg-green-700"
                  >
                    <Package className="h-3 w-3" />
                    {ref}
                  </button>
                ))}
                {detectedReferences.bl_numbers.map((ref, idx) => (
                  <button
                    key={`bl-${idx}`}
                    onClick={() => { setSearchQuery(ref); autoSearchByReference(ref, 'bl'); }}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-amber-600 text-white text-xs rounded-md hover:bg-amber-700"
                  >
                    <FileText className="h-3 w-3" />
                    {ref}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Auto-Matched PF Highlight */}
          {autoMatchedPf && (
            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm font-semibold text-green-800">Auto-Matched: {autoMatchedPf.pf_number}</p>
                    <p className="text-xs text-green-600">{autoMatchedPf.shipper} → {autoMatchedPf.consignee}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="text-green-700 border-green-300" onClick={() => linkEmailToPf(autoMatchedPf.id || autoMatchedPf.pf_number)}>
                    <Link2 className="h-4 w-4 mr-1" /> Link
                  </Button>
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => {setSelectedPf(autoMatchedPf); checkNotificationEmails(autoMatchedPf); setShowAddMilestone(true);}}>
                    <Plus className="h-4 w-4 mr-1" /> Milestone
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex gap-2">
            <Input
              placeholder="Search PF, B/L, container..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchPositionFolders(); }}
              className="flex-1 text-sm"
              data-testid="pf-search-input"
            />
            <Button onClick={fetchPositionFolders} size="sm" disabled={pfsLoading} data-testid="pf-search-btn">
              {pfsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
            <Button onClick={() => setShowCreatePf(true)} size="sm" variant="default" className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          
          <ScrollArea className="h-[calc(100vh-260px)]">
            {positionFolders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FolderOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No position folders found</p>
                <p className="text-xs">Search or match email first</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                  onClick={() => setShowCreatePf(true)}
                >
                  <Plus className="h-4 w-4 mr-1" /> Create New PF
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {positionFolders.map((pf) => (
                  <Card key={pf.id || pf.pf_number} className="cursor-pointer hover:bg-slate-50">
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0" onClick={() => setSelectedPf(pf)}>
                          <p className="font-medium text-sm">{pf.pf_number || pf.reference}</p>
                          <p className="text-xs text-gray-500">{pf.shipper} → {pf.consignee}</p>
                          {pf.containers && pf.containers.length > 0 && (
                            <p className="text-xs font-mono text-blue-600">{pf.containers.join(", ")}</p>
                          )}
                          {pf.container_number && !pf.containers && (
                            <p className="text-xs font-mono text-blue-600">{pf.container_number}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">{pf.status || "Open"}</Badge>
                            {pf.bl_number && <span className="text-xs text-gray-400">B/L: {pf.bl_number}</span>}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button size="sm" variant="ghost" onClick={() => linkEmailToPf(pf.id || pf.pf_number)} title="Link Email">
                            <Link2 className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => {setSelectedPf(pf); checkNotificationEmails(pf); setShowAddMilestone(true);}} title="Add Milestone">
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {/* Quick Actions — 3-column grid so the 5 buttons
                          wrap onto a 2nd row inside the narrow Outlook
                          side panel instead of squashing / overflowing. */}
                      <div className="grid grid-cols-3 gap-1 mt-2 pt-2 border-t" data-testid="outlook-pf-quick-actions">
                        <Button size="sm" variant="outline" className="text-xs" onClick={() => {setSelectedPf(pf); checkNotificationEmails(pf); setShowAddMilestone(true);}}>
                          <Plus className="h-3 w-3 mr-1" /> Milestone
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs"
                                onClick={() => {setSelectedPf(pf); setNewComment({ text: "", container_number: "", notify_customer: true, audience: "customer" }); setShowAddComment(true);}}
                                data-testid={`outlook-add-comment-${pf.pf_number}`}>
                          <MessageSquare className="h-3 w-3 mr-1" /> Comment
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-xs"
                          onClick={() => togglePfMilestones(pf.id, pf.pf_number)}
                        >
                          {expandedPfMilestones[pf.id || pf.pf_number] ? (
                            <><ChevronUp className="h-3 w-3 mr-1" /> Hide</>
                          ) : (
                            <><ChevronDown className="h-3 w-3 mr-1" /> View</>
                          )}
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs" onClick={() => {setSelectedPf(pf); setShowPfDocUpload(true);}}>
                          <Upload className="h-3 w-3 mr-1" /> Doc
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs border-teal-300 text-teal-700 hover:bg-teal-50"
                          title="Toggle DVH Pending/Done · save DVH number · or upload DVH.pdf"
                          data-testid="outlook-pf-dvh-btn"
                          onClick={() => {
                            setSelectedPf(pf);
                            setDvhNumberInput(pf.dvh_number || "");
                            setDvhDoneToggle(pf.dvh_done === true);
                            setShowDvhDialog(true);
                          }}
                        >
                          <FileText className="h-3 w-3 mr-1" /> DVH
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                          title="Toggle DIZ Pending/Done · save DIZ number · or upload DIZ.pdf"
                          data-testid="outlook-pf-diz-btn"
                          onClick={() => {
                            setSelectedPf(pf);
                            setDizNumberInput(pf.diz_number || "");
                            setDizDoneToggle(pf.diz_done === true);
                            setShowDizDialog(true);
                          }}
                        >
                          <FileText className="h-3 w-3 mr-1" /> DIZ
                        </Button>
                      </div>
                      
                      {/* Milestones Collapsible Section */}
                      {expandedPfMilestones[pf.id || pf.pf_number] && (
                        <div className="mt-2 pt-2 border-t bg-gray-50 -mx-3 -mb-3 px-3 pb-3 rounded-b">
                          <p className="text-xs font-medium text-gray-600 mb-2">Milestones</p>
                          {pfMilestonesLoading[pf.id || pf.pf_number] ? (
                            <div className="text-center py-2">
                              <Loader2 className="h-4 w-4 animate-spin mx-auto text-gray-400" />
                            </div>
                          ) : (pfMilestonesData[pf.id || pf.pf_number] || []).length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-2">No milestones yet</p>
                          ) : (
                            <div className="space-y-1.5 max-h-48 overflow-y-auto">
                              {(pfMilestonesData[pf.id || pf.pf_number] || []).map((milestone, idx) => (
                                <div key={idx} className="bg-white p-2 rounded border text-xs">
                                  <div className="flex items-start gap-2">
                                    <CheckCircle className={`h-4 w-4 flex-shrink-0 mt-0.5 ${milestone.completed ? 'text-green-500' : 'text-gray-300'}`} />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between gap-2">
                                        <span className="font-semibold text-gray-900">
                                          {milestone.label || getMilestoneLabel(milestone.milestone_type || milestone.event_type)}
                                        </span>
                                        {milestone.source === 'outlook_plugin' && (
                                          <span className="text-[10px] bg-blue-100 text-blue-600 px-1 rounded">Plugin</span>
                                        )}
                                      </div>
                                      {milestone.description && milestone.description !== milestone.label && (
                                        <p className="text-gray-600 mt-0.5">{milestone.description}</p>
                                      )}
                                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-gray-500">
                                        {(milestone.actual_date || milestone.milestone_date) && (
                                          <span className="flex items-center gap-1">
                                            <Calendar className="h-3 w-3" />
                                            {new Date(milestone.actual_date || milestone.milestone_date).toLocaleString('en-GB', {
                                              day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                            })}
                                          </span>
                                        )}
                                        {milestone.container_number && (
                                          <span className="flex items-center gap-1">
                                            <Package className="h-3 w-3" />
                                            {milestone.container_number}
                                          </span>
                                        )}
                                      </div>
                                      {milestone.notes && (
                                        <p className="text-gray-400 mt-1 italic">"{milestone.notes}"</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Offers Tab */}
        <TabsContent value="offers" className="p-3 space-y-3">
          <div className="flex gap-2">
            <Button onClick={fetchOffers} variant="outline" size="sm" disabled={offersLoading} className="flex-1">
              {offersLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-1" /> Refresh</>}
            </Button>
          </div>
          
          <ScrollArea className="h-[calc(100vh-260px)]">
            {offers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Receipt className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No offers found for this customer</p>
              </div>
            ) : (
              <div className="space-y-2">
                {offers.map((offer) => (
                  <Card key={offer.id}>
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm">{offer.reference || offer.id?.slice(0, 8)}</p>
                          <p className="text-xs text-gray-500">{offer.origin} → {offer.destination}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant={offer.status === "accepted" ? "default" : "outline"} className="text-xs">
                              {offer.status}
                            </Badge>
                            {offer.total_amount && (
                              <span className="text-xs font-medium">€{offer.total_amount}</span>
                            )}
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => linkToRecord("offer", offer.id, offer.reference)}>
                          <Link2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* ── LogiGrowth Handoff Brief Tab — pick talking points → AI reply ── */}
        <TabsContent value="handoff" className="p-3 space-y-3 min-w-0 max-w-full overflow-x-hidden" data-testid="handoff-tab">
          {!handoffData?.isHandoff ? (
            <div className="rounded-lg border bg-white p-4 text-center" data-testid="handoff-no-brief">
              <Flame className="h-6 w-6 text-orange-400 mx-auto mb-2" />
              <p className="text-xs text-slate-600 font-medium">No Handoff Brief detected</p>
              <p className="text-[11px] text-slate-400 mt-1">
                Open a 🔥 <strong>HANDOFF BRIEF</strong> email from LogiGrowth and this tab will let you
                pick talking points and AI-draft a reply.
              </p>
            </div>
          ) : (
            <>
              {/* Context header */}
              <div className="rounded-lg border bg-orange-50 border-orange-200 p-2.5" data-testid="handoff-context">
                <div className="flex items-center gap-2">
                  <Flame className="h-4 w-4 text-orange-500 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-orange-900 truncate" data-testid="handoff-contact">
                      {handoffData.contactName || "Contact"}{handoffData.company ? ` @ ${handoffData.company}` : ""}
                    </div>
                    {handoffData.replyEmail && (
                      <div className="text-[10px] text-orange-700 truncate">Reply goes to {handoffData.replyEmail}</div>
                    )}
                  </div>
                </div>
                {handoffData.goal && (
                  <p className="text-[11px] text-orange-800 mt-1.5 leading-snug">
                    <span className="font-medium">Goal:</span> {handoffData.goal}
                  </p>
                )}
              </div>

              {/* Talking points */}
              <div className="rounded-lg border bg-white p-2.5">
                <div className="text-[11px] font-semibold text-slate-700 mb-1.5">
                  Talking points <span className="text-slate-400 font-normal">(pick one or more)</span>
                </div>
                {handoffData.talkingPoints.length === 0 ? (
                  <p className="text-[11px] text-slate-400">No talking points found in this brief.</p>
                ) : (
                  <div className="space-y-1.5">
                    {handoffData.talkingPoints.map((pt, i) => {
                      const checked = hbSelectedPoints.includes(i);
                      return (
                        <label
                          key={i}
                          className={`flex items-start gap-2 p-1.5 rounded border cursor-pointer transition-colors ${checked ? "bg-blue-50 border-blue-300" : "bg-white border-slate-200 hover:bg-slate-50"}`}
                          data-testid={`handoff-point-${i}`}
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={checked}
                            onChange={() =>
                              setHbSelectedPoints((prev) =>
                                prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]
                              )
                            }
                            data-testid={`handoff-point-checkbox-${i}`}
                          />
                          <span className="text-[11px] text-slate-700 leading-snug">{pt}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Options */}
              <div className="rounded-lg border bg-white p-2.5 space-y-2">
                <div>
                  <label className="text-[11px] font-medium text-slate-600">Custom angle (optional)</label>
                  <Input
                    value={hbCustomAngle}
                    onChange={(e) => setHbCustomAngle(e.target.value)}
                    placeholder="e.g. mention our weekly Koper–Hamburg LCL service"
                    className="text-xs h-8 mt-1"
                    data-testid="handoff-custom-angle"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-slate-600">Reply language</label>
                  <select
                    value={hbLanguage}
                    onChange={(e) => setHbLanguage(e.target.value)}
                    className="w-full text-xs border rounded px-2 py-1.5 mt-1"
                    data-testid="handoff-language"
                  >
                    <option>English</option>
                    <option>Slovenian</option>
                    <option>Italian</option>
                    <option>German</option>
                    <option>Croatian</option>
                  </select>
                </div>
              </div>

              <Button
                onClick={generateHandoffReply}
                disabled={hbBusy}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="handoff-generate-btn"
              >
                {hbBusy ? (
                  <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Drafting…</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-1.5" /> {hbReply ? "Regenerate reply" : "Generate AI reply"}</>
                )}
              </Button>

              {/* Editable reply preview */}
              {hbReply && (
                <div className="rounded-lg border bg-white p-2.5 space-y-2" data-testid="handoff-reply-preview">
                  <div className="text-[11px] font-semibold text-slate-700">Draft reply (editable)</div>
                  <Textarea
                    value={hbReply}
                    onChange={(e) => setHbReply(e.target.value)}
                    className="text-xs min-h-[160px]"
                    data-testid="handoff-reply-text"
                  />
                  <p className="text-[10px] text-slate-400">
                    The original "{handoffData.contactName || "contact"}" message will be quoted below your reply automatically.
                  </p>
                  <Button
                    onClick={insertHandoffReply}
                    disabled={tplBusy}
                    className="w-full"
                    data-testid="handoff-insert-btn"
                  >
                    <Send className="h-4 w-4 mr-1.5" /> Insert into Outlook reply
                  </Button>
                  <Button
                    onClick={logHandoffToCrm}
                    disabled={hbLogging || hbLogged}
                    variant="outline"
                    className="w-full"
                    data-testid="handoff-log-crm-btn"
                  >
                    {hbLogging ? (
                      <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Logging…</>
                    ) : hbLogged ? (
                      <><CheckCircle className="h-4 w-4 mr-1.5 text-green-600" /> Logged to CRM</>
                    ) : (
                      <><ClipboardCheck className="h-4 w-4 mr-1.5" /> Log to CRM + follow-up task</>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* ── Email Templates Tab — pre-fill Outlook reply ─────────────── */}
        <TabsContent value="templates" className="p-3 space-y-3 min-w-0 max-w-full overflow-x-hidden">
          {/* PF Picker — auto-selected from match results, operator can change */}
          <div className="rounded-lg border bg-white p-2">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-[11px] font-medium text-slate-700">Data source</span>
              {tplPfPicked && (
                <button
                  onClick={() => setTplPfPicked(null)}
                  className="text-[10px] text-slate-500 hover:text-red-600"
                  data-testid="tpl-clear-pf"
                >
                  clear
                </button>
              )}
            </div>
            {tplPfPicked ? (
              <div className="text-xs">
                <div className="font-semibold text-blue-700" data-testid="tpl-picked-pf-number">
                  {tplPfPicked.pf_number}
                </div>
                <div className="text-slate-500 text-[11px] truncate">
                  {tplPfPicked.customer || tplPfPicked.crm_company_name || "—"}
                </div>
              </div>
            ) : matchResults?.position_folders?.length ? (
              <select
                value=""
                onChange={(e) => {
                  const pf = matchResults.position_folders.find(p => p.id === e.target.value);
                  if (pf) setTplPfPicked(pf);
                }}
                className="w-full text-xs border rounded px-2 py-1"
                data-testid="tpl-pf-select"
              >
                <option value="">Pick a matched PF…</option>
                {matchResults.position_folders.map((pf) => (
                  <option key={pf.id} value={pf.id}>
                    {pf.pf_number} — {pf.customer || pf.crm_company_name || "—"}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-[11px] text-slate-500">
                No PF auto-matched. Templates will render with placeholders like <code>[PF NUMBER]</code> that you can fill in manually.
              </p>
            )}
          </div>

          {/* Template browser */}
          <div className="rounded-lg border bg-white">
            <div className="p-2 border-b bg-slate-50">
              <Input
                placeholder="🔍 Search templates…"
                value={tplSearch}
                onChange={(e) => setTplSearch(e.target.value)}
                className="text-xs h-8"
                data-testid="tpl-search"
              />
            </div>
            <div
              className="p-1"
              style={{
                // Fixed-pixel height so Outlook's web-view always renders an
                // intrinsic scrollbar (vh-based heights collapse in some
                // embedded Office.js hosts and you end up with no thumb).
                height: 360,
                maxHeight: 360,
                overflowY: "scroll",
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "thin",   // Firefox / Edge Chromium
                scrollbarColor: "#94a3b8 transparent",
              }}
              onWheel={(e) => {
                // Force the wheel event to bubble inside this container even
                // when Outlook tries to capture it for the host pane.
                e.currentTarget.scrollTop += e.deltaY;
                e.stopPropagation();
              }}
              data-testid="tpl-list-scroll"
            >
              {TEMPLATE_CATEGORIES.map((cat) => {
                const inCat = EMAIL_TEMPLATES.filter(
                  (t) => t.category === cat.id &&
                    (!tplSearch ||
                      t.name.toLowerCase().includes(tplSearch.toLowerCase()) ||
                      (t.subject || "").toLowerCase().includes(tplSearch.toLowerCase()))
                );
                if (inCat.length === 0) return null;
                return (
                  <div key={cat.id} className="mb-1.5">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 px-2 py-1 bg-slate-50 rounded sticky top-0 z-10">
                      {cat.name}
                    </div>
                    {inCat.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => selectTemplate(t.id)}
                        className={`w-full text-left text-xs px-2 py-1.5 rounded hover:bg-blue-50 transition-colors ${
                          tplSelectedId === t.id ? "bg-blue-100 text-blue-900 font-medium" : "text-slate-700"
                        }`}
                        data-testid={`tpl-pick-${t.id}`}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                );
              })}
              {/* Extra spacer so the last template clears the bottom edge */}
              <div style={{ height: 24 }} />
            </div>
            <p className="px-2 py-1 text-[10px] text-slate-400 border-t bg-slate-50 leading-tight">
              {EMAIL_TEMPLATES.length} templates · scroll inside the list to see them all
            </p>
          </div>

          {/* Preview / edit */}
          {tplSelectedId && (
            <div className="rounded-lg border bg-white p-2 space-y-2">
              <div>
                <Label className="text-[10px]">Subject</Label>
                <Input
                  value={tplSubject}
                  onChange={(e) => setTplSubject(e.target.value)}
                  className="text-xs h-8"
                  data-testid="tpl-subject"
                />
              </div>
              <div>
                <Label className="text-[10px]">Body</Label>
                <Textarea
                  value={tplBody}
                  onChange={(e) => setTplBody(e.target.value)}
                  rows={8}
                  className="text-xs font-mono"
                  data-testid="tpl-body"
                />
              </div>

              {/* Actions: responsive grid — auto-fits column width */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                <Button
                  onClick={openOutlookReplyWithTemplate}
                  disabled={tplBusy}
                  className="bg-blue-600 hover:bg-blue-700 text-xs h-8"
                  data-testid="tpl-open-reply"
                  title="Opens a fresh Outlook Reply-All compose window pre-filled with this template"
                >
                  📧 Open Reply
                </Button>
                <Button
                  onClick={insertTemplateIntoDraft}
                  disabled={tplBusy}
                  variant="outline"
                  className="text-xs h-8"
                  data-testid="tpl-insert-draft"
                  title="Insert at cursor — works only when you already have a Reply open"
                >
                  ⬇ Insert in draft
                </Button>
                <Button
                  onClick={copyTemplateToClipboard}
                  disabled={tplBusy}
                  variant="outline"
                  className="text-xs h-8"
                  data-testid="tpl-copy"
                  title="Subject + Body to clipboard"
                >
                  📋 Copy
                </Button>
              </div>
              <p className="text-[10px] text-slate-500 leading-tight">
                <b>Open Reply</b> launches a normal Outlook compose window (recipients & thread preserved). <b>Insert in draft</b> drops the body at the cursor of an already-open Reply.
              </p>

              {/* Persistent diagnostic + result line — 2026-05-27 v3.
                  Toasts disappear and in production users couldn't tell
                  whether "Open Reply" worked or silently failed. Show the
                  Office.js state AND the last action outcome right here. */}
              <div className="rounded border border-slate-200 bg-slate-50 p-2 mt-2 space-y-1 text-[10px]" data-testid="tpl-diag">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-semibold text-slate-700">Outlook context:</span>
                  <span className={`px-1.5 py-0.5 rounded ${officeDiag.office ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                    Office.js {officeDiag.office ? "✓" : "✗"}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded ${officeDiag.item ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                    item: {officeDiag.item ? (officeDiag.itemType || "yes") : "NONE"}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded ${officeDiag.mode === "compose" ? "bg-indigo-100 text-indigo-800" : officeDiag.mode === "read" ? "bg-sky-100 text-sky-800" : "bg-slate-200 text-slate-700"}`}>
                    mode: {officeDiag.mode}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded ${officeDiag.asyncReplyAll ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                    asyncReplyAll: {officeDiag.asyncReplyAll ? "✓" : "✗"}
                  </span>
                </div>
                {!officeDiag.item && (
                  <div className="text-amber-700 leading-tight">
                    ⚠ No email is selected in Outlook. <b>Open Reply</b> will open a fresh New-Message window with the template instead of a reply.
                  </div>
                )}
                {tplLastResult && (
                  <div className={`leading-tight ${tplLastResult.ok ? "text-emerald-700" : "text-red-700"}`} data-testid="tpl-last-result">
                    [{tplLastResult.at}] {tplLastResult.ok ? "✓" : "✗"} <b>{tplLastResult.kind}</b> — {tplLastResult.msg}
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Transport Orders Tab */}
        <TabsContent value="orders" className="p-3 space-y-3">
          <div className="flex gap-2">
            <Button onClick={fetchTransportOrders} variant="outline" size="sm" disabled={ordersLoading} className="flex-1">
              {ordersLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-1" /> Refresh</>}
            </Button>
            <Button onClick={() => setShowCreateTruckingOrder(true)} size="sm" variant="default" className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-1" /> New Order
            </Button>
          </div>
          
          <ScrollArea className="h-[calc(100vh-260px)]">
            {transportOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Truck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No transport orders found</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                  onClick={() => setShowCreateTruckingOrder(true)}
                >
                  <Plus className="h-4 w-4 mr-1" /> Create Trucking Order
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {transportOrders.map((order) => (
                  <TOEditCard key={order.id} order={order} onUpdate={fetchTransportOrders} />
                ))}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* WhatsApp Tab */}
        <TabsContent value="whatsapp" className="p-3 space-y-3">
          <Card className="border-green-200 bg-green-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-green-600" />
                WhatsApp Ticket Reply
              </CardTitle>
              <CardDescription className="text-xs">
                Reply to WhatsApp support tickets directly from Outlook
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                size="sm"
                variant="outline"
                onClick={detectWhatsAppTicket}
                disabled={whatsappLoading}
                className="w-full"
              >
                {whatsappLoading ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-1" />
                )}
                Detect Ticket from Email
              </Button>
            </CardContent>
          </Card>

          {whatsappLoading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 mx-auto animate-spin text-green-500" />
              <p className="text-sm text-gray-500 mt-2">Loading ticket...</p>
            </div>
          ) : whatsappTicket ? (
            <div className="space-y-3">
              {/* Ticket Info */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>Ticket #{whatsappTicket.ticket_id}</span>
                    <Badge variant={
                      whatsappTicket.status === "pending" ? "secondary" :
                      whatsappTicket.status === "resolved" ? "default" : "outline"
                    }>
                      {whatsappTicket.status}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span>{whatsappTicket.customer_name || "Unknown"}</span>
                  </div>
                  {whatsappTicket.customer_company && (
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-gray-400" />
                      <span>{whatsappTicket.customer_company}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="font-mono text-xs">{whatsappTicket.phone_number}</span>
                  </div>
                  {whatsappTicket.reference_number && (
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-gray-400" />
                      <span className="font-mono">{whatsappTicket.reference_number}</span>
                    </div>
                  )}
                  {whatsappTicket.related_pf && (
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-gray-400" />
                      <Badge variant="outline">{whatsappTicket.related_pf}</Badge>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Chat Transcript */}
              {whatsappTicket.chat_transcript && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs">Chat Transcript</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-32 rounded bg-gray-50 p-2">
                      <pre className="text-xs whitespace-pre-wrap font-mono">
                        {whatsappTicket.chat_transcript}
                      </pre>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}

              {/* Reply Section */}
              <Card className="border-green-300">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Send className="h-4 w-4 text-green-600" />
                    Send WhatsApp Reply
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Textarea
                    value={whatsappReply}
                    onChange={(e) => setWhatsappReply(e.target.value)}
                    placeholder="Type your reply message..."
                    rows={3}
                    className="text-sm"
                  />
                  <Button
                    onClick={sendWhatsAppReply}
                    disabled={!whatsappReply.trim() || sendingWhatsappReply}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    {sendingWhatsappReply ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-1" />
                    )}
                    Send via WhatsApp
                  </Button>
                </CardContent>
              </Card>

              {/* Status Actions */}
              <div className="flex gap-2">
                {whatsappTicket.status === "pending" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateWhatsAppStatus("in_progress")}
                    className="flex-1 text-blue-600 border-blue-200"
                  >
                    <Clock className="h-3 w-3 mr-1" /> In Progress
                  </Button>
                )}
                {whatsappTicket.status !== "resolved" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateWhatsAppStatus("resolved")}
                    className="flex-1 text-green-600 border-green-200"
                  >
                    <CheckCircle className="h-3 w-3 mr-1" /> Resolved
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Open a WhatsApp ticket email</p>
              <p className="text-xs mt-1 text-gray-400">Subject must contain "WhatsApp Ticket #WA-..."</p>
            </div>
          )}
        </TabsContent>

        {/* ═══ PA 3-TIER NOTIFICATIONS TAB ═══ */}
        <TabsContent value="pa_notif" className="p-3 space-y-3" data-testid="pa-notif-tab">
          {/* Notification Summary Bar */}
          <div className="flex gap-2 text-xs overflow-x-auto pb-1">
            <span className={`whitespace-nowrap px-2 py-1 rounded font-medium ${paNotifications.counts?.urgent > 0 ? "bg-red-100 text-red-800 animate-pulse" : "bg-red-50 text-red-400"}`}>
              {paNotifications.counts?.urgent || 0} urgent
            </span>
            <span className={`whitespace-nowrap px-2 py-1 rounded font-medium ${paNotifications.counts?.action > 0 ? "bg-amber-100 text-amber-800" : "bg-amber-50 text-amber-400"}`}>
              {paNotifications.counts?.action || 0} action
            </span>
            <span className="whitespace-nowrap px-2 py-1 rounded bg-blue-50 text-blue-600">
              {paNotifications.counts?.info || 0} info
            </span>
            <button onClick={fetchPaNotifications} className="ml-auto text-xs text-blue-600 flex items-center gap-1" data-testid="pa-notif-refresh">
              <RefreshCw className="h-3 w-3" /> Refresh
            </button>
          </div>

          <ScrollArea className="h-[calc(100vh-290px)]">
            <div className="space-y-3">
              {/* TIER 1 — Urgent (red) */}
              {paNotifications.tier1.length > 0 && (
                <div data-testid="pa-notif-tier1">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-red-700 mb-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> Urgent — Needs Immediate Attention
                  </p>
                  <div className="space-y-1.5">
                    {paNotifications.tier1.map((n) => (
                      <Card key={n.id} className="border-l-4 border-l-red-500 cursor-pointer hover:bg-red-50/50 transition-colors"
                        onClick={() => { setActiveTab("pa_inbox"); setPaEmailFilter("escalated"); setTimeout(() => openPaEmailForReply(n.email_id), 200); }}
                        data-testid={`pa-notif-urgent-${n.id}`}>
                        <CardContent className="p-2.5">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-red-800 truncate">{n.title}</p>
                              <p className="text-[10px] text-red-600/70 truncate">{n.detail}</p>
                            </div>
                            <Badge className="text-[9px] bg-red-600 text-white flex-shrink-0">{n.type}</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* TIER 2 — Action Required (amber) */}
              {paNotifications.tier2.length > 0 && (
                <div data-testid="pa-notif-tier2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1 flex items-center gap-1">
                    <Clock className="h-3 w-3" /> Action Needed
                  </p>
                  <div className="space-y-1.5">
                    {paNotifications.tier2.map((n) => (
                      <Card key={n.id} className="border-l-4 border-l-amber-400 cursor-pointer hover:bg-amber-50/50 transition-colors"
                        onClick={() => {
                          if (n.type === "draft_pending") { setActiveTab("pa_queue"); }
                          else if (n.type === "booking_approval") { setActiveTab("pa_inbox"); setTimeout(() => openPaEmailForReply(n.email_id), 200); }
                          else { setActiveTab("pa_inbox"); setTimeout(() => openPaEmailForReply(n.email_id), 200); }
                        }}
                        data-testid={`pa-notif-action-${n.id}`}>
                        <CardContent className="p-2.5">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-amber-900 truncate">{n.title}</p>
                              <p className="text-[10px] text-amber-600/70 truncate">{n.detail}</p>
                            </div>
                            <Badge variant="outline" className="text-[9px] border-amber-400 text-amber-700 flex-shrink-0">{n.type.replace("_", " ")}</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* TIER 3 — Info (blue) */}
              {paNotifications.tier3.length > 0 && (
                <div data-testid="pa-notif-tier3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-blue-600 mb-1 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" /> Recent Activity
                  </p>
                  <div className="space-y-1">
                    {paNotifications.tier3.map((n) => (
                      <div key={n.id} className="px-2.5 py-1.5 bg-blue-50/60 rounded text-xs text-blue-800 flex items-start gap-2" data-testid={`pa-notif-info-${n.id}`}>
                        <CheckCircle className="h-3 w-3 text-blue-400 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <span className="font-medium truncate block">{n.title}</span>
                          <span className="text-[10px] text-blue-500">{n.detail}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {paNotifications.counts?.total === 0 && (
                <div className="text-center py-10 text-gray-400">
                  <CheckCircle className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm font-medium">All clear</p>
                  <p className="text-xs mt-1">No pending notifications</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        {/* ═══ PA INBOX TAB ═══ */}
        <TabsContent value="pa_inbox" className="p-3 space-y-3">
          {/* PA Stats Bar */}
          {paStats && (
            <div className="flex gap-2 text-xs overflow-x-auto pb-1">
              <span className="whitespace-nowrap px-2 py-1 rounded bg-amber-100 text-amber-800">{paStats.pending_emails || 0} pending</span>
              <span className="whitespace-nowrap px-2 py-1 rounded bg-indigo-100 text-indigo-800">{paStats.drafts_waiting || 0} drafts</span>
              <span className="whitespace-nowrap px-2 py-1 rounded bg-emerald-100 text-emerald-800">{paStats.auto_sent_24h || 0} sent</span>
              <span className="whitespace-nowrap px-2 py-1 rounded bg-red-100 text-red-800">{paStats.escalated || 0} escalated</span>
            </div>
          )}

          {/* Filters */}
          <div className="flex gap-1 flex-wrap">
            {["all", "pending", "drafted", "escalated", "sent", "completed"].map(f => (
              <button
                key={f}
                onClick={() => setPaEmailFilter(f)}
                className={`text-xs px-2 py-1 rounded transition-colors ${paEmailFilter === f ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                data-testid={`pa-filter-${f}`}
              >
                {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
            <button onClick={() => { fetchPaEmails(); fetchPaStats(); }} className="ml-auto text-xs text-blue-600 flex items-center gap-1">
              <RefreshCw className={`h-3 w-3 ${paEmailsLoading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>

          <ScrollArea className="h-[calc(100vh-310px)]">
            {paEmailsLoading ? (
              <div className="text-center py-8"><Loader2 className="h-6 w-6 mx-auto animate-spin text-blue-500" /></div>
            ) : paEmails.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Inbox className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No emails in this view</p>
              </div>
            ) : (
              <div className="space-y-2">
                {paEmails.map((em) => (
                  <Card key={em.email_id} className={`cursor-pointer hover:border-blue-300 transition-colors ${em.is_internal ? "opacity-60 border-dashed" : ""}`} onClick={() => openPaEmailForReply(em.email_id)}>
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <span className="font-mono text-[10px] text-gray-400">{em.email_id}</span>
                        <div className="flex gap-1 flex-shrink-0">
                          {em.is_internal && (
                            <Badge className="text-[9px] bg-slate-200 text-slate-600">internal</Badge>
                          )}
                          <Badge variant="secondary" className={`text-[10px] ${
                            em.category === "complaint" ? "bg-red-100 text-red-700" :
                            em.category === "pricing_commercial" ? "bg-amber-100 text-amber-700" :
                            em.category === "shipment_status" ? "bg-blue-100 text-blue-700" :
                            em.category === "eta_request" ? "bg-cyan-100 text-cyan-700" :
                            "bg-gray-100"
                          }`}>{em.category?.replace("_", " ")}</Badge>
                          <Badge variant="outline" className={`text-[10px] ${
                            em.status === "pending" ? "border-amber-300 text-amber-700" :
                            em.status === "escalated" ? "border-red-300 text-red-700" :
                            em.status === "sent" ? "border-green-300 text-green-700" :
                            em.status === "skipped_internal" ? "border-slate-300 text-slate-500" :
                            em.status === "ignored" ? "border-gray-300 text-gray-500" :
                            "border-gray-300"
                          }`}>{em.status === "skipped_internal" ? "skipped" : em.status}</Badge>
                        </div>
                      </div>
                      <p className="text-sm font-medium truncate">{em.subject}</p>
                      <p className="text-xs text-gray-500 truncate">{em.from_email}</p>
                      {em.reference && <p className="text-[10px] text-violet-600 mt-1">Ref: {em.reference}</p>}
                      {em.skip_reason && <p className="text-[10px] text-slate-400 mt-0.5 italic">{em.skip_reason}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* PA Email Detail Overlay with Reply Composer */}
          {paSelectedEmail && (
            <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid="pa-email-detail-overlay">
              {/* Header */}
              <div className={`text-white px-3 py-2 flex items-center justify-between flex-shrink-0 ${paSelectedEmail.is_internal ? "bg-slate-500" : paSelectedEmail.status === "escalated" ? "bg-red-600" : "bg-blue-600"}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium truncate">{paSelectedEmail.email_id}</span>
                  {paSelectedEmail.is_internal && (
                    <Badge className="text-[9px] bg-white/20 text-white">INTERNAL</Badge>
                  )}
                  <Badge className={`text-[10px] ${paSelectedEmail.status === "escalated" ? "bg-red-400/30 text-white" : paSelectedEmail.status === "completed" ? "bg-green-400/30 text-white" : paSelectedEmail.status === "skipped_internal" ? "bg-slate-400/30 text-white" : "bg-white/20 text-white"}`}>
                    {paSelectedEmail.status === "skipped_internal" ? "skipped" : paSelectedEmail.status}
                  </Badge>
                </div>
                <button onClick={() => { setPaSelectedEmail(null); setPaReplyMode(false); }} className="p-1 hover:bg-black/10 rounded" data-testid="pa-close-detail">
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Scrollable body */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-3 space-y-3">
                  {/* Internal domain notice */}
                  {paSelectedEmail.is_internal && (
                    <div className="bg-slate-50 border border-slate-200 rounded px-2.5 py-1.5 text-xs text-slate-600 flex items-center gap-2" data-testid="pa-internal-notice">
                      <Building2 className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                      <span>Internal group company email — auto-skipped by PA domain rules</span>
                    </div>
                  )}

                  {/* Subject & sender */}
                  <div>
                    <p className="text-sm font-medium">{paSelectedEmail.subject}</p>
                    <p className="text-xs text-gray-500">{paSelectedEmail.from_email} &rarr; {paSelectedEmail.to_email || "info@europacific.com"}</p>
                    {paSelectedEmail.reference && <p className="text-[10px] text-violet-600 mt-0.5">Ref: {paSelectedEmail.reference} ({paSelectedEmail.reference_type})</p>}
                  </div>

                  {/* Enhanced Classification Analysis */}
                  <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-2.5 border border-indigo-100" data-testid="pa-email-analysis">
                    <p className="text-[10px] text-indigo-600 uppercase tracking-wider font-bold mb-1.5">AI Classification</p>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <p className="text-[9px] text-gray-500 uppercase">Category</p>
                        <Badge className={`text-[10px] mt-0.5 ${paSelectedEmail.category === "complaint" ? "bg-red-100 text-red-700" : paSelectedEmail.category === "pricing_commercial" ? "bg-amber-100 text-amber-700" : paSelectedEmail.category === "eta_request" ? "bg-cyan-100 text-cyan-700" : "bg-blue-100 text-blue-700"}`}>
                          {paSelectedEmail.category?.replace("_", " ")}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-[9px] text-gray-500 uppercase">Risk</p>
                        <Badge variant="outline" className={`text-[10px] mt-0.5 ${paSelectedEmail.risk_level === "high" ? "border-red-300 text-red-700" : paSelectedEmail.risk_level === "low" ? "border-green-300 text-green-700" : "border-amber-300 text-amber-700"}`}>
                          {paSelectedEmail.risk_level}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-[9px] text-gray-500 uppercase">Confidence</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <div className="h-1.5 w-12 bg-gray-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${paSelectedEmail.confidence >= 0.85 ? "bg-green-500" : paSelectedEmail.confidence >= 0.6 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${(paSelectedEmail.confidence || 0) * 100}%` }} />
                          </div>
                          <span className="text-[10px] font-mono">{Math.round((paSelectedEmail.confidence || 0) * 100)}%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Original email body */}
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 font-medium">Original Email</p>
                    <div className="bg-gray-50 rounded p-2 text-xs text-gray-700 border whitespace-pre-wrap max-h-40 overflow-y-auto">
                      {paSelectedEmail.body_full || paSelectedEmail.body_preview || "(No content available)"}
                    </div>
                  </div>

                  {/* PA Draft Reply — Approve / Decline / Edit */}
                  {paSelectedEmail.draft && (
                    <div className="border border-indigo-200 rounded-lg overflow-hidden" data-testid="pa-draft-section">
                      <div className="bg-indigo-50 px-3 py-1.5 flex items-center justify-between">
                        <p className="text-[10px] text-indigo-700 uppercase tracking-wider font-medium flex items-center gap-1">
                          <Sparkles className="h-3 w-3" /> PA Draft Reply
                        </p>
                        <span className="text-[9px] text-gray-500">
                          {paSelectedEmail.draft.template_used || "auto"} | {paSelectedEmail.draft.risk_level || "—"} risk
                        </span>
                      </div>
                      {paIsEditingDraft ? (
                        <div className="p-2 space-y-2">
                          <Textarea
                            value={paEditingDraftBody}
                            onChange={e => setPaEditingDraftBody(e.target.value)}
                            rows={6}
                            className="text-xs"
                            data-testid="pa-draft-edit-textarea"
                          />
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" className="text-xs" onClick={() => setPaIsEditingDraft(false)}>Cancel</Button>
                            <Button size="sm" className="text-xs bg-indigo-600 hover:bg-indigo-700" disabled={paDraftActionLoading}
                              onClick={() => handlePaDraftAction(paSelectedEmail.draft.draft_id, "edit", paEditingDraftBody)}
                              data-testid="pa-draft-save-edit">
                              {paDraftActionLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Save Edit
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-2">
                          <div className="bg-white rounded p-2 text-xs text-gray-700 border whitespace-pre-wrap max-h-36 overflow-y-auto mb-2">
                            {paSelectedEmail.draft.body}
                          </div>
                          {paSelectedEmail.draft.status !== "approved" && paSelectedEmail.draft.status !== "rejected" && (
                            <div className="flex flex-wrap gap-1.5">
                              <Button size="sm" className="text-xs bg-green-600 hover:bg-green-700 flex-1 min-w-[80px] gap-1" disabled={paDraftActionLoading}
                                onClick={() => handlePaDraftAction(paSelectedEmail.draft.draft_id, "approve")}
                                data-testid="pa-draft-approve">
                                <CheckCircle className="h-3 w-3" /> Approve
                              </Button>
                              <Button size="sm" variant="outline" className="text-xs flex-1 min-w-[80px] gap-1"
                                onClick={() => { setPaIsEditingDraft(true); setPaEditingDraftBody(paSelectedEmail.draft.body); }}
                                data-testid="pa-draft-edit">
                                <Edit3 className="h-3 w-3" /> Edit
                              </Button>
                              <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-200 flex-1 min-w-[80px] gap-1" disabled={paDraftActionLoading}
                                onClick={() => handlePaDraftAction(paSelectedEmail.draft.draft_id, "reject")}
                                data-testid="pa-draft-decline">
                                <X className="h-3 w-3" /> Decline
                              </Button>
                            </div>
                          )}
                          {paSelectedEmail.draft.status === "approved" && (
                            <p className="text-[10px] text-green-600 font-medium flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Approved</p>
                          )}
                          {paSelectedEmail.draft.status === "rejected" && (
                            <p className="text-[10px] text-red-600 font-medium flex items-center gap-1"><X className="h-3 w-3" /> Declined</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-1 flex-wrap">
                    {paSelectedEmail.status !== "completed" && (
                      <Button size="sm" className="text-xs bg-blue-600 hover:bg-blue-700 gap-1" onClick={() => setPaReplyMode(!paReplyMode)} data-testid="pa-toggle-reply">
                        <Send className="h-3 w-3" /> {paReplyMode ? "Hide Reply" : "Reply"}
                      </Button>
                    )}
                    {paSelectedEmail.status === "pending" && (
                      <>
                        <Button size="sm" variant="outline" className="text-xs" onClick={() => { handlePaEmailAction(paSelectedEmail.email_id, "escalate"); setPaSelectedEmail(null); setPaReplyMode(false); }}>
                          <AlertCircle className="h-3 w-3 mr-1" /> Escalate
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs" onClick={() => { handlePaEmailAction(paSelectedEmail.email_id, "no_action"); setPaSelectedEmail(null); setPaReplyMode(false); }}>
                          <CheckCircle className="h-3 w-3 mr-1" /> No Action
                        </Button>
                      </>
                    )}
                    {(paSelectedEmail.status === "escalated" || paSelectedEmail.status === "waiting_internal") && (
                      <Button size="sm" variant="outline" className="text-xs text-green-600 border-green-200" onClick={async () => {
                        try {
                          await axios.put(`${API_URL}/pa/escalations/${paSelectedEmail.email_id}/resolve?notes=Resolved from Outlook plugin`, null, getAuthHeaders());
                          toast.success("Resolved");
                          setPaSelectedEmail(null); setPaReplyMode(false);
                          fetchPaEmails(); fetchPaStats();
                        } catch (err) { toast.error("Resolve failed"); }
                      }} data-testid="pa-resolve-btn">
                        <CheckCircle className="h-3 w-3 mr-1" /> Resolve
                      </Button>
                    )}
                  </div>

                  {/* Reply Composer */}
                  {paReplyMode && (
                    <div className="border rounded-lg p-3 bg-blue-50/50 space-y-2" data-testid="pa-reply-composer">
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium flex items-center gap-1"><Send className="h-3 w-3" /> Compose Reply</p>
                      <div>
                        <Label className="text-[10px] text-gray-500">To</Label>
                        <Input value={paReplyTo} onChange={e => setPaReplyTo(e.target.value)} className="text-xs h-7" data-testid="pa-reply-to" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-500">CC (optional)</Label>
                        <Input value={paReplyCc} onChange={e => setPaReplyCc(e.target.value)} className="text-xs h-7" placeholder="cc@example.com" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-500">Subject</Label>
                        <Input value={paReplySubject} onChange={e => setPaReplySubject(e.target.value)} className="text-xs h-7" />
                      </div>
                      <Textarea value={paReplyBody} onChange={e => setPaReplyBody(e.target.value)} rows={5}
                        placeholder="Type your reply..." className="text-xs" data-testid="pa-reply-body" />
                      {paSelectedEmail.draft && !paReplyBody && (
                        <button onClick={() => setPaReplyBody(paSelectedEmail.draft.body)}
                          className="text-[10px] text-indigo-600 hover:underline flex items-center gap-1">
                          <Sparkles className="h-3 w-3" /> Use PA suggested reply
                        </button>
                      )}
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" className="text-xs" onClick={() => setPaReplyMode(false)}>Cancel</Button>
                        <Button size="sm" className="text-xs gap-1" onClick={() => handlePaReply(paSelectedEmail.email_id)}
                          disabled={paReplySending || !paReplyBody.trim()} data-testid="pa-send-reply-btn">
                          {paReplySending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                          Send Reply
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Internal Note */}
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 font-medium">Internal Note</p>
                    <div className="flex gap-1">
                      <Input value={paNote} onChange={e => setPaNote(e.target.value)} placeholder="Add a note..."
                        className="text-xs h-7" onKeyDown={e => e.key === "Enter" && handlePaAddNote(paSelectedEmail.email_id)}
                        data-testid="pa-note-input" />
                      <Button size="sm" className="h-7 text-xs" onClick={() => handlePaAddNote(paSelectedEmail.email_id)} disabled={!paNote.trim()}>Add</Button>
                    </div>
                  </div>

                  {/* Timeline */}
                  {paSelectedEmail.events?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1 font-medium">Timeline ({paSelectedEmail.events.length})</p>
                      <div className="space-y-1 border-l-2 border-gray-200 ml-1 pl-3">
                        {paSelectedEmail.events.map((evt, i) => (
                          <div key={i} className="text-xs">
                            <div className="flex items-center gap-1">
                              <span className="text-gray-400">{new Date(evt.timestamp).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                              <span className={`font-mono text-[10px] ${evt.type.includes("reply") ? "text-green-600" : evt.type.includes("note") ? "text-amber-600" : evt.type.includes("escalat") ? "text-red-600" : "text-violet-600"}`}>
                                {evt.type}
                              </span>
                              {evt.acted_by && <span className="text-gray-400">by {evt.acted_by}</span>}
                            </div>
                            <p className="text-gray-600">{evt.detail}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ═══ PA APPROVAL QUEUE TAB ═══ */}
        <TabsContent value="pa_queue" className="p-3 space-y-3 min-w-0 max-w-full overflow-x-hidden">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="text-sm font-semibold flex items-center gap-1">
              <ClipboardCheck className="h-4 w-4 text-indigo-600" /> Approval Queue ({paQueue.length})
            </h3>
            <div className="flex items-center gap-2">
              {/* Mine ↔ All filter */}
              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-[11px]" data-testid="pa-queue-filter">
                <button
                  type="button"
                  onClick={() => setPaQueueFilter("mine")}
                  className={`px-2 py-0.5 ${paQueueFilter === "mine" ? "bg-indigo-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                  data-testid="pa-queue-filter-mine"
                >
                  Mine
                </button>
                <button
                  type="button"
                  onClick={() => setPaQueueFilter("all")}
                  className={`px-2 py-0.5 border-l border-slate-200 ${paQueueFilter === "all" ? "bg-indigo-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                  data-testid="pa-queue-filter-all"
                >
                  All
                </button>
              </div>
              <button onClick={() => { fetchPaQueue(); fetchPaStats(); fetchPaQueueStats(); ensureDraftForCurrentEmail(); }} className="text-xs text-blue-600 flex items-center gap-1">
                <RefreshCw className={`h-3 w-3 ${paQueueLoading ? "animate-spin" : ""}`} /> Refresh
              </button>
            </div>
          </div>

          {paStats && (
            <div className="flex gap-2 text-xs flex-wrap">
              <span className="px-2 py-1 rounded bg-indigo-100 text-indigo-800">{paStats.drafts_waiting || 0} waiting</span>
              <span className="px-2 py-1 rounded bg-amber-100 text-amber-800">{paStats.pending_emails || 0} pending</span>
              {paQueueStats?.older_than_7_days > 0 && (
                <span className="px-2 py-1 rounded bg-red-100 text-red-800" data-testid="queue-stats-older-7d">
                  {paQueueStats.older_than_7_days} older than 1 week
                </span>
              )}
            </div>
          )}

          {/* ─── Pinned: Current Outlook Email ─── */}
          {currentEmail && (
            <Card className="border-l-4 border-l-amber-500 bg-amber-50/40" data-testid="pa-queue-current-email">
              <CardContent className="p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-amber-700 font-semibold">
                      <Mail className="h-3 w-3" /> Current email
                    </div>
                    <p className="text-sm font-medium truncate" title={currentEmail.subject}>
                      {paCurrentDraft?.ticket ? <span className="font-mono text-[10px] text-slate-400 mr-1">{paCurrentDraft.ticket}</span> : null}
                      {currentEmail.subject || "(no subject)"}
                    </p>
                    <p className="text-xs text-slate-500 truncate">From: {currentEmail.sender_email || "—"}</p>
                  </div>
                </div>

                {paCurrentDraftLoading ? (
                  <div className="text-center py-3"><Loader2 className="h-4 w-4 mx-auto animate-spin text-amber-500" /></div>
                ) : paCurrentDraft?.approval?.status === "approved" ? (
                  <div className="text-xs flex items-center gap-1.5 text-green-700 bg-green-100 rounded px-2 py-1" data-testid="current-draft-approved">
                    <CheckCircle className="h-3.5 w-3.5" />
                    <span>Already approved{paCurrentDraft.approval.approved_by ? ` by ${paCurrentDraft.approval.approved_by}` : ""}</span>
                  </div>
                ) : paCurrentDraft?.approval?.status === "rejected" ? (
                  <div className="text-xs flex items-center gap-1.5 text-red-700 bg-red-100 rounded px-2 py-1" data-testid="current-draft-rejected">
                    <X className="h-3.5 w-3.5" />
                    <span>Already rejected{paCurrentDraft.approval.rejected_by ? ` by ${paCurrentDraft.approval.rejected_by}` : ""}</span>
                  </div>
                ) : paCurrentDraft?.draft ? (
                  <>
                    <div className="bg-white rounded p-2 text-xs text-slate-700 border whitespace-pre-wrap max-h-32 overflow-y-auto" data-testid="current-draft-body">
                      {paCurrentDraft.draft.body}
                    </div>
                    {paCurrentDraft.draft_created && (
                      <p className="text-[10px] text-amber-700 italic">Draft was just generated for this email.</p>
                    )}
                    <div className="flex flex-wrap items-center gap-1">
                      <PaTrustBadge draft={paCurrentDraft.draft} testId="current-draft-trust" />
                      <Button size="sm" className="text-xs bg-emerald-600 hover:bg-emerald-700 flex-1 min-w-[90px]" onClick={() => handleCurrentDraftAction("approve")} data-testid="current-draft-approve">
                        <CheckCircle className="h-3 w-3 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-200 flex-1 min-w-[90px]" onClick={() => handleCurrentDraftAction("reject")} data-testid="current-draft-reject">
                        <X className="h-3 w-3 mr-1" /> Reject
                      </Button>
                    </div>
                    {/* Rewrite suggestion — regenerate this reply via AI before approving */}
                    <div className="flex flex-col gap-1 pt-1">
                      <textarea
                        rows={2}
                        value={paRewriteText[paCurrentDraft.draft.draft_id] || ""}
                        onChange={(e) => setPaRewriteText(prev => ({ ...prev, [paCurrentDraft.draft.draft_id]: e.target.value }))}
                        placeholder="Suggest a rewrite (e.g. 'shorter, note ETA is tentative')…"
                        className="w-full text-xs border border-slate-300 rounded-md px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400"
                        data-testid="current-draft-rewrite-input"
                      />
                      <button
                        type="button"
                        onClick={() => handlePaRewrite(paCurrentDraft.draft.draft_id)}
                        disabled={paRewriteLoading === paCurrentDraft.draft.draft_id}
                        className="inline-flex items-center justify-center gap-1 py-1.5 px-2 rounded-md text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 disabled:opacity-50"
                        data-testid="current-draft-rewrite-btn"
                      >
                        {paRewriteLoading === paCurrentDraft.draft.draft_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Rewrite with AI
                      </button>
                    </div>
                    <PaReplyPreview draftId={paCurrentDraft.draft.draft_id} authHeaders={getAuthHeaders()} />
                  </>
                ) : (
                  <div className="flex items-center justify-between gap-2 text-xs text-slate-500">
                    <span>No draft yet for this email.</span>
                    <Button size="sm" variant="outline" className="text-xs" onClick={ensureDraftForCurrentEmail} data-testid="current-draft-generate">
                      <Sparkles className="h-3 w-3 mr-1" /> Generate
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* ─── Bulk cleanup toolbar ─── */}
          {paQueue.length > 0 && (
            <Card className="border-slate-200 bg-slate-50/60" data-testid="pa-queue-bulk-toolbar">
              <CardContent className="p-2 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Bulk cleanup</span>
                  <span className="text-[10px] text-slate-500" data-testid="bulk-selected-count">{paQueueSelected.size} selected</span>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  <Button size="sm" variant="outline" className="text-[11px] h-7"
                    onClick={() => {
                      const cutoff = Date.now() - 24 * 3600 * 1000;
                      const ids = paQueue.filter(d => new Date(d.created_at).getTime() < cutoff).map(d => d.draft_id);
                      setPaQueueSelected(new Set(ids));
                    }}
                    data-testid="bulk-select-older-1d"
                  >
                    Select {">"} 1 day
                  </Button>
                  <Button size="sm" variant="outline" className="text-[11px] h-7"
                    onClick={() => {
                      const cutoff = Date.now() - 7 * 24 * 3600 * 1000;
                      const ids = paQueue.filter(d => new Date(d.created_at).getTime() < cutoff).map(d => d.draft_id);
                      setPaQueueSelected(new Set(ids));
                    }}
                    data-testid="bulk-select-older-7d"
                  >
                    Select {">"} 1 week
                  </Button>
                  <Button size="sm" variant="outline" className="text-[11px] h-7"
                    onClick={() => setPaQueueSelected(new Set(paQueue.map(d => d.draft_id)))}
                    data-testid="bulk-select-all"
                  >
                    Select all ({paQueue.length})
                  </Button>
                  <Button size="sm" variant="ghost" className="text-[11px] h-7 text-slate-500"
                    onClick={() => setPaQueueSelected(new Set())}
                    disabled={paQueueSelected.size === 0}
                    data-testid="bulk-clear"
                  >
                    Clear
                  </Button>
                </div>
                <Button size="sm"
                  className={`text-[11px] h-7 w-full ${paRejectAllConfirming ? "bg-red-900 hover:bg-red-950 animate-pulse" : "bg-red-700 hover:bg-red-800"}`}
                  disabled={paBulkLoading}
                  onClick={() => {
                    if (!paRejectAllConfirming) {
                      setPaRejectAllConfirming(true);
                      toast.info('Tap "Confirm — reject ALL pending" again to clear the ENTIRE backlog (auto-cancels in 5s)');
                      setTimeout(() => setPaRejectAllConfirming(false), 5000);
                      return;
                    }
                    setPaRejectAllConfirming(false);
                    handleBulkReject({ allPending: true, reason: "reject_all_pending" });
                  }}
                  data-testid="bulk-reject-all-pending"
                >
                  {paBulkLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1 shrink-0" /> : <X className="h-3 w-3 mr-1 shrink-0" />}
                  <span className="truncate">{paRejectAllConfirming ? "Confirm — reject ALL pending" : "Reject ALL pending (clears backlog)"}</span>
                </Button>
                <div className="flex gap-1">
                  <Button size="sm"
                    className={`text-[11px] h-7 flex-1 min-w-0 ${paBulkRejectConfirming ? "bg-red-800 hover:bg-red-900 animate-pulse" : "bg-red-600 hover:bg-red-700"}`}
                    disabled={paQueueSelected.size === 0 || paBulkLoading}
                    onClick={() => {
                      if (!paBulkRejectConfirming) {
                        setPaBulkRejectConfirming(true);
                        toast.info(`Tap "Confirm reject" again to reject ${paQueueSelected.size} draft${paQueueSelected.size === 1 ? "" : "s"} (auto-cancels in 5s)`);
                        setTimeout(() => setPaBulkRejectConfirming(false), 5000);
                        return;
                      }
                      setPaBulkRejectConfirming(false);
                      handleBulkReject({ draftIds: Array.from(paQueueSelected), reason: "manual_bulk" });
                    }}
                    data-testid="bulk-reject-selected"
                  >
                    {paBulkLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1 shrink-0" /> : <X className="h-3 w-3 mr-1 shrink-0" />}
                    <span className="truncate">{paBulkRejectConfirming ? "Confirm reject" : "Reject selected"}</span>
                  </Button>
                  <Button size="sm" variant="outline"
                    className={`text-[11px] h-7 shrink-0 ${paAutoCleanupConfirming ? "bg-red-100 text-red-900 border-red-400 animate-pulse" : "text-red-700 border-red-200"}`}
                    onClick={() => {
                      if (!paAutoCleanupConfirming) {
                        setPaAutoCleanupConfirming(true);
                        toast.info('Tap "Confirm" again to auto-reject all drafts older than 7 days (auto-cancels in 5s)');
                        setTimeout(() => setPaAutoCleanupConfirming(false), 5000);
                        return;
                      }
                      setPaAutoCleanupConfirming(false);
                      handleAutoCleanup(7);
                    }}
                    disabled={paBulkLoading}
                    data-testid="bulk-auto-cleanup-7d"
                    title="Auto-reject every pending draft older than 7 days"
                  >
                    {paAutoCleanupConfirming ? "Confirm" : `Cleanup > 1 wk`}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="h-[calc(100vh-280px)] w-full max-w-full overflow-y-auto overflow-x-hidden">
            {paQueueLoading ? (
              <div className="text-center py-8"><Loader2 className="h-6 w-6 mx-auto animate-spin text-indigo-500" /></div>
            ) : paQueue.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No drafts pending approval</p>
              </div>
            ) : (
              <div className="space-y-3 min-w-0 max-w-full pr-1">
                {paQueue.map((draft) => {
                  const info = draft.email_info || {};
                  const createdAt = draft.created_at ? new Date(draft.created_at) : null;
                  const ageHours = createdAt ? Math.floor((Date.now() - createdAt.getTime()) / 3600000) : null;
                  const ageLabel = ageHours == null ? "" :
                    ageHours < 24 ? `${ageHours}h ago` :
                    ageHours < 24 * 7 ? `${Math.floor(ageHours / 24)}d ago` :
                    `${Math.floor(ageHours / (24 * 7))}w ago`;
                  const isOld = ageHours != null && ageHours >= 24 * 7;
                  const isSelected = paQueueSelected.has(draft.draft_id);
                  return (
                    <Card key={draft.draft_id} className={`border-indigo-200 w-full max-w-full ${isSelected ? "ring-2 ring-red-300 bg-red-50/30" : ""}`} data-testid={`pa-draft-${draft.draft_id}`}>
                      <CardContent className="p-2.5 space-y-2 min-w-0 max-w-full">
                        {/* Header row: checkbox + meta + action buttons inline.
                            Buttons are part of the natural flex flow (NOT
                            absolutely positioned) so they CANNOT clip off-screen
                            no matter how narrow the Outlook pane gets. */}
                        <div className="flex items-start gap-1.5 min-w-0">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              setPaQueueSelected(prev => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(draft.draft_id);
                                else next.delete(draft.draft_id);
                                return next;
                              });
                            }}
                            className="mt-1 cursor-pointer shrink-0"
                            data-testid={`pa-draft-checkbox-${draft.draft_id}`}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="font-mono text-[10px] text-gray-400 truncate max-w-[140px]">{draft.draft_id}</span>
                              {ageLabel && <span className={`text-[10px] shrink-0 ${isOld ? "text-red-600 font-semibold" : "text-gray-400"}`}>· {ageLabel}</span>}
                              <Badge variant="outline" className={`text-[10px] border shrink-0 ${
                                draft.risk_level === "high" ? "border-red-300 text-red-700" :
                                draft.risk_level === "low" ? "border-green-300 text-green-700" :
                                "border-amber-300 text-amber-700"
                              }`}>{draft.risk_level}</Badge>
                              <PaTrustBadge draft={draft} testId={`pa-trust-${draft.draft_id}`} />
                            </div>
                            <p className="text-sm font-medium break-words" title={draft.subject || info.subject}>{draft.subject || info.subject}</p>
                            <p className="text-xs text-gray-500 truncate">To: {info.from_email || "—"}</p>
                          </div>
                          {/* Actions moved to a guaranteed-visible bottom bar. */}
                        </div>
                        {/* Category badge on its own line below the header. */}
                        {info.category && (
                          <div className="flex">
                            <Badge variant="secondary" className={`text-[10px] ${
                              info.category === "complaint" ? "bg-red-100 text-red-700" :
                              info.category === "eta_request" ? "bg-cyan-100 text-cyan-700" :
                              "bg-gray-100"
                            }`}>{info.category.replace("_", " ")}</Badge>
                          </div>
                        )}

                        {/* Draft body */}
                        {paEditDraft === draft.draft_id ? (
                          <div className="space-y-2">
                            <Textarea value={paEditBody} onChange={(e) => setPaEditBody(e.target.value)} rows={4} className="text-xs" />
                            <div className="flex gap-1">
                              <Button size="sm" className="text-xs" onClick={() => { handlePaQueueAction(draft.draft_id, "edit", paEditBody); setPaEditDraft(null); }}>
                                <Save className="h-3 w-3 mr-1" /> Save
                              </Button>
                              <Button size="sm" variant="ghost" className="text-xs" onClick={() => setPaEditDraft(null)}>Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-gray-50 rounded p-2 text-xs text-gray-700 border whitespace-pre-wrap break-words max-h-28 overflow-y-auto">
                            {draft.body}
                          </div>
                        )}

                        {/* Guaranteed-visible action bar: Edit + Reject wrap
                            and Approve always spans the full row, so every
                            button stays reachable no matter how narrow the pane. */}
                        <div className="flex flex-wrap gap-1.5">
                          <button
                            type="button"
                            onClick={() => { setPaEditDraft(draft.draft_id); setPaEditBody(draft.body || ""); }}
                            className="flex-1 min-w-[88px] inline-flex items-center justify-center gap-1 py-1.5 px-2 rounded-md text-xs font-medium text-slate-700 bg-white hover:bg-slate-100 border border-slate-300"
                            data-testid={`pa-edit-${draft.draft_id}`}
                          >
                            <Edit3 className="h-3.5 w-3.5 shrink-0" /> Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePaQueueAction(draft.draft_id, "reject")}
                            className="flex-1 min-w-[88px] inline-flex items-center justify-center gap-1 py-1.5 px-2 rounded-md text-xs font-medium text-red-600 bg-white hover:bg-red-50 border border-red-300"
                            data-testid={`pa-reject-${draft.draft_id}`}
                          >
                            <X className="h-3.5 w-3.5 shrink-0" /> Reject
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePaQueueAction(draft.draft_id, "approve")}
                            className="basis-full flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white py-2 px-3 rounded-md text-xs font-semibold border border-green-700"
                            data-testid={`pa-approve-${draft.draft_id}`}
                          >
                            <CheckCircle className="h-4 w-4 shrink-0" />
                            <span>Approve</span>
                          </button>
                        </div>
                        {/* Rewrite suggestion — 2-line instruction; PA regenerates the reply. */}
                        <div className="flex flex-col gap-1">
                          <textarea
                            rows={2}
                            value={paRewriteText[draft.draft_id] || ""}
                            onChange={(e) => setPaRewriteText(prev => ({ ...prev, [draft.draft_id]: e.target.value }))}
                            placeholder="Suggest a rewrite (e.g. 'shorter, note ETA is tentative')…"
                            className="w-full text-xs border border-slate-300 rounded-md px-2 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400"
                            data-testid={`pa-rewrite-input-${draft.draft_id}`}
                          />
                          <button
                            type="button"
                            onClick={() => handlePaRewrite(draft.draft_id)}
                            disabled={paRewriteLoading === draft.draft_id}
                            className="inline-flex items-center justify-center gap-1 py-1.5 px-2 rounded-md text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 disabled:opacity-50"
                            data-testid={`pa-rewrite-btn-${draft.draft_id}`}
                          >
                            {paRewriteLoading === draft.draft_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Rewrite with AI
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ═══ PA ERP RESOLVER TAB ═══ */}
        <TabsContent value="pa_erp" className="p-3 space-y-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Search className="h-4 w-4 text-violet-600" /> ERP Shipment Lookup
              </CardTitle>
              <CardDescription className="text-xs">Search by Container, BL, PF number, HBL, MBL, Booking</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  value={paErpQuery}
                  onChange={(e) => setPaErpQuery(e.target.value)}
                  placeholder="e.g. MSKU1234567, PF-000160-26"
                  className="text-sm"
                  onKeyDown={(e) => e.key === "Enter" && searchPaErp()}
                  data-testid="pa-erp-search"
                />
                <Button onClick={searchPaErp} disabled={paErpLoading} size="sm">
                  {paErpLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Auto-fill from email context */}
          {currentEmail && detectedReferences && (
            <div className="flex gap-1 flex-wrap">
              {detectedReferences.pf_numbers?.map(pf => (
                <button key={pf} onClick={() => { setPaErpQuery(pf); }} className="text-[10px] px-2 py-0.5 rounded bg-violet-100 text-violet-700 hover:bg-violet-200">
                  {pf}
                </button>
              ))}
              {detectedReferences.container_numbers?.map(c => (
                <button key={c} onClick={() => { setPaErpQuery(c); }} className="text-[10px] px-2 py-0.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200">
                  {c}
                </button>
              ))}
            </div>
          )}

          <ScrollArea className="h-[calc(100vh-320px)]">
            {paErpLoading ? (
              <div className="text-center py-8"><Loader2 className="h-6 w-6 mx-auto animate-spin text-violet-500" /></div>
            ) : paErpResult ? (
              <div className="space-y-2">
                {/* Status Detail (from /erp/status) */}
                {paErpResult.status_detail && (
                  <Card className="border-violet-200 bg-violet-50">
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs font-bold text-violet-700">{paErpResult.status_detail.pf_number}</span>
                        <Badge className={`text-[10px] ${paErpResult.status_detail.is_fresh ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {paErpResult.status_detail.is_fresh ? "Fresh" : "Stale"}
                        </Badge>
                      </div>
                      <p className="text-sm">{paErpResult.status_detail.summary_text}</p>
                      <div className="grid grid-cols-2 gap-1 text-xs text-gray-600">
                        <span>Status: <strong>{paErpResult.status_detail.status}</strong></span>
                        <span>ETA: <strong>{paErpResult.status_detail.eta || "—"}</strong></span>
                        <span>Carrier: <strong>{paErpResult.status_detail.carrier || "—"}</strong></span>
                        <span>Confidence: <strong>{paErpResult.status_detail.confidence}</strong></span>
                        <span>Safe to reply: <strong className={paErpResult.status_detail.safe_to_reply ? "text-green-600" : "text-red-600"}>{paErpResult.status_detail.safe_to_reply ? "Yes" : "No"}</strong></span>
                        {paErpResult.status_detail.hours_since_update != null && (
                          <span>Updated: <strong>{paErpResult.status_detail.hours_since_update}h ago</strong></span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Search Results */}
                {paErpResult.results?.map((r, i) => (
                  <Card key={i}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-1">
                        <Badge variant="outline" className="text-[10px]">{r.source?.replace("_", " ")}</Badge>
                        {r.pf_number && (
                          <button onClick={() => getPaErpStatus(r.pf_number)} className="text-[10px] text-violet-600 hover:underline flex items-center gap-0.5">
                            <FileSearch className="h-3 w-3" /> Status
                          </button>
                        )}
                      </div>
                      {r.pf_number && <p className="text-xs font-mono font-bold">{r.pf_number}</p>}
                      {r.container_number && <p className="text-xs font-mono">{r.container_number}</p>}
                      {r.customer && <p className="text-xs text-gray-500">{r.customer}</p>}
                      {r.status && <p className="text-xs"><span className="text-gray-500">Status:</span> {r.status}</p>}
                      {r.origin && <p className="text-xs text-gray-400">{r.origin} &rarr; {r.destination}</p>}
                      {r.eta && <p className="text-xs text-gray-400">ETA: {r.eta}</p>}
                    </CardContent>
                  </Card>
                ))}
                {paErpResult.count === 0 && !paErpResult.status_detail && (
                  <div className="text-center py-6 text-gray-400">
                    <p className="text-sm">No results for "{paErpResult.reference}"</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Search for a shipment reference</p>
                <p className="text-xs mt-1">Used by PA to resolve ERP data for email replies</p>
              </div>
            )}
          </ScrollArea>
        </TabsContent>
      </Tabs>

      {/* Suggestion Box - Fixed at bottom */}
      <div className="sticky bottom-0 left-0 right-0 mx-3 mb-2 p-2 bg-blue-50 border border-blue-200 rounded-lg shadow-lg">
        <p className="text-[10px] font-medium text-blue-700 mb-1 flex items-center gap-1">
          <Lightbulb className="h-3 w-3" /> Have a suggestion?
        </p>
        <div className="flex gap-1">
          <Input
            value={suggestionText}
            onChange={(e) => setSuggestionText(e.target.value)}
            placeholder="Share your idea..."
            className="text-xs flex-1 bg-white h-7"
            onKeyDown={(e) => e.key === "Enter" && submitSuggestion()}
          />
          <Button 
            size="sm" 
            onClick={submitSuggestion}
            disabled={submittingSuggestion || !suggestionText.trim()}
            className="bg-blue-600 hover:bg-blue-700 h-7 px-2"
          >
            {submittingSuggestion ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
          </Button>
        </div>
      </div>

      {/* Create Task Dialog */}
      <Dialog open={showCreateTask} onOpenChange={setShowCreateTask}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md w-[95vw]">
          <DialogHeader>
            <DialogTitle>Create Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                placeholder="Follow up on quote..."
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                placeholder="Task details..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Priority</Label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={newTask.priority}
                  onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTask(false)}>Cancel</Button>
            <Button onClick={createTask}>Create Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Contact Dialog */}
      <Dialog open={showCreateContact} onOpenChange={(open) => {
        setShowCreateContact(open);
        if (!open) {
          setContactCompanySearch("");
          setContactCompanyResults([]);
          setShowContactCompanyDropdown(false);
        }
      }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md w-[95vw]">
          <DialogHeader>
            <DialogTitle>Create Contact</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={newContact.name}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                placeholder="John Smith"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                placeholder="john@company.com"
              />
            </div>
            <div className="relative">
              <Label>Company (Search Pantheon)</Label>
              <div className="relative">
                <Input
                  value={contactCompanySearch}
                  onChange={(e) => {
                    setContactCompanySearch(e.target.value);
                    setNewContact({ ...newContact, company_name: e.target.value, company_id: "" });
                    searchContactCompanies(e.target.value);
                  }}
                  onFocus={() => contactCompanyResults.length > 0 && setShowContactCompanyDropdown(true)}
                  placeholder="Search Pantheon customers..."
                  className="pr-8"
                />
                {contactCompanyLoading && (
                  <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-gray-400" />
                )}
              </div>
              {showContactCompanyDropdown && contactCompanyResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                  {contactCompanyResults.map((company) => (
                    <button
                      key={company.id}
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center gap-2"
                      onClick={() => {
                        setNewContact({ ...newContact, company_name: company.name, company_id: company.id });
                        setContactCompanySearch(company.name);
                        setShowContactCompanyDropdown(false);
                      }}
                    >
                      <Building2 className="h-4 w-4 text-gray-400" />
                      <span className="truncate">{company.name}</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2 border-t"
                    onClick={() => {
                      requestNewPantheonCustomer(contactCompanySearch, "Create Contact form");
                      setShowContactCompanyDropdown(false);
                    }}
                  >
                    <Mail className="h-4 w-4" />
                    Request New: "{contactCompanySearch}"
                  </button>
                </div>
              )}
              {newContact.company_id && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" /> Linked to Pantheon
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Phone</Label>
                <Input
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  placeholder="+1 234 567 890"
                />
              </div>
              <div>
                <Label>Title</Label>
                <Input
                  value={newContact.title}
                  onChange={(e) => setNewContact({ ...newContact, title: e.target.value })}
                  placeholder="Sales Manager"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateContact(false)}>
              Cancel
            </Button>
            <Button onClick={createContact}>Create Contact</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Activity Dialog */}
      <Dialog open={showCreateActivity} onOpenChange={(open) => {
        setShowCreateActivity(open);
        if (!open) {
          setActivityCompanySearch("");
          setActivityCompanyResults([]);
          setShowActivityCompanyDropdown(false);
        }
      }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md w-[95vw]">
          <DialogHeader>
            <DialogTitle>Log Activity</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Label>Company (Search Pantheon)</Label>
              <div className="relative">
                <Input
                  value={activityCompanySearch}
                  onChange={(e) => {
                    setActivityCompanySearch(e.target.value);
                    setNewActivity({ ...newActivity, company_name: e.target.value, company_id: "" });
                    searchActivityCompanies(e.target.value);
                  }}
                  onFocus={() => activityCompanyResults.length > 0 && setShowActivityCompanyDropdown(true)}
                  placeholder="Search Pantheon customers..."
                  className="pr-8"
                />
                {activityCompanyLoading && (
                  <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-gray-400" />
                )}
              </div>
              {showActivityCompanyDropdown && activityCompanyResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                  {activityCompanyResults.map((company) => (
                    <button
                      key={company.id}
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center gap-2"
                      onClick={() => {
                        setNewActivity({ ...newActivity, company_name: company.name, company_id: company.id });
                        setActivityCompanySearch(company.name);
                        setShowActivityCompanyDropdown(false);
                      }}
                    >
                      <Building2 className="h-4 w-4 text-gray-400" />
                      <span className="truncate">{company.name}</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2 border-t"
                    onClick={() => {
                      requestNewPantheonCustomer(activityCompanySearch, "Log Activity form");
                      setShowActivityCompanyDropdown(false);
                    }}
                  >
                    <Mail className="h-4 w-4" />
                    Request New: "{activityCompanySearch}"
                  </button>
                </div>
              )}
              {newActivity.company_id && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" /> Linked to Pantheon: {newActivity.company_name}
                </p>
              )}
            </div>
            <div>
              <Label>Type</Label>
              <select
                className="w-full border rounded px-3 py-2"
                value={newActivity.activity_type}
                onChange={(e) => setNewActivity({ ...newActivity, activity_type: e.target.value })}
              >
                <option value="email_received">Email Received</option>
                <option value="email_sent">Email Sent</option>
                <option value="call">Phone Call</option>
                <option value="meeting">Meeting</option>
                <option value="task">Task/Follow-up</option>
              </select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newActivity.notes}
                onChange={(e) => setNewActivity({ ...newActivity, notes: e.target.value })}
                placeholder="Activity notes..."
                rows={3}
              />
            </div>
            <div>
              <Label>Due Date (optional)</Label>
              <Input
                type="date"
                value={newActivity.due_date}
                onChange={(e) => setNewActivity({ ...newActivity, due_date: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateActivity(false)}>
              Cancel
            </Button>
            <Button onClick={() => createActivity(null)}>Create Activity</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Status Comment Dialog (2026-05-14) */}
      <Dialog open={showAddComment} onOpenChange={setShowAddComment}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md w-[95vw]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-slate-700" />
              Add Comment to {selectedPf?.pf_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Comment *</Label>
              <Textarea
                value={newComment.text}
                onChange={(e) => setNewComment({ ...newComment, text: e.target.value })}
                rows={4}
                placeholder="e.g. Customer asked about delivery 27.4 — still checking with carrier, POD expected by Friday"
                maxLength={2000}
                data-testid="outlook-comment-textarea"
              />
              <p className="text-xs text-slate-400 mt-1">
                {newComment.text.length}/2000 — visible to the customer on the tracking page.
              </p>
            </div>

            {Array.isArray(selectedPf?.containers) && selectedPf.containers.length > 1 && (
              <div>
                <Label>Container (optional)</Label>
                <select
                  value={newComment.container_number}
                  onChange={(e) => setNewComment({ ...newComment, container_number: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm"
                  data-testid="outlook-comment-container-select"
                >
                  <option value="">All containers</option>
                  {selectedPf.containers.map((c) => {
                    const cn = (c?.container_number || c || "").toString().trim().toUpperCase();
                    return cn ? <option key={cn} value={cn}>{cn}</option> : null;
                  })}
                </select>
              </div>
            )}

            {/* Audience selector — Sub PF (customer) vs Master PF (co-loader) */}
            <div className="rounded-lg border border-slate-200 p-2 bg-slate-50">
              <Label className="text-[11px] font-medium text-slate-700">
                This comment is for…
              </Label>
              <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                <button
                  type="button"
                  onClick={() => setNewComment({ ...newComment, audience: "customer" })}
                  className={`text-[11px] px-2 py-1.5 rounded border text-left transition-colors ${
                    newComment.audience === "customer"
                      ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                      : "bg-white text-slate-700 border-slate-300 hover:border-blue-400"
                  }`}
                  data-testid="outlook-comment-audience-customer"
                >
                  <div className="font-semibold">👤 Sub PF · Customer</div>
                  <div className={`text-[10px] mt-0.5 leading-tight ${
                    newComment.audience === "customer" ? "text-blue-50" : "text-slate-500"
                  }`}>
                    Customer Notify list · visible on tracking + WhatsApp <code>track</code>.
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setNewComment({ ...newComment, audience: "master" })}
                  className={`text-[11px] px-2 py-1.5 rounded border text-left transition-colors ${
                    newComment.audience === "master"
                      ? "bg-amber-600 text-white border-amber-600 shadow-sm"
                      : "bg-white text-slate-700 border-slate-300 hover:border-amber-400"
                  }`}
                  data-testid="outlook-comment-audience-master"
                >
                  <div className="font-semibold">🔒 Master PF · Co-loader</div>
                  <div className={`text-[10px] mt-0.5 leading-tight ${
                    newComment.audience === "master" ? "text-amber-50" : "text-slate-500"
                  }`}>
                    Master/co-loader Notify list · internal · WhatsApp <code>comments+</code> only.
                  </div>
                </button>
              </div>
            </div>

            <label className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 cursor-pointer">
              <input
                type="checkbox"
                checked={!!newComment.notify_customer}
                onChange={(e) => setNewComment({ ...newComment, notify_customer: e.target.checked })}
                className="mt-0.5"
                data-testid="outlook-comment-notify-toggle"
              />
              <div className="text-sm">
                <div className="font-medium">Send short status alert email</div>
                <p className="text-xs text-slate-600">
                  Goes to the <b>{newComment.audience === "master" ? "master-PF / co-loader" : "customer"}</b> Notify list with this comment + a tracking link.
                </p>
              </div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddComment(false)}>Cancel</Button>
            <Button onClick={addComment} disabled={savingComment}
                    data-testid="outlook-comment-submit-btn">
              {savingComment ? "Saving…" : "Add Comment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Milestone Dialog */}
      <Dialog open={showAddMilestone} onOpenChange={setShowAddMilestone}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md w-[95vw]">
          <DialogHeader>
            <DialogTitle>Add Milestone to {selectedPf?.pf_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Milestone Type</Label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={newMilestone.milestone_type}
                onChange={(e) => setNewMilestone({ ...newMilestone, milestone_type: e.target.value })}
                data-testid="outlook-milestone-type-select"
              >
                {Object.entries(MILESTONE_TEMPLATES).map(([groupName, group]) => (
                  <optgroup key={groupName} label={groupName}>
                    {group.items.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={newMilestone.description}
                onChange={(e) => setNewMilestone({ ...newMilestone, description: e.target.value })}
                placeholder="Milestone description"
              />
            </div>
            <div>
              <Label>Container Number</Label>
              <Input
                value={newMilestone.container_number || selectedPf?.containers?.[0] || ""}
                onChange={(e) => setNewMilestone({ ...newMilestone, container_number: e.target.value })}
                placeholder="XXXX1234567"
              />
            </div>
            <div>
              <Label>Date (optional)</Label>
              <Input
                type="text"
                inputMode="numeric"
                placeholder="dd/mm/yyyy"
                value={newMilestone.date}
                onChange={(e) => setNewMilestone({ ...newMilestone, date: e.target.value })}
                data-testid="milestone-date-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Type the event date as <strong>dd/mm/yyyy</strong> (e.g. 23/06/2026).
                A future date is saved as an <strong>estimated</strong> (ETD/ETA) date;
                a past/today date is the <strong>actual</strong> event. Leave blank to record it as now.
              </p>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newMilestone.notes}
                onChange={(e) => setNewMilestone({ ...newMilestone, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <input
                type="checkbox"
                id="send_notification"
                checked={newMilestone.send_notification}
                onChange={(e) => setNewMilestone({ ...newMilestone, send_notification: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <label htmlFor="send_notification" className="flex-1">
                <span className="text-sm font-medium text-green-800">Send email notification to customer</span>
                <p className="text-xs text-green-600">Customer will receive an update with shipment tracking link</p>
              </label>
            </div>
            
            {/* Notification Email Status/Input */}
            {newMilestone.send_notification && (
              <div className="space-y-2">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                  <p className="text-sm text-blue-800 font-medium flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Notification recipients:
                  </p>
                  
                  {/* Email Tags */}
                  {editableNotificationEmails.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {editableNotificationEmails.map((email, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          {email}
                          <button
                            type="button"
                            onClick={() => removeNotificationEmail(email)}
                            className="hover:bg-blue-200 rounded-full p-0.5"
                            title="Remove email"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded flex items-center gap-2">
                      <AlertCircle className="h-3 w-3" />
                      No recipients configured. Add email(s) below:
                    </p>
                  )}
                  
                  {/* Add New Email */}
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      value={newNotificationEmail}
                      onChange={(e) => setNewNotificationEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNotificationEmail(); }}}
                      placeholder="Add email address..."
                      className="text-sm flex-1"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={addNotificationEmail}
                      disabled={!newNotificationEmail.includes("@")}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-blue-600">
                    These emails will be saved to the PF for future notifications
                  </p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMilestone(false)}>Cancel</Button>
            <Button onClick={() => addMilestone(false)} data-testid="outlook-add-milestone-submit">Add Milestone</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Inspection Dialog */}
      <Dialog open={showOrderInspection} onOpenChange={setShowOrderInspection}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Order Port Inspection</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Inspection Type</Label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={newInspection.inspection_type}
                onChange={(e) => setNewInspection({ ...newInspection, inspection_type: e.target.value })}
              >
                <option value="customs">Customs Inspection</option>
                <option value="phytosanitary">Phytosanitary</option>
                <option value="veterinary">Veterinary</option>
                <option value="quality">Quality Check</option>
                <option value="fumigation">Fumigation</option>
                <option value="radiation">Radiation Check</option>
              </select>
            </div>
            <div>
              <Label>Container Number *</Label>
              <Input
                value={newInspection.container_number || selectedPf?.containers?.[0] || ""}
                onChange={(e) => setNewInspection({ ...newInspection, container_number: e.target.value })}
                placeholder="XXXX1234567"
                required
              />
            </div>
            <div>
              <Label>Port</Label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={newInspection.port}
                onChange={(e) => setNewInspection({ ...newInspection, port: e.target.value })}
              >
                <option value="KOPER">Luka Koper</option>
                <option value="RIJEKA">Rijeka</option>
                <option value="HAMBURG">Hamburg</option>
                <option value="ROTTERDAM">Rotterdam</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div>
              <Label>Urgency</Label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={newInspection.urgency}
                onChange={(e) => setNewInspection({ ...newInspection, urgency: e.target.value })}
              >
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={newInspection.notes}
                onChange={(e) => setNewInspection({ ...newInspection, notes: e.target.value })}
                placeholder="Special instructions..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrderInspection(false)}>Cancel</Button>
            <Button onClick={orderInspection} className="bg-orange-600 hover:bg-orange-700">Order Inspection</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PF Document Upload Dialog */}
      <Dialog open={showPfDocUpload} onOpenChange={(open) => {
        setShowPfDocUpload(open);
        if (!open) {
          setPfDocDragging(false);
          pfDocDragCounter.current = 0;
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document to {selectedPf?.pf_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Document Type</Label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={pfDocType}
                onChange={(e) => setPfDocType(e.target.value)}
              >
                <optgroup label="Commercial Documents">
                  <option value="invoice">Commercial Invoice</option>
                  <option value="proforma_invoice">Proforma Invoice</option>
                  <option value="packing_list">Packing List</option>
                  <option value="purchase_order">Purchase Order</option>
                  <option value="sales_contract">Sales Contract</option>
                </optgroup>
                <optgroup label="Transport Documents">
                  <option value="bl">Bill of Lading</option>
                  <option value="awb">Air Waybill</option>
                  <option value="cmr">CMR</option>
                  <option value="sea_waybill">Sea Waybill</option>
                  <option value="booking_confirmation">Booking Confirmation</option>
                </optgroup>
                <optgroup label="Customs Documents">
                  <option value="customs_declaration">Customs Declaration / MRN</option>
                  <option value="T1">T1 / Transit Document</option>
                  <option value="eur1">EUR.1 Certificate</option>
                  <option value="atr">ATR Certificate</option>
                  <option value="certificate_of_origin">Certificate of Origin</option>
                  <option value="phyto_certificate">Phytosanitary Certificate</option>
                  <option value="veterinary_certificate">Veterinary Certificate</option>
                </optgroup>
                <optgroup label="Port of Koper">
                  <option value="DVH">DVH — Entrance Disposition (Vhodna)</option>
                  <option value="DIZ">DIZ — Exit Disposition (Izhodna)</option>
                </optgroup>
                <optgroup label="Other">
                  <option value="insurance">Insurance Certificate</option>
                  <option value="inspection_report">Inspection Report</option>
                  <option value="pod">Proof of Delivery</option>
                  <option value="correspondence">Correspondence</option>
                  <option value="other">Other</option>
                </optgroup>
              </select>
            </div>
            
            {/* Drag & Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                pfDocDragging 
                  ? "border-blue-500 bg-blue-50" 
                  : "border-gray-300 hover:border-gray-400"
              }`}
              onDragEnter={handlePfDocDragEnter}
              onDragLeave={handlePfDocDragLeave}
              onDragOver={handlePfDocDragOver}
              onDrop={handlePfDocDrop}
            >
              {pfDocUploading ? (
                <div className="py-4">
                  <Loader2 className="h-8 w-8 mx-auto animate-spin text-blue-500" />
                  <p className="text-sm text-gray-600 mt-2">Uploading...</p>
                </div>
              ) : (
                <>
                  <Upload className={`h-8 w-8 mx-auto ${pfDocDragging ? "text-blue-500" : "text-gray-400"}`} />
                  <p className="text-sm text-gray-600 mt-2">
                    {pfDocDragging ? "Drop files here" : "Drag & drop files here"}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">or</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.multiple = true;
                      input.accept = '.pdf,.doc,.docx,.xls,.xlsx,.msg,.eml,.jpg,.jpeg,.png,.tif,.tiff';
                      input.onchange = (e) => {
                        const files = Array.from(e.target.files);
                        if (files.length > 0) uploadPfDocument(files);
                      };
                      input.click();
                    }}
                  >
                    Browse Files
                  </Button>
                  <p className="text-xs text-gray-400 mt-2">
                    PDF, Word, Excel, Images, Emails
                  </p>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPfDocUpload(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DVH (vhodna dispozicija) Quick Input — closes the Koper
          DVH follow-up reminder loop the moment staff records either a
          number or uploads a PDF tagged DVH. */}
      <Dialog open={showDvhDialog} onOpenChange={(open) => {
        setShowDvhDialog(open);
        if (!open) setDvhNumberInput("");
      }}>
        <DialogContent className="max-w-md" data-testid="outlook-dvh-dialog">
          <DialogHeader>
            <DialogTitle>DVH for {selectedPf?.pf_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="rounded-md bg-teal-50 border border-teal-200 p-3 text-teal-900 text-xs leading-relaxed">
              <b>Vhodna dispozicija / Entrance disposition.</b><br />
              <b>Any one</b> of these stops the 2-hour reminder loop:
              <ol className="list-decimal pl-5 mt-1 space-y-0.5">
                <li>Toggle status to <b>Done</b> below</li>
                <li>Enter the DVH number</li>
                <li>Upload DVH.pdf via the <b>Doc</b> button (tag <code>DVH</code>)</li>
              </ol>
            </div>

            {/* Quick Pending/Done toggle */}
            <div className="flex items-center justify-between bg-white border rounded-md px-3 py-2">
              <div>
                <div className="text-xs text-gray-500">Status</div>
                <div className="font-semibold">
                  {dvhDoneToggle ? (
                    <span className="text-emerald-600">✅ Done</span>
                  ) : (
                    <span className="text-amber-600">⏳ Pending</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={dvhDoneToggle}
                data-testid="outlook-dvh-toggle"
                onClick={() => setDvhDoneToggle(v => !v)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  dvhDoneToggle ? "bg-emerald-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    dvhDoneToggle ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <div>
              <Label htmlFor="dvh-number-input">DVH number <span className="text-gray-400 text-xs">(optional)</span></Label>
              <input
                id="dvh-number-input"
                data-testid="outlook-dvh-number-input"
                type="text"
                value={dvhNumberInput}
                onChange={(e) => setDvhNumberInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !dvhSaving) saveDvhNumber();
                }}
                placeholder="e.g. DVH-2026-04-001234"
                className="mt-1 w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                autoFocus
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Currently stored: <code>{selectedPf?.dvh_number || "—"}</code>
              </p>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPfDocType("DVH");
                  setShowDvhDialog(false);
                  setShowPfDocUpload(true);
                }}
                data-testid="outlook-dvh-upload-pdf-btn"
              >
                <Upload className="h-3 w-3 mr-1" /> Upload DVH.pdf instead
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDvhDialog(false)}
              disabled={dvhSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={saveDvhNumber}
              disabled={dvhSaving || (!(dvhNumberInput || "").trim() && dvhDoneToggle === (selectedPf?.dvh_done === true))}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="outlook-dvh-save-btn"
            >
              {dvhSaving ? (
                <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Saving…</>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIZ (izhodna dispozicija) Quick Input — export-side mirror of
          the DVH dialog. Closes the Koper DIZ follow-up loop the moment
          staff records a number, flips Done, or uploads DIZ.pdf. */}
      <Dialog open={showDizDialog} onOpenChange={(open) => {
        setShowDizDialog(open);
        if (!open) setDizNumberInput("");
      }}>
        <DialogContent className="max-w-md" data-testid="outlook-diz-dialog">
          <DialogHeader>
            <DialogTitle>DIZ for {selectedPf?.pf_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="rounded-md bg-indigo-50 border border-indigo-200 p-3 text-indigo-900 text-xs leading-relaxed">
              <b>Izhodna dispozicija / Exit disposition.</b><br />
              <b>Any one</b> of these stops the 2-hour reminder loop:
              <ol className="list-decimal pl-5 mt-1 space-y-0.5">
                <li>Toggle status to <b>Done</b> below</li>
                <li>Enter the DIZ number</li>
                <li>Upload DIZ.pdf via the <b>Doc</b> button (tag <code>DIZ</code>)</li>
              </ol>
            </div>

            {/* Quick Pending/Done toggle */}
            <div className="flex items-center justify-between bg-white border rounded-md px-3 py-2">
              <div>
                <div className="text-xs text-gray-500">Status</div>
                <div className="font-semibold">
                  {dizDoneToggle ? (
                    <span className="text-emerald-600">✅ Done</span>
                  ) : (
                    <span className="text-amber-600">⏳ Pending</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={dizDoneToggle}
                data-testid="outlook-diz-toggle"
                onClick={() => setDizDoneToggle(v => !v)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  dizDoneToggle ? "bg-emerald-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    dizDoneToggle ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <div>
              <Label htmlFor="diz-number-input">DIZ number <span className="text-gray-400 text-xs">(optional)</span></Label>
              <input
                id="diz-number-input"
                data-testid="outlook-diz-number-input"
                type="text"
                value={dizNumberInput}
                onChange={(e) => setDizNumberInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !dizSaving) saveDizNumber();
                }}
                placeholder="e.g. DIZ-2026-04-001234"
                className="mt-1 w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                autoFocus
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Currently stored: <code>{selectedPf?.diz_number || "—"}</code>
              </p>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPfDocType("DIZ");
                  setShowDizDialog(false);
                  setShowPfDocUpload(true);
                }}
                data-testid="outlook-diz-upload-pdf-btn"
              >
                <Upload className="h-3 w-3 mr-1" /> Upload DIZ.pdf instead
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDizDialog(false)}
              disabled={dizSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={saveDizNumber}
              disabled={dizSaving || (!(dizNumberInput || "").trim() && dizDoneToggle === (selectedPf?.diz_done === true))}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="outlook-diz-save-btn"
            >
              {dizSaving ? (
                <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Saving…</>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Create New PF Dialog */}
      <Dialog open={showCreatePf} onOpenChange={(open) => {
        setShowCreatePf(open);
        if (!open) {
          setPfCustomerSearch("");
          setPfCustomerResults([]);
          setShowPfCustomerDropdown(false);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-blue-600" />
              Create New Position Folder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              {/* Pantheon Customer Search */}
              <div className="col-span-2 relative">
                <Label>Customer (Search Pantheon)</Label>
                <div className="relative">
                  <Input
                    value={pfCustomerSearch}
                    onChange={(e) => {
                      setPfCustomerSearch(e.target.value);
                      setNewPf({ ...newPf, customer_name: e.target.value, customer_id: "" });
                      searchPfCustomers(e.target.value);
                    }}
                    onFocus={() => pfCustomerResults.length > 0 && setShowPfCustomerDropdown(true)}
                    placeholder="Search Pantheon customers..."
                    className="pr-8"
                  />
                  {pfCustomerLoading && (
                    <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-gray-400" />
                  )}
                </div>
                {showPfCustomerDropdown && pfCustomerResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                    {pfCustomerResults.map((company) => (
                      <button
                        key={company.id}
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 flex items-center gap-2"
                        onClick={() => {
                          setNewPf({ ...newPf, customer_name: company.name, customer_id: company.id });
                          setPfCustomerSearch(company.name);
                          setShowPfCustomerDropdown(false);
                        }}
                      >
                        <Building2 className="h-4 w-4 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{company.name}</div>
                          {company.city && (
                            <div className="text-xs text-gray-500">{company.city}, {company.country}</div>
                          )}
                        </div>
                      </button>
                    ))}
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2 border-t"
                      onClick={() => {
                        requestNewPantheonCustomer(pfCustomerSearch, "Create PF form");
                        setShowPfCustomerDropdown(false);
                      }}
                    >
                      <Mail className="h-4 w-4" />
                      Request New: "{pfCustomerSearch}"
                    </button>
                  </div>
                )}
                {newPf.customer_id && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" /> Linked to Pantheon: {newPf.customer_name}
                  </p>
                )}
              </div>
              
              <div className="col-span-2">
                <Label>Shipper *</Label>
                <Input
                  value={newPf.shipper}
                  onChange={(e) => setNewPf({ ...newPf, shipper: e.target.value })}
                  placeholder="Shipper company name"
                />
              </div>
              <div className="col-span-2">
                <Label>Consignee *</Label>
                <Input
                  value={newPf.consignee}
                  onChange={(e) => setNewPf({ ...newPf, consignee: e.target.value })}
                  placeholder="Consignee company name"
                />
              </div>
              <div>
                <Label>Origin Port</Label>
                <Input
                  value={newPf.origin_port}
                  onChange={(e) => setNewPf({ ...newPf, origin_port: e.target.value })}
                  placeholder="e.g. CNSHA"
                />
              </div>
              <div>
                <Label>Destination Port</Label>
                <Input
                  value={newPf.destination_port}
                  onChange={(e) => setNewPf({ ...newPf, destination_port: e.target.value })}
                  placeholder="e.g. SIKOP"
                />
              </div>
              <div>
                <Label>Container Type</Label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={newPf.container_type}
                  onChange={(e) => setNewPf({ ...newPf, container_type: e.target.value })}
                >
                  <option value="20GP">20' GP</option>
                  <option value="40GP">40' GP</option>
                  <option value="40HC">40' HC</option>
                  <option value="45HC">45' HC</option>
                  <option value="20RF">20' Reefer</option>
                  <option value="40RF">40' Reefer</option>
                  <option value="OT">Open Top</option>
                  <option value="FR">Flat Rack</option>
                  <option value="LCL">LCL</option>
                  <option value="AIR">Air Cargo</option>
                </select>
              </div>
              <div>
                <Label>Incoterm</Label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={newPf.incoterm}
                  onChange={(e) => setNewPf({ ...newPf, incoterm: e.target.value })}
                >
                  <option value="EXW">EXW</option>
                  <option value="FCA">FCA</option>
                  <option value="FOB">FOB</option>
                  <option value="CFR">CFR</option>
                  <option value="CIF">CIF</option>
                  <option value="DAP">DAP</option>
                  <option value="DDP">DDP</option>
                </select>
              </div>
              <div className="col-span-2">
                <Label>Commodity</Label>
                <Input
                  value={newPf.commodity}
                  onChange={(e) => setNewPf({ ...newPf, commodity: e.target.value })}
                  placeholder="Cargo description"
                />
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={newPf.notes}
                  onChange={(e) => setNewPf({ ...newPf, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreatePf(false)}>Cancel</Button>
            <Button onClick={createNewPf} disabled={creatingPf} className="bg-blue-600 hover:bg-blue-700">
              {creatingPf ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              Create PF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create New Trucking Order Dialog */}
      <Dialog open={showCreateTruckingOrder} onOpenChange={setShowCreateTruckingOrder}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-blue-600" />
              Create Trucking Order
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Pickup Location *</Label>
                <Input
                  value={newTruckingOrder.pickup_location}
                  onChange={(e) => setNewTruckingOrder({ ...newTruckingOrder, pickup_location: e.target.value })}
                  placeholder="e.g. Port of Koper, Terminal B"
                />
              </div>
              <div className="col-span-2">
                <Label>Delivery Location *</Label>
                <Input
                  value={newTruckingOrder.delivery_location}
                  onChange={(e) => setNewTruckingOrder({ ...newTruckingOrder, delivery_location: e.target.value })}
                  placeholder="e.g. Ljubljana, Slovenia"
                />
              </div>
              <div>
                <Label>Pickup Date</Label>
                <Input
                  type="date"
                  value={newTruckingOrder.pickup_date}
                  onChange={(e) => setNewTruckingOrder({ ...newTruckingOrder, pickup_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Container Number</Label>
                <Input
                  value={newTruckingOrder.container_number}
                  onChange={(e) => setNewTruckingOrder({ ...newTruckingOrder, container_number: e.target.value })}
                  placeholder="e.g. MSCU1234567"
                />
              </div>
              <div>
                <Label>Weight (kg)</Label>
                <Input
                  type="number"
                  value={newTruckingOrder.weight}
                  onChange={(e) => setNewTruckingOrder({ ...newTruckingOrder, weight: e.target.value })}
                  placeholder="e.g. 25000"
                />
              </div>
              <div className="col-span-2">
                <Label>Cargo Description</Label>
                <Input
                  value={newTruckingOrder.cargo_description}
                  onChange={(e) => setNewTruckingOrder({ ...newTruckingOrder, cargo_description: e.target.value })}
                  placeholder="Brief cargo description"
                />
              </div>
              <div className="col-span-2">
                <Label>Special Instructions</Label>
                <Textarea
                  value={newTruckingOrder.special_instructions}
                  onChange={(e) => setNewTruckingOrder({ ...newTruckingOrder, special_instructions: e.target.value })}
                  placeholder="ADR, temperature requirements, delivery time window..."
                  rows={2}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTruckingOrder(false)}>Cancel</Button>
            <Button onClick={createNewTruckingOrder} disabled={creatingTruckingOrder} className="bg-blue-600 hover:bg-blue-700">
              {creatingTruckingOrder ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
              Create Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
